import { z } from 'zod'
import {
  dmpAlimentationEtatRetenirSchema,
  dmpEnvoiAutomatiquePatchSchema,
  dmpReglageAccesWriteSchema,
  dmpReglageVisibiliteWriteSchema,
} from '@/lib/Ameli/DMP/dmpMutationSchemas'
import {
  fromInsBodySchema,
  lookupByInsBodySchema,
  lookupByTraitsBodySchema,
  lookupByVitaleBodySchema,
} from '@/lib/Ameli/INSi/insiLookupSchemas'
import { careDossierInsPatchSchema } from '@/lib/Ameli/INSi/insiSchemas'
import { PARTICIPATION_REJECT_REASONS } from '@/lib/campaign/participationRejectReason'
import {
  deviceConfirmationEmailCodeSchema,
  deviceConfirmationVerifyEmailCodeSchema,
  securitySettingsUpdateSchema,
} from '@/lib/deviceConfirmation/schema'
import {
  conventionBindingSchema,
  signableDocumentTemplateUpsertSchema,
  signableInstanceSendAtSchema,
} from '@/lib/insight/signableDocumentTemplate/schema'
import {
  surveyWindowCreateSchema,
  surveyWindowDeleteSchema,
  surveyWindowUpdateSchema,
} from '@/lib/insight/surveyWindow/schema'
import { legalDocumentAcceptSchema, legalDocumentPublishSchema } from '@/lib/legal/schema'
import { publicOrdonnanceDepositSchema } from '@/lib/patient/ordonnanceDepositSchema'
import {
  programEmailBodyTemplateSchema,
  programEmailDraftSchema,
  programEmailEmptyBodySchema,
  programEmailRecipientExclusionSchema,
  programEmailRecipientsExportSchema,
  programEmailResendSchema,
  programEmailResolveCipsSchema,
  programEmailScheduleEstimateSchema,
  programEmailScheduleSchema,
  programEmailScheduleUpdateSchema,
  programEmailTestSchema,
} from '@/lib/programEmail/programEmailSchemas'
import {
  programFeedbackAnswersSchema,
  programFeedbackPresentationSchema,
} from '@/lib/programFeedback/schema'
import {
  qrLoginSessionApproveSchema,
  qrLoginSessionRejectSchema,
  qrLoginSessionScanSchema,
  qrLoginSessionExchangeSchema,
  qrLoginTransferActionSchema,
  qrLoginTransferClaimSchema,
  qrLoginTransferCreateSchema,
  qrLoginTransferExchangeSchema,
} from '@/lib/qrLogin/schema'
// Listes définies dans des modules déjà chargés par le shell (cf. scripts/check-shell-budget.cjs).
import { HOLDER_CHANGE_REASON_REQUIRING_DETAIL, HOLDER_CHANGE_REASONS } from '@/types/Company'
import { CRM_TASK_TYPE_CHOICES, PHARMACY_UPDATE_REASONS } from '@/types/enum/constants'
import { DOCUMENT_TYPE_DOCUMENTARY_SPACE } from '@/types/group/Document'
import { GERS_APPLY_ACTIONS, GERS_SURE_OWNER_NAME_SOURCES } from '@/types/group/gers'
import { MAX_SOUND_PROGRAMS } from '@/types/group/partnerCampaign'

export type ApiMutationEndpointConfig = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  endpoint: string
  schema?: z.ZodType
  /**
   * N'envoie PAS l'en-tete `X-Switch-User`, meme pendant un « Se connecter en tant que ».
   * Reserve aux operations qui decrivent l'activite de l'AGENT et non celle de la personne
   * consultee (aujourd'hui : les `crm_calls` du softphone Ringover, qui reste branche
   * pendant une impersonation). Voir `buildHeaders` dans lib/hook/tanstackQuery.ts.
   */
  ignoreSwitchUser?: boolean
  /**
   * Cet endpoint DEMANDE un export asynchrone (202 ; le fichier est genere en tache de fond
   * puis livre par e-mail et depose dans l'historique « Telechargements » du header).
   *
   * Declaratif : c'est la couche REST (`fetchRest` / `useRestMutation`) qui en tire la
   * consequence — au succes, la liste du header passe en rafraichissement rapide pendant
   * quelques minutes (`lib/export/asyncExportSignal.ts`). Aucun ecran n'a de code de
   * signalement, il suffit de SPREADER la definition de l'endpoint.
   *
   * Ne PAS le poser sur un telechargement direct (blob, lien presigne rendu dans la reponse) :
   * il n'y a alors rien a attendre dans l'historique.
   */
  asyncExport?: boolean
}

/**
 * POST / PATCH `store_bag_products` : schema `.passthrough()` (la fiche produit poste un
 * corps large et variable). Il ne FERME que les champs du moteur de campagne, dont une
 * valeur hors contrat produirait une erreur PHP plutot qu'un 422 :
 *  - `campaignAxis` : `'gx' | 'bs' | null` — JAMAIS `''` (le setter appelle
 *    `CampaignAxis::from()`, une chaine inconnue leve une ValueError) ;
 *  - `maxUnitsPerPharmacy` : entier > 0 ou `null` (repli sur le plafond global du moteur) ;
 *  - `clusterPrices` : lu dans le CORPS BRUT par `StoreBagProductPostProcessor` (pas
 *    denormalise par API Platform) — forme d'une ligne
 *    `{cluster, priceHt?, priceUnitBrutFournitureHt?, priceUnitFullHt?, available?}`.
 *    Une ligne `{cluster, available: false}` sans prix est valide : c'est la case vide du
 *    bareme laboratoire (produit non proposable a ce cluster).
 */
const storeBagProductCampaignSchema = z
  .object({
    campaignAxis: z.enum(['gx', 'bs']).nullable().optional(),
    maxUnitsPerPharmacy: z.number().int().positive().nullable().optional(),
    // Sacs remis au patient par unité (capacité de distribution, front#759) : entier > 0 ou null.
    bagsPerUnit: z.number().int().positive().nullable().optional(),
    clusterPrices: z
      .array(
        z
          .object({
            cluster: z.string().regex(/^\/api\/v2\/company_clusters\/\d+$/),
            priceHt: z.number().nullable().optional(),
            priceUnitBrutFournitureHt: z.number().nullable().optional(),
            priceUnitFullHt: z.number().nullable().optional(),
            available: z.boolean().optional(),
          })
          .passthrough()
      )
      .optional(),
  })
  .passthrough()

/**
 * POST / PATCH `sound_programmes` : meme principe que ci-dessus — `.passthrough()` sur un
 * corps large, seul `campaignAxis` est ferme (`'gx' | 'bs' | null`, JAMAIS `''`).
 * `source` (docs/SOUND_REGIES.md cote API) est ferme aussi : une valeur hors enum ferait lever
 * `SoundProgrammeSource::from()`. Lue a la creation seulement.
 */
const soundProgrammeCampaignSchema = z
  .object({
    campaignAxis: z.enum(['gx', 'bs']).nullable().optional(),
    source: z.enum(['classic', 'soundcast', 'dploy']).optional(),
    // Programme Dploy : ID_pubs de la pub choisie, lu a la creation seulement.
    dployAdId: z.number().int().positive().nullable().optional(),
  })
  .passthrough()

/**
 * Une entree de `soundPrograms[]` de l'assistant de campagne (proposition, prevalidation,
 * import). `axis` (lot 4, `report-4.md` §3.4) est OPTIONNEL : absent, le serveur derive
 * l'axe de l'index (1 = GX, 2 = BS) ; une valeur hors `gx`/`bs` est un 422 nommant le
 * programme fautif. Le schema doit le declarer explicitement — `z.object()` STRIPPE les
 * cles inconnues, un `axis` non declare partirait donc silencieusement a la trappe.
 */
// Renvoi d'un message du journal d'une fiche : `id` pour le chemin, `email` = une des adresses
// proposees par `user_message_notifications/:id/resend_options` (absente : l'adresse par defaut).
const userMessageResendSchema = z.object({
  id: z.string(),
  email: z.string().email().optional(),
})

const campaignSoundProgramInputSchema = z.object({
  label: z.string().min(1).max(60),
  type: z.string().min(1),
  spotIds: z.array(z.number().positive()),
  axis: z.enum(['gx', 'bs']).optional(),
})

/**
 * JOUR d'une campagne partenaire (date limite de signature), au format `AAAA-MM-JJ` STRICT.
 * C'est un jour, pas un instant : le serveur le relit tel quel (`ParisCalendar::parseDay`) et
 * repond 422 sur tout autre format — une date ISO complete (`...T00:00:00+02:00`) serait donc
 * refusee apres l'aller-retour, autant la rejeter ici.
 */
const campaignDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

// Programmes EXISTANTS de la campagne qui recoivent un volet du fichier (19/10/2026) : numero
// de colonne radio (« 1 ».. « 4 ») => id de programme Sound, et id du programme PLV. ADMIN
// seul (403 sinon) ; refuse en 422 un programme d'une autre campagne, termine ou supprime.
const campaignTargetProgramsSchema = z.object({
  sound: z.record(z.string().regex(/^[1-4]$/), z.number().int().positive()).optional(),
  plv: z.number().int().positive().optional(),
})

/**
 * Règle de ciblage de version Companion. Une seule cible renseignée selon le type, les autres à
 * `null` (l'API refuse une règle qui en porte deux) : poste pour `installation`, pharmacie ou
 * groupement pour `company` / `groupement`, code CIP pour `cip`, aucune pour `all`.
 */
const companionVersionTargetSchema = z.object({
  type: z.enum(['installation', 'company', 'groupement', 'cip', 'all']),
  targetVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
  installation: z.string().nullable(),
  company: z.string().nullable(),
  cipCode: z.string().nullable(),
})

/**
 * Demande de reconnaissance Winpharma à un poste : aucun corps, le poste visé est dans l'URL et
 * l'instant de la demande est posé par le serveur.
 */
const companionRequestWinpharmaProbeSchema = z.object({})

/** Demande de sauvegarde Winpharma : aucun corps, le poste est dans l'URL. */
const companionRequestWinpharmaBackupSchema = z.object({})

/**
 * OUTIL DE VALIDATION TEMPORAIRE — exécution d'une commande PowerShell sur un poste (super admin).
 * Sert à éprouver les commandes COM Winpharma sans redéployer le companion. Le poste rend la sortie
 * dans son flux de logs. À RETIRER une fois la commande COM figée.
 */
const companionPsExecSchema = z.object({
  script: z.string().trim().min(1).max(8000),
})

/**
 * Sauvegarde Winpharma automatique d'un poste. Mêmes bornes que l'API : heure 0-23, requise pour
 * activer.
 */
const companionWinpharmaBackupScheduleSchema = z
  .object({
    installationId: z.number().int().positive(),
    enabled: z.boolean(),
    hour: z.number().int().min(0).max(23).nullable(),
  })
  .refine(value => !value.enabled || value.hour !== null, { path: ['hour'] })

/**
 * Les SIX regles de calcul reglables d'une repartition a deux enveloppes (lot 1), envoyees
 * avec la proposition ET avec l'export XLSX — sans quoi le classeur telecharge divergerait de
 * la proposition affichee.
 *
 * Bornes IDENTIQUES a celles du serveur (`bounds` de `allocation_settings`) : hors bornes,
 * l'API repond 422 en nommant le champ ; les rejeter ici evite l'aller-retour.
 * `newInstallationThreshold` est le seul `nullable` : `null` DESACTIVE la nouvelle pose radio
 * (ce n'est pas « valeur par defaut »), `0` n'est donc pas une valeur acceptee.
 */
/**
 * Ordre des criteres du remplissage exact des consommables : des cles connues et DISTINCTES
 * (le serveur repond 422 en nommant le champ sinon). Un doublon (« gap, gap, catalog »)
 * laisserait un critere jamais applique sans que rien ne le dise.
 *
 * De TROIS a CINQ cles : `spread` (« repartir sur plusieurs references ») puis `supply` (« cout
 * de fourniture le plus bas ») sont arrives apres les trois premiers, et une fiche labo — ou un
 * ordre repris d'un calcul — enregistre avant eux n'en porte que trois ou quatre. Les refuser
 * reviendrait a bloquer un calcul sur une valeur pourtant legitime ; c'est le catalogue rendu
 * par `allocation_settings.criteria` qui dit ce que l'API accepte.
 */
export const consumableCriteriaOrderSchema = z
  .array(z.enum(['gap', 'shipments', 'catalog', 'spread', 'supply']))
  .min(3)
  .max(5)
  .refine(order => new Set(order).size === order.length, {
    message: 'consumableCriteriaOrder: les criteres doivent etre distincts',
  })

export const campaignAllocationSettingsSchema = z.object({
  logisticsAdminFee: z.number().min(0).max(10000),
  radioMonthlyFee: z.number().min(0).max(10000),
  radioSetupFee: z.number().min(0).max(10000),
  newInstallationThreshold: z.number().min(0.01).max(100000).nullable(),
  // SEUIL DE PREFERENCE RADIO, sur le total GX + BS : `null` coupe la regle pour ce calcul, ABSENT
  // = fiche du laboratoire. Optionnel pour rester compatible d'une API qui ne le connait pas.
  radioPreferenceThreshold: z.number().min(0.01).max(100000).nullable().optional(),
  defaultMaxUnitsPerPharmacy: z.number().int().min(1).max(1000),
  fixedPlvMaxPerProduct: z.number().int().min(1).max(50),
  // REMISE RADIO + PLV (%), sur la gestion radio d'une officine qui a aussi de la PLV. ABSENT =
  // fiche du laboratoire puis 10 ; `0` est une valeur (aucune remise). Memes bornes que l'API.
  radioPlvDiscountPercent: z.number().min(0).max(100).optional(),
  // ABSENT = ordre du moteur (ecart -> envois -> catalogue). Optionnel pour rester
  // compatible d'une API qui ne connait pas encore le reglage.
  consumableCriteriaOrder: consumableCriteriaOrderSchema.optional(),
  // SCENARIO B a la demande. Optionnel : une API anterieure au reglage le calculait toujours.
  scenarioBEnabled: z.boolean().optional(),
})

// Telechargement binaire (XLSX) de la proposition de repartition d'une campagne partenaire.
// N'est pas declare dans l'arbre ApiMutationEndpoint car la reponse est un blob (pas de JSON) :
// consomme via lib/fetchBinary.fetchBlob, pas via useRestMutation.
export const PARTNER_CAMPAIGN_PROPOSAL_EXPORT_ENDPOINT =
  'partner_campaigns/allocation_proposal_export'

// Modele XLSX d'import direct (repartition facultative) : memes entetes que la proposition,
// canal vide + feuille Notice (quantites vs montants). Telechargement binaire via fetchBlob.
export const PARTNER_CAMPAIGN_TEMPLATE_EXPORT_ENDPOINT =
  'partner_campaigns/allocation_template_export'

// Pave d'une tache CRM envoye a l'enregistrement (`updateCrmTaskCommentEntities[]`). Seul le
// « Type de demande » est contraint : un pave NEUF peut etre une cession (lot 6 du chantier
// « Qualite des fiches »), routee par le catalogue ; les autres champs restent libres.
const crmTaskCommentEntityInputSchema = z
  .object({
    crmTaskTypeCode: z.enum(CRM_TASK_TYPE_CHOICES).optional().nullable(),
    // Mise à jour de fiche : ce qui change (23/10/2026), lu par l'API pour un pavé neuf.
    requestReasons: z.array(z.enum(PHARMACY_UPDATE_REASONS)).optional().nullable(),
  })
  .passthrough()

// Actions groupees sur le SIRET fige des lignes de programme (chantier SIRET, lot 5 —
// pharmedigroup-api/docs/PROGRAM_LINE_SIRET.md). Les lignes cochees (`lineIds`) OU toutes celles
// du programme dans des etats (`siretStates`), jamais les deux : le serveur refuse un corps vide
// en 422 (`nothingSelected`). Valeurs ecrites en ligne, sans importer les types du chantier :
// ce fichier est dans le shell (budget de modules), un import de plus y compterait.
const programLineSiretActionSchema = z
  .object({
    module: z.enum(['insight', 'sound', 'storebag']),
    programId: z.number().int().positive(),
    lineIds: z.array(z.number().int().positive()).min(1).optional(),
    siretStates: z
      .array(z.enum(['valid', 'to_validate', 'owner_changed', 'exempt']))
      .min(1)
      .optional(),
  })
  .refine(body => !!body.lineIds !== !!body.siretStates, {
    message: 'lineIds OU siretStates, exactement un des deux',
  })

type ApiMutationEndpointTree = {
  [key: string]: ApiMutationEndpointTree | ApiMutationEndpointConfig
}

// Geste « Changer de titulaire ou de propriétaire » (OWNERSHIP / OWNERSHIP_PREVIEW). Les
// contrôles de fond (SIRET actif à l'INSEE, e-mail déjà pris, motif obligatoire hors
// correction) restent côté API : ce schéma ne vérifie que la forme.
const ownershipSchema = z.object({
  personChanges: z.boolean(),
  holder: z
    .object({
      email: z.string().trim().email(),
      firstname: z.string().trim(),
      lastname: z.string().trim().min(1),
      rppsNumber: z
        .string()
        .regex(/^\d{11}$/)
        .nullable()
        .optional(),
    })
    .nullable(),
  siret: z
    .string()
    .regex(/^\d{14}$/)
    .nullable()
    .optional(),
  tva: z.string().nullable().optional(),
  reason: z.string().trim().max(255),
  sellerEmail: z.string().trim().email().nullable().optional(),
  sendInvitation: z.boolean().optional(),
  // Remplacer l'e-mail de connexion du compte boutique par celui du titulaire (opt-in).
  replaceStoreLogin: z.boolean().optional(),
  // Date de la cession (AAAA-MM-JJ), quand ce n'est pas aujourd'hui : borne la période close
  // et partage les données (recette du 14/10). Jamais dans le futur, l'API le vérifie.
  effectiveDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
})

export const ApiMutationEndpoint = {
  // Remontee d'un ticket dans un canal Teams de l'equipe IT depuis le back-office.
  // Le serveur etablit lui-meme l'identite du demandeur, le compte consulte en
  // switch-user et la pharmacie : ces champs n'existent pas dans le corps.
  SUPPORT_TICKET: {
    CREATE: {
      method: 'POST',
      endpoint: 'support_tickets',
      schema: z.object({
        channelKey: z.string().min(1),
        message: z.string().min(1).max(4000),
        url: z.string().max(2000).optional(),
        routeName: z.string().max(120).optional(),
        // Capture en data URL : transportee telle quelle jusqu'a Teams, sans hebergement.
        // Obligatoire cote front (un ticket sans ecran ne se traite pas) ; l'API l'accepte
        // encore absente, choix du 29/09/2026.
        imageDataUrl: z.string().regex(/^data:image\/(png|jpeg);base64,/),
        // Commentaires épinglés : le numéro est le rang, dessiné sur l'image. Bornes = API.
        annotations: z
          .array(
            z.object({
              x: z.number().min(0).max(1),
              y: z.number().min(0).max(1),
              text: z.string().trim().min(1).max(300),
            })
          )
          .max(10)
          .optional(),
        entity: z.string().max(50).optional(),
        buildId: z.string().max(120).optional(),
        userAgent: z.string().max(400).optional(),
        viewport: z.string().max(40).optional(),
      }),
    },
    // Description proposée par Claude (via Merlin) depuis la capture annotée : rien n'est
    // publié, le texte revient dans le champ. `draft` = ce que l'admin a déjà écrit.
    SUGGEST: {
      method: 'POST',
      endpoint: 'support_tickets/suggestion',
      schema: z.object({
        imageDataUrl: z.string().regex(/^data:image\/(png|jpeg);base64,/),
        annotations: z
          .array(
            z.object({
              x: z.number().min(0).max(1),
              y: z.number().min(0).max(1),
              text: z.string().trim().min(1).max(300),
            })
          )
          .min(1)
          .max(10),
        draft: z.string().max(3900).optional(),
        routeName: z.string().max(120).optional(),
      }),
    },
  },
  PRO_SANTE_CONNECT: {
    EXCHANGE: {
      method: 'POST',
      endpoint: 'pro-sante-connect/exchange',
      schema: z.object({ ticket: z.string().min(1) }),
    },
    LINK: {
      method: 'POST',
      endpoint: 'pro-sante-connect/link',
      schema: z.object({
        registrationTicket: z.string().min(1),
        email: z.string().email(),
        password: z.string().min(1),
      }),
    },
    REGISTER_PHARMACY: {
      method: 'POST',
      endpoint: 'pro-sante-connect/register/pharmacy',
      schema: z
        .object({
          companyId: z.number(),
          proSanteConnectRegistrationTicket: z.string().min(1),
        })
        .passthrough(),
    },
    REFRESH: {
      method: 'POST',
      endpoint: 'pro-sante-connect/refresh',
      schema: z.object({ proSanteConnectSessionId: z.string().min(1) }),
    },
    LOGOUT_URL: {
      method: 'POST',
      endpoint: 'pro-sante-connect/logout-url',
      schema: z.object({
        proSanteConnectSessionId: z.string().min(1),
        postLogoutRedirectUri: z.string().url(),
        localRefreshToken: z.string().nullable().optional(),
      }),
    },
  },
  COMMON: {
    MYSTERY_VISIT: {
      // Operation Post : uriTemplate `/mystery-visit/import` (ImportMysteryVisitCSVController). Body { keyFile }.
      // Reponse = message (string).
      IMPORT: {
        method: 'POST',
        endpoint: 'mystery-visit/import',
        schema: z.object({ keyFile: z.string() }).passthrough(),
      },
      // Operation Post : uriTemplate `/mystery-visit-settings` (MysteryVisitSettings). Body { entity, startAt, endAt }.
      ADD_NEXT: {
        method: 'POST',
        endpoint: 'mystery-visit-settings',
        schema: z
          .object({
            entity: z.string(),
            startAt: z.string().nullable(),
            endAt: z.string().nullable(),
          })
          .passthrough(),
      },
      // Operation Patch : uriTemplate `/mystery-visit-settings/{id}` (MysteryVisitSettings), ROLE_ADMIN.
      // `:id` resolu via le champ `id` passe au mutateAsync. Body (groupe write) { entity, startAt, endAt }.
      UPDATE_SETTINGS: {
        method: 'PATCH',
        endpoint: 'mystery-visit-settings/:id',
        schema: z.object({
          startAt: z.string().optional(),
          endAt: z.string().optional(),
          entity: z.string().optional(),
        }),
      },
    },
    // Vues PARTAGEES des tableaux TableV2 (ROLE_ADMIN, tout admin peut modifier ou supprimer).
    // Laboratoire et equipe en ids scalaires, resolus par l'API ; 422 si doublon de nom pour le
    // meme tableau, laboratoire et equipe. Suppression logique, auteur conserve.
    // Schemas en ligne : ce fichier est dans le shell, un module de schema de plus compterait
    // dans son budget (scripts/check-shell-budget.cjs). La cle du tableau ne se modifie pas.
    TABLE_VIEW: {
      CREATE: {
        method: 'POST',
        endpoint: 'table_views',
        schema: z.object({
          tableKey: z.string().min(1).max(100),
          label: z.string().trim().min(1).max(80),
          partnerId: z.number().int().positive().nullable(),
          adminServiceId: z.number().int().positive().nullable(),
          columns: z.record(z.string().min(1).max(150), z.boolean()),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'table_views/:id',
        schema: z.object({
          label: z.string().trim().min(1).max(80),
          partnerId: z.number().int().positive().nullable(),
          adminServiceId: z.number().int().positive().nullable(),
          columns: z.record(z.string().min(1).max(150), z.boolean()),
        }),
      },
      DELETE: { method: 'DELETE', endpoint: 'table_views/:id' },
    },
  },
  OAUTH: {
    AUTHORIZE: {
      method: 'POST',
      endpoint: 'oauth/authorize',
      schema: z.object({
        client_id: z.string().min(1),
        redirect_uri: z.string().min(1),
        state: z.string(),
        scope: z.string(),
        code_challenge: z.string(),
        code_challenge_method: z.string(),
        resource: z.string(),
      }),
    },
  },
  TOTP: {
    IMPORT: { method: 'POST', endpoint: 'totp/import' },
    SHARE: { method: 'POST', endpoint: 'totp/accounts/:id/share' },
    CREATE: { method: 'POST', endpoint: 'totp/accounts' },
    TOGGLE: { method: 'POST', endpoint: 'totp/accounts/:id/toggle' },
    UNSHARE: { method: 'DELETE', endpoint: 'totp/accounts/:id/share/:userId' },
    UNLOCK_REQUEST: { method: 'POST', endpoint: 'totp/unlock/request' },
    UNLOCK_VERIFY: { method: 'POST', endpoint: 'totp/unlock/verify' },
  },
  GROUP: {
    // SIRET fige des lignes de programme Insight / Sound / PLV (ROLE_ADMIN). Lignes facturees
    // toujours ecartees ; plus de 50 pharmacies : tache de fond, reponse `{ queued: true }`.
    PROGRAM_LINE_SIRET: {
      // Nouveau controle INSEE a la date du jour, verdict ecrit sur les lignes ET les fiches.
      CONTROL: {
        method: 'POST',
        endpoint: 'program_line_sirets/control',
        schema: programLineSiretActionSchema,
      },
      // Reprend le SIRET de la fiche (sans appel si la fiche est valide sur le meme SIRET).
      // Les lignes au proprietaire change sont ecartees : elles ont leur propre geste, REBIND.
      REALIGN: {
        method: 'POST',
        endpoint: 'program_line_sirets/realign',
        schema: programLineSiretActionSchema,
      },
      // « Rattacher a la societe actuelle » (30/10/2026) : SEULES les lignes au proprietaire
      // change non facturees prennent le SIRET de la fiche ; la facture changera de societe.
      REBIND: {
        method: 'POST',
        endpoint: 'program_line_sirets/rebind',
        schema: programLineSiretActionSchema,
      },
    },
    // Questionnaire de satisfaction post-signature de facture. La pharmacie n'est jamais
    // transmise : le serveur la deduit de l'utilisateur authentifie. Les deux operations
    // refusent une session en switch-user (IS_IMPERSONATOR), reponse 204.
    PROGRAM_FEEDBACK: {
      // Modale affichee : consomme une des deux presentations autorisees. Un second appel
      // dans les 30 minutes n'est pas recompte (double montage, rechargement de page).
      PRESENTATION: {
        method: 'POST',
        endpoint: 'program_feedbacks/presentations',
        schema: programFeedbackPresentationSchema,
      },
      // Les cinq reponses. Le serveur refuse une soumission partielle ou une modalite
      // etrangere au module, et repond 409 si le questionnaire a deja ete renseigne.
      ANSWER: {
        method: 'POST',
        endpoint: 'program_feedbacks/answers',
        schema: programFeedbackAnswersSchema,
      },
    },
    JOB: {
      // Declenche un job ETL Kibana Store on-demand : POST /v2/jobs/kibana/{name}/launch
      // (Model KibanaJob -> LaunchKibanaJobController, security ROLE_ADMIN). {name} est valide
      // cote back contre l'enum KibanaJobName (whitelist) et resout la path variable :name.
      // Le corps est lu sous la cle `options` { reindex?, order_id? }. Reponse 202 + { jobId }.
      // Le statut/logs se suivent ensuite via le Model CronLog REST (cf. doJob + tables LIST_*).
      LAUNCH_KIBANA: {
        method: 'POST',
        endpoint: 'jobs/kibana/:name/launch',
        schema: z.object({
          name: z.string().min(1),
          options: z
            .object({
              reindex: z.boolean().optional(),
              order_id: z.number().optional(),
            })
            .optional(),
        }),
      },
      // Relance d'une tache relancable (POST /jobs/{name}/launch, super-admin) : 202 + { jobId }
      // a suivre via LAUNCHABLE_RUN. sendToRecipients=false (defaut) = envoi de test au seul
      // demandeur ; true = destinataires parametres (opt-in explicite a l'ecran).
      LAUNCH: {
        method: 'POST',
        endpoint: 'jobs/:name/launch',
        schema: z.object({ sendToRecipients: z.boolean() }),
      },
      // Reglages d'une tache relancable (PATCH /jobs/{name}). Le serveur revalide et normalise
      // les adresses, et refuse l'activation sans destinataire (422).
      UPDATE_SETTINGS: {
        method: 'PATCH',
        endpoint: 'jobs/:name',
        schema: z.object({
          enabled: z.boolean(),
          recipients: z.array(z.string().email()).max(10),
          ccRecipients: z.array(z.string().email()).max(10),
        }),
      },
    },
    SFTP: {
      // Telechargement d'un fichier SFTP : retourne le contenu encode en base64 (JSON).
      DOWNLOAD_SFTP_FILE: {
        method: 'POST',
        endpoint: 'sftp/downloadSftpFile',
        schema: z.object({
          keyFile: z.string().min(1),
          name: z.string().min(1),
        }),
      },
      DOWNLOAD_SFTP_FILE_BY_ID: {
        method: 'POST',
        endpoint: 'sftp/downloadSftpFile/:id',
        schema: z.object({
          id: z.number(),
          keyFile: z.string().min(1),
          name: z.string().min(1),
        }),
      },
    },
    MAILJET_EMAILING: {
      SYNC_LISTS: {
        method: 'POST',
        endpoint: 'mailjet/lists/sync',
        schema: z.object({ entity: z.string().optional() }),
      },
      CREATE_AUTOMATED: {
        method: 'POST',
        endpoint: 'mailjet/lists/automated',
        schema: z.object({
          name: z.string().min(1),
          source: z.enum([
            'group',
            'store',
            'combined',
            'school',
            'sound',
            'insight',
            'storebag',
            'care',
          ]),
          synchronizationMode: z.enum(['replace', 'additive']),
          enabled: z.boolean(),
          rules: z.object({
            groupementAliases: z.array(z.string()).optional(),
            excludeGroupementAliases: z.array(z.string()).optional(),
            storeUserGroupAliases: z.array(z.string()).optional(),
            excludeStoreUserGroupAliases: z.array(z.string()).optional(),
            trainingId: z.number().int().positive().optional(),
            trainingIds: z.array(z.number().int().positive()).min(1).max(50).optional(),
            programId: z.number().int().positive().optional(),
            includeTitulars: z.boolean().optional(),
            includeTeam: z.boolean().optional(),
            audience: z.enum(['titular', 'team']).optional(),
            excludeCompleted: z.boolean().optional(),
            completionStatus: z.enum(['all', 'completed', 'not_completed']).optional(),
            careStatus: z.enum(['active', 'inactive']).optional(),
            // Périmètre company (Groupe, Combiné, Care). Absent = officines françaises.
            companyType: z.string().min(1).optional(),
            country: z.string().length(2).optional(),
            // Ignorer les inactifs (société inactive, compte jamais connecté). Absent = oui.
            excludeInactive: z.boolean().optional(),
          }),
        }),
      },
      UPDATE_AUTOMATED: {
        method: 'PUT',
        endpoint: 'mailjet/lists/automated/:id',
        schema: z.union([
          z.object({
            id: z.number(),
            name: z.string().min(1),
            source: z.enum([
              'group',
              'store',
              'combined',
              'school',
              'sound',
              'insight',
              'storebag',
              'care',
            ]),
            synchronizationMode: z.enum(['replace', 'additive']),
            enabled: z.boolean(),
            rules: z.object({
              groupementAliases: z.array(z.string()).optional(),
              excludeGroupementAliases: z.array(z.string()).optional(),
              storeUserGroupAliases: z.array(z.string()).optional(),
              excludeStoreUserGroupAliases: z.array(z.string()).optional(),
              trainingId: z.number().int().positive().optional(),
              trainingIds: z.array(z.number().int().positive()).min(1).max(50).optional(),
              programId: z.number().int().positive().optional(),
              includeTitulars: z.boolean().optional(),
              includeTeam: z.boolean().optional(),
              audience: z.enum(['titular', 'team']).optional(),
              excludeCompleted: z.boolean().optional(),
              completionStatus: z.enum(['all', 'completed', 'not_completed']).optional(),
              careStatus: z.enum(['active', 'inactive']).optional(),
              // Périmètre company (Groupe, Combiné, Care). Absent = officines françaises.
              companyType: z.string().min(1).optional(),
              country: z.string().length(2).optional(),
              // Ignorer les inactifs (société inactive, compte jamais connecté). Absent = oui.
              excludeInactive: z.boolean().optional(),
            }),
          }),
          z.object({
            id: z.string().min(1),
            synchronizationMode: z.enum(['replace', 'additive']),
            enabled: z.boolean(),
            includeTitulars: z.boolean().optional(),
            includeTeam: z.boolean().optional(),
          }),
        ]),
      },
      PREVIEW_AUTOMATED: {
        method: 'POST',
        endpoint: 'mailjet/lists/automated/preview',
        schema: z.object({
          source: z.enum([
            'group',
            'store',
            'combined',
            'school',
            'sound',
            'insight',
            'storebag',
            'care',
          ]),
          rules: z.object({
            groupementAliases: z.array(z.string()).optional(),
            excludeGroupementAliases: z.array(z.string()).optional(),
            storeUserGroupAliases: z.array(z.string()).optional(),
            excludeStoreUserGroupAliases: z.array(z.string()).optional(),
            trainingId: z.number().int().positive().optional(),
            trainingIds: z.array(z.number().int().positive()).min(1).max(50).optional(),
            programId: z.number().int().positive().optional(),
            includeTitulars: z.boolean().optional(),
            includeTeam: z.boolean().optional(),
            audience: z.enum(['titular', 'team']).optional(),
            excludeCompleted: z.boolean().optional(),
            completionStatus: z.enum(['all', 'completed', 'not_completed']).optional(),
            careStatus: z.enum(['active', 'inactive']).optional(),
            // Périmètre company (Groupe, Combiné, Care). Absent = officines françaises.
            companyType: z.string().min(1).optional(),
            country: z.string().length(2).optional(),
            // Ignorer les inactifs (société inactive, compte jamais connecté). Absent = oui.
            excludeInactive: z.boolean().optional(),
          }),
        }),
      },
      SYNC_AUTOMATED: {
        method: 'POST',
        endpoint: 'mailjet/lists/automated/:id/sync',
        schema: z.object({ id: z.union([z.number(), z.string()]) }),
      },
      DELETE_AUTOMATED: {
        method: 'DELETE',
        endpoint: 'mailjet/lists/automated/:id',
        schema: z.object({ id: z.number() }),
      },
      IMPORT_MANUAL: {
        method: 'POST',
        endpoint: 'mailjet/lists/manual/import',
        schema: z.object({
          entity: z.string().min(1),
          name: z.string().min(1),
          cips: z.array(z.string()).max(5000),
          emails: z.array(z.string().email()).max(5000),
        }),
      },
      DELETE_MANUAL: {
        method: 'DELETE',
        endpoint: 'mailjet/lists/manual/:entity/:listId',
        schema: z.object({ entity: z.string(), listId: z.number() }),
      },
      UPDATE_EXCLUSION: {
        method: 'PUT',
        endpoint: 'mailjet/lists/exclusions',
        schema: z.object({ scope: z.string(), email: z.string().email(), excluded: z.boolean() }),
      },
      IMPORT_EXCLUSIONS: {
        method: 'POST',
        endpoint: 'mailjet/lists/exclusions/import',
        schema: z.object({
          scope: z.string(),
          emails: z.array(z.string().email()).min(1).max(5000),
        }),
      },
      SYNC_EXCLUSIONS: {
        method: 'POST',
        endpoint: 'mailjet/lists/exclusions/sync',
        schema: z.object({ scope: z.string() }),
      },
      UPDATE_USER_PREFERENCES: {
        method: 'PUT',
        endpoint: 'mailjet/preferences/:userId',
        schema: z.object({
          userId: z.number(),
          preferences: z.record(z.string(), z.boolean()),
          globalOptOut: z.boolean().optional(),
        }),
      },
      COPY_TEMPLATES: {
        method: 'POST',
        endpoint: 'mailjet/lists/templates/copy',
        schema: z.object({
          sourceEntity: z.string().min(1),
          targetEntity: z.string().min(1),
          templateIds: z.array(z.number().int().positive()).min(1).max(25),
          copyImages: z.boolean().optional(),
        }),
      },
      UPDATE_ACCOUNT_SETTINGS: {
        method: 'PUT',
        endpoint: 'mailjet/lists/accounts/:entity/settings',
        schema: z.object({
          entity: z.string(),
          alias: z.string().max(100),
          automatedListsEnabled: z.boolean(),
          visibleCountries: z.array(z.enum(['FR', 'IT', 'ES', 'DE', 'GB', 'BE', 'LU', 'NL', 'CH'])),
        }),
      },
    },
    // E-mails de programme (Sound / PLV-Sacherie / Insight).
    // Cf. pharmedigroup-api/docs/PROGRAM_MAIL_CAMPAIGNS_PLAN.md §3.2 et
    // lib/programEmail/programEmailSchemas.ts pour la forme exacte des corps.
    //
    // ATTENTION : `useRestMutation` invalide TOUTES les queries au succes. Le brouillon
    // (DRAFT) et l'exclusion d'une ligne se jouent DANS le dialog, ou ce rafraichissement
    // general rechargerait le tableau des participants derriere : ProgramEmailDialog les
    // appelle par `fetchRest`, en validant le corps avec le meme schema.
    PROGRAM_EMAIL: {
      // Modeles de CORPS rediges chez nous (coquille Mailjet). Le serveur refuse en 422 tout
      // jeton {{cle}} absent du catalogue, et en 409 la suppression d'un modele deja utilise.
      BODY_TEMPLATE_CREATE: {
        method: 'POST',
        endpoint: 'program_email_body_templates',
        schema: programEmailBodyTemplateSchema,
      },
      BODY_TEMPLATE_UPDATE: {
        method: 'PATCH',
        endpoint: 'program_email_body_templates/:id',
        schema: programEmailBodyTemplateSchema,
      },
      BODY_TEMPLATE_DELETE: {
        method: 'DELETE',
        endpoint: 'program_email_body_templates/:id',
        schema: programEmailEmptyBodySchema,
      },
      // Cree ou actualise (draftId) un brouillon : resolution, gel, regroupement, snapshots.
      DRAFT: {
        method: 'POST',
        endpoint: 'program_email_campaigns/draft',
        schema: programEmailDraftSchema,
      },
      // Statut de chaque CIP colle ou importe : matched | notInProgram | duplicate | invalid.
      RESOLVE_CIPS: {
        method: 'POST',
        endpoint: 'program_email_campaigns/resolve_cips',
        schema: programEmailResolveCipsSchema,
      },
      // UN message reel au testeur (administrateur) : pose test_sent_at + test_rules_hash.
      TEST: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/test',
        schema: programEmailTestSchema,
      },
      // Dry-run de fraicheur (D-28) : lignes qui seraient retirees aujourd'hui.
      REVALIDATE: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/revalidate',
        schema: programEmailEmptyBodySchema,
      },
      // Mise en file (draft) ou validation (pending_validation) — repond 202.
      DISPATCH: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/dispatch',
        schema: programEmailEmptyBodySchema,
      },
      // Conserve le brouillon pour plus tard : il echappe a la purge des brouillons
      // abandonnes (7 jours) et devient reprenable depuis le suivi du programme.
      SAVE_DRAFT: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/save_draft',
        schema: programEmailEmptyBodySchema,
      },
      // Un brouillon annule est supprime, une pending_validation est conservee en cancelled.
      CANCEL: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/cancel',
        schema: programEmailEmptyBodySchema,
      },
      // Relit les statuts Mailjet des messages de la campagne.
      REFRESH_STATUS: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/refresh_status',
        schema: programEmailEmptyBodySchema,
      },
      // Renvoi A L'UNITE d'un message parti (journal des messages d'une fiche), par le moteur
      // d'envoi de la campagne. `customId` ne resout que la variable de chemin : corps vide.
      MESSAGE_RESEND: {
        method: 'POST',
        endpoint: 'program_email_campaigns/messages/:customId/resend',
        schema: programEmailResendSchema,
      },
      // Export XLSX ASYNCHRONE des destinataires (.claude/rules/exports.md) : le corps porte
      // les filtres de l'ecran, la reponse est un 202 {queued, email, message}. Aucun fichier
      // ne revient ici — il part par e-mail au demandeur.
      RECIPIENTS_EXPORT: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/recipients/export',
        schema: programEmailRecipientsExportSchema,
        asyncExport: true,
      },
      // Exclure / reintegrer une ligne : ne touche jamais le hash du ciblage.
      RECIPIENT_EXCLUSION: {
        method: 'POST',
        endpoint: 'program_email_campaigns/:id/recipients/:recipientId/exclusion',
        schema: programEmailRecipientExclusionSchema,
      },
      // Envois programmes (lot S, plan §18). Le cycle de vie (editable/cancellable/deletable)
      // est calcule par le SERVEUR (ProgramEmailScheduleStatus) : ces mutations n'appliquent
      // aucune regle de transition cote front, elles relaient un geste.
      SCHEDULE_CREATE: {
        method: 'POST',
        endpoint: 'program_email_schedules',
        schema: programEmailScheduleSchema,
      },
      // N'est accepte par le serveur que sur une regle encore `scheduled` (409 sinon).
      SCHEDULE_UPDATE: {
        method: 'PATCH',
        endpoint: 'program_email_schedules/:id',
        schema: programEmailScheduleUpdateSchema,
      },
      SCHEDULE_CANCEL: {
        method: 'POST',
        endpoint: 'program_email_schedules/:id/cancel',
        schema: programEmailEmptyBodySchema,
      },
      // DELETE physique, uniquement `scheduled|cancelled` sans campagne liee (409 sinon).
      SCHEDULE_DELETE: {
        method: 'DELETE',
        endpoint: 'program_email_schedules/:id',
      },
      // Renvoie une COPIE NON PERSISTEE (id: null, runAt: null) : le dialog la pre-remplit,
      // l'utilisateur soumet ensuite une creation normale (SCHEDULE_CREATE).
      SCHEDULE_DUPLICATE: {
        method: 'POST',
        endpoint: 'program_email_schedules/:id/duplicate',
        schema: programEmailEmptyBodySchema,
      },
      // Dry-run de ciblage, AUCUNE persistance — meme forme de compteurs que le
      // recapitulatif de l'etape 2 du dialog manuel (reutilise PreviewSummary).
      SCHEDULE_ESTIMATE: {
        method: 'POST',
        endpoint: 'program_email_schedules/estimate',
        schema: programEmailScheduleEstimateSchema,
      },
      // Meme corps que le test d'une campagne ({email, recipientId?}) : reutilise le schema.
      SCHEDULE_TEST: {
        method: 'POST',
        endpoint: 'program_email_schedules/:id/test',
        schema: programEmailTestSchema,
      },
      // Reserve super-admin cote serveur (403 sinon) : force la prise en charge immediate.
      SCHEDULE_RUN_NOW: {
        method: 'POST',
        endpoint: 'program_email_schedules/:id/run_now',
        schema: programEmailEmptyBodySchema,
      },
    },
    // Formulaire de contact / demande de rappel (RegisterApiContactController, POST /ask_contact).
    // Cree une CrmTask de type contact et envoie l'email. Body { type, email, phone, companyName,
    // firstname, lastname, message, ... } -> tous optionnels (le back complete via le compte connecte/cip).
    // NOTE: l'upsert Mailjet (proprietes contact) fait par la gateway GraphQL n'est PAS encore porte
    // cote ask_contact -> a ajouter dans une wave backend ulterieure.
    CONTACT_CALL: {
      SEND_EMAIL: {
        method: 'POST',
        endpoint: 'ask_contact',
        schema: z
          .object({
            // Module concerne : decide de la boite destinataire cote API.
            entity: z.string().optional(),
            type: z.string().optional(),
            email: z.string().nullable().optional(),
            phone: z.string().nullable().optional(),
            companyName: z.string().nullable().optional(),
            firstname: z.string().nullable().optional(),
            lastname: z.string().nullable().optional(),
            message: z.string().nullable().optional(),
          })
          .passthrough(),
      },
    },
    // CTI Ringover (plan `pharmedigroup-api/docs/RINGOVER_CTI_PLAN.md`, lot 1) : un appel
    // lance depuis une fiche connait DEJA la pharmacie, l'agent et le contexte metier —
    // le webhook Ringover n'a plus qu'a completer la ligne. Reponse commune aux deux
    // operations : { id, crmTaskId, crmTaskCallId, callId, state, phoneNumber, mode, companyId }.
    // Erreurs metier 422 { code: INVALID_PHONE | COMPANY_REQUIRED | AGENT_RINGOVER_NUMBER_MISSING }
    // et 502 { code: RINGOVER_CALLBACK_FAILED } — traduites une par une cote ecran.
    // `ignoreSwitchUser: true` sur TOUTES les operations crm_calls : le softphone reste
    // branche pendant un « Se connecter en tant que » (l'iframe porte l'appel en cours, la
    // demonter le couperait). Ces ecritures decrivent l'activite telephonique de l'AGENT,
    // jamais celle de la personne consultee — et l'API exige ROLE_ADMIN dessus, donc elles
    // partiraient en 403 si l'en-tete X-Switch-User les attribuait au pharmacien.
    CRM_CALL: {
      // POST /crm_calls : appel initie depuis le CRM -> tache du jour + ligne
      // crm_task_call « en attente ». En mode `callback`, l'API appelle Ringover et
      // renvoie le call_id AVANT que le telephone sonne.
      INITIATE: {
        method: 'POST',
        endpoint: 'crm_calls',
        ignoreSwitchUser: true,
        schema: z.object({
          companyId: z.number().int().nullable().optional(),
          phoneNumber: z.string().optional(),
          direction: z.enum(['out', 'in']).optional(),
          mode: z.enum(['sdk', 'callback']).optional(),
          device: z.enum(['ALL', 'APP', 'WEB', 'SIP', 'MOB', 'EXT']).nullable().optional(),
          entity: z.string().nullable().optional(),
          callId: z.string().nullable().optional(),
        }),
      },
      // PATCH /crm_calls/:id : lien call_id + avancement d'etat depuis les evenements du
      // softphone (ringingCall -> answeredCall -> hangupCall).
      LINK: {
        method: 'PATCH',
        endpoint: 'crm_calls/:id',
        ignoreSwitchUser: true,
        schema: z.object({
          callId: z.string().nullable().optional(),
          state: z.enum(['ringing', 'answered', 'ended']),
          durationSeconds: z.number().int().nullable().optional(),
        }),
      },
    },
    CRM_TASK: {
      ASK_CONTACT: { method: 'POST', endpoint: 'ask_contact' },
      // Creation d'une tache CRM. Body = champs CrmTask (recipientUser/recipientCompany/crmTaskTag en IRI).
      ADD: {
        method: 'POST',
        endpoint: 'crm_tasks',
        // .passthrough() : gros formulaire CRM (commentaires, jsonAnswers, onboarding...), champs dynamiques.
        schema: z
          .object({
            title: z.string().optional(),
            updateCrmTaskCommentEntities: z.array(crmTaskCommentEntityInputSchema).optional(),
          })
          .passthrough(),
      },
      // Mise a jour d'une tache CRM. id -> :id, le reste a plat dans le body.
      UPDATE: {
        method: 'PATCH',
        endpoint: 'crm_tasks/:id',
        schema: z
          .object({
            id: z.number(),
            updateCrmTaskCommentEntities: z.array(crmTaskCommentEntityInputSchema).optional(),
          })
          .passthrough(),
      },
      DELETE: { method: 'DELETE', endpoint: 'crm_tasks/:id' },
      // « Informer le demandeur » d'une demande de cession : pavés de cession clos, la tâche
      // revient au demandeur, à son nom (CrmCessionRequesterService) ; `message` (facultatif)
      // devient le commentaire de son pavé. Réponse : ce qui a été RÉELLEMENT fait (`informed`,
      // `notified`, `dueDate`, `requester`). Refus : 409 { code: 'NOT_A_CESSION' }, 422
      // { code: 'REQUESTER_INVALID' | 'MESSAGE_TOO_LONG' }, 403 sous « Se connecter en tant
      // que » (la trace porte le nom de l'auteur réel).
      INFORM_CESSION_REQUESTER: {
        method: 'POST',
        endpoint: 'crm_tasks/:id/inform_cession_requester',
        // Une personne (`requesterId`) OU un service (`serviceId`, 23/10/2026), pas les deux.
        schema: z
          .object({
            id: z.number().int().positive(),
            requesterId: z.number().int().positive().optional(),
            serviceId: z.number().int().positive().optional(),
            // Même borne que l'API (CrmCessionRequesterService::MESSAGE_MAX_LENGTH).
            message: z.string().max(2000).optional(),
          })
          .refine(body => !!body.requesterId !== !!body.serviceId, {
            message: 'requesterId ou serviceId, exactement un des deux',
          }),
      },
      UPDATE_MULTIPLE_COLUMNS: {
        method: 'PATCH',
        endpoint: 'crm-tasks/update-multiple-columns',
        schema: z.object({
          ids: z.array(z.number()),
          columns: z
            .object({
              type: z.string().optional(),
              ownerUser: z.number().optional(),
              status: z.string().optional(),
            })
            .passthrough(),
        }),
      },
      // Traitement asynchrone d'un import CSV (post-upload S3). Renvoie un objet JSON (rapport/lien).
      PROCESS_IMPORT: {
        method: 'POST',
        endpoint: 'crm_tasks/import_crm_tasks',
        schema: z.object({ keyFile: z.string().min(1) }),
      },
    },
    CRM_TASK_COMMENT_ENTITY: {
      DELETE: { method: 'DELETE', endpoint: 'crm_task_comment_entities/:id' },
      // « Reprendre cette fiche » (bandeau CrmOtherAgentBanner) : le pavé EN COURS d'un
      // collègue passe au nom de l'agent courant, une ligne « Repris par X (précédemment Y) »
      // est ajoutée au pavé. Aucun e-mail. Réponse = ce qui a RÉELLEMENT été fait :
      //   { commentEntityId, crmTaskId, takenOver, reason: 'ALREADY_OWNER' | null,
      //     previousOwner, newOwner }
      // Refus : 409 { code: 'PAVE_NOT_OPEN' } (terminé / archivé entre-temps), 404 (supprimé),
      // 403 sous « Se connecter en tant que » (le geste doit porter le nom de son auteur —
      // PAS d'`ignoreSwitchUser` ici, contrairement aux opérations crm_calls).
      TAKE_OVER: {
        method: 'POST',
        endpoint: 'crm_task_comment_entities/:id/take_over',
        schema: z.object({ id: z.number().int().positive() }),
      },
      EXPORT: {
        method: 'POST',
        endpoint: 'crm_task_comment_entities_export',
        schema: z.object({
          dateStart: z.string(),
          dateEnd: z.string(),
        }),
      },
    },
    // Reporting « Relation pharmacien » — export asynchrone du reporting relation (CDC labo).
    // POST /crm_relation_summary/export -> 202 { status:'queued' } : le classeur XLSX est genere
    // en tache de fond puis envoye par email au demandeur. Meme filtres que le summary.
    // Laboratoires partenaires d'un groupement/chaine (hub groupement) : CRUD + priorite.
    // Ecriture scopee serveur : admin, ou manager sur SON groupement (fail-closed).
    GROUPEMENT_PARTNER: {
      CREATE: {
        method: 'POST',
        endpoint: 'groupement_partner_settings',
        // Ids scalaires (pas d'IRIs) : /companies/{id} est inaccessible aux managers
        // (CompanyExtension) — le processor API resout et valide les types.
        schema: z.object({
          groupementId: z.number(),
          industryId: z.number(),
          priority: z.number(),
          active: z.boolean().optional(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'groupement_partner_settings/:id',
        schema: z
          .object({ priority: z.number().optional(), active: z.boolean().optional() })
          .passthrough(),
      },
      DELETE: { method: 'DELETE', endpoint: 'groupement_partner_settings/:id' },
    },
    // Donneurs d'ordre autorises pour un groupement/chaine : ouvre a ce tiers les factures
    // SIGNEES (depuis le 01/10/2026) des campagnes de ce donneur d'ordre chez ses adherents.
    // Super admin uniquement (403 sinon) ; 422 si type de fiche invalide ou couple existant.
    GROUPEMENT_INVOICE_ACCESS: {
      CREATE: {
        method: 'POST',
        endpoint: 'groupement_invoice_accesses',
        // Ids scalaires (pas d'IRIs), resolus et valides par le processor API.
        schema: z.object({
          groupementId: z.number().int().positive(),
          partnerId: z.number().int().positive(),
        }),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'groupement_invoice_accesses/:id',
        schema: z.object({ id: z.number().int().positive() }),
      },
    },
    // Acces par MODULE d'un utilisateur d'une fiche (matrice commune labo / groupement).
    // Module porte par des roles : la « Fonction » correspondante est ecrite sur la fiche ;
    // « Ma centrale » : compte client cree / coupe (memes effets et memes codes que
    // « Autoriser Ma centrale »). 422 module inconnu ou non ouvert a la fiche, 403 cible hors
    // fiche. Admin : `company=<id>`.
    // Rapprochement Store : correction en masse des SIRET (fiche <-> compte boutique). Lignes
    // cochees (`ids`) ou tout le filtre (`query` = chaine de requete de la liste). dryRun (defaut)
    // = apercu du nombre de fiches touchees ; sinon 202, traitement en tache de fond et rapport
    // par e-mail au demandeur. Un SIREN different n'a pas d'action : il se tranche fiche par fiche.
    STORE_SIRET: {
      // Une cession depuis UNE ligne : ni selection ni filtre, l'API les refuse (decision du
      // 02/11/2026). Un SIRET se met a jour depuis le controle de la fiche. Meme liste que
      // StoreSiretLineAction, ecrite ici : ce fichier est dans l'arbre du shell, dont le budget de
      // modules est depasse (check-shell-budget).
      APPLY: {
        method: 'POST' as const,
        endpoint: 'store_siret_reconciliations/apply',
        schema: z.object({
          action: z.enum(['report_cession', 'create_closed_fiche']),
          ids: z.array(z.number().int()).length(1),
          dryRun: z.boolean(),
          // Création de fiche close : date de cession saisie (AAAA-MM-JJ), sinon celle de l'INSEE.
          cessionDate: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        }),
      },
      // Sans `companyIds` : recalcul complet en tache de fond (meme traitement que la nuit).
      // Avec : ces seules fiches, tout de suite (apres l'assistant ou le controle INSEE).
      RECOMPUTE: {
        method: 'POST' as const,
        endpoint: 'store_siret_reconciliations/recompute',
        schema: z.object({
          companyIds: z.array(z.number().int().positive()).min(1).max(20).optional(),
        }),
      },
    },
    // « Pilotage des pharmacies » : ecarter un sujet d'un drapeau (programme a cheval, SIRET
    // ferme, succession) — la fiche est recalculee aussitot ; recalcul complet a la demande.
    PILOTAGE: {
      ACKNOWLEDGE: {
        method: 'POST' as const,
        endpoint: 'pharmacy_pilotage/acknowledgements',
        schema: z.object({
          companyId: z.number().int().positive(),
          code: z.string().min(1).max(40),
          subjectKey: z.string().min(1).max(100),
          // Meme liste que PharmacyFlagAck::DECISIONS cote API.
          decision: z.enum(['verified', 'kept']),
          reason: z.string().max(255).optional(),
        }),
      },
      RECOMPUTE: {
        method: 'POST' as const,
        endpoint: 'pharmacy_pilotage/recompute',
        schema: z.object({}),
      },
      // Mise en attente d'une fiche (25/10/2026) : hors des contrôles jusqu'au `holdUntil` inclus
      // (180 jours au plus, PharmacyControlHold::MAX_DAYS), tâche d'appel confiée à l'auteur en option.
      HOLD: {
        method: 'POST' as const,
        endpoint: 'pharmacy_pilotage/holds',
        schema: z.object({
          companyId: z.number().int().positive(),
          reason: z.string().trim().min(1).max(255),
          holdUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          createCallTask: z.boolean(),
        }),
      },
      // « Reprendre » : la fiche revient dans les contrôles.
      HOLD_RELEASE: {
        method: 'POST' as const,
        endpoint: 'pharmacy_pilotage/holds/:holdId/release',
        schema: z.object({ holdId: z.number().int().positive() }),
      },
      // Nouvelle analyse « Par quoi commencer » par Merlin (Claude Sonnet), enregistrée ; plan
      // calculé s'il ne répond pas. Rien dans le corps : l'API compte elle-même.
      ADVICE: {
        method: 'POST' as const,
        endpoint: 'pharmacy_pilotage/advice',
        schema: z.object({}),
      },
      // « Marquer comme erreur de saisie » les SIREN de l'historique sans facture : 50 fiches au
      // plus (HistorySirenErroneousBatchService::MAX_COMPANIES), simulation d'abord, motif a la
      // validation.
      HISTORY_SIREN_ERRONEOUS: {
        method: 'POST' as const,
        endpoint: 'pharmacy_pilotage/history_siren_erroneous',
        schema: z.object({
          companyIds: z.array(z.number().int().positive()).min(1).max(50),
          sirens: z.array(z.string().regex(/^\d{9}$/)).optional(),
          dryRun: z.boolean(),
          reason: z.string().trim().min(1).max(255).optional(),
        }),
      },
      // Corriger en masse les fautes de saisie CNOP (nom, prénom, RPPS de l'Ordre sur le compte du
      // titulaire) : 50 changements au plus (CnopEntryErrorFixBatchService::MAX_ITEMS), simulation
      // d'abord.
      CNOP_ENTRY_ERROR_FIXES: {
        method: 'POST' as const,
        endpoint: 'pharmacy_pilotage/cnop_entry_error_fixes',
        schema: z.object({
          items: z
            .array(
              z.object({
                companyId: z.number().int().positive(),
                subjectKey: z.string().min(1).max(100),
              })
            )
            .min(1)
            .max(50),
          dryRun: z.boolean(),
        }),
      },
    },
    // Referentiel officiel GERS : import synchrone du fichier depose sur S3, puis mises a jour en
    // masse depuis le tableau de rapprochement (super admin).
    GERS: {
      // Ecriture des valeurs retenues de l'analyse de fiche. Liste blanche cote API (ni CIP
      // ni titulaire), SIRET revalide aupres de l'INSEE, et la fiche est historisee.
      FIELD_ANALYSIS_APPLY: {
        method: 'POST' as const,
        endpoint: 'pharmacies/:companyId/field_analysis/apply',
        schema: z.object({
          companyId: z.number().int().positive(),
          fields: z.array(z.object({ field: z.string().min(1), value: z.string().min(1) })).min(1),
        }),
      },
      // Actualite web de l'officine, en complement des referentiels. POST et non query :
      // chaque appel declenche une recherche web FACTUREE — un endpoint de lecture serait
      // prefetche par TanStack Query, ou rejoue a chaque remontage du panneau. Rien de ce
      // qu'il renvoie n'est applicable : voir le Model PharmacyWebInsight cote API.
      WEB_INSIGHT: {
        method: 'POST' as const,
        endpoint: 'pharmacies/:companyId/web_insight',
        schema: z.object({ companyId: z.number().int().positive() }),
      },
      IMPORT: {
        method: 'POST',
        endpoint: 'gers_reference_imports/import',
        schema: z.object({
          keyFile: z.string().min(1),
          fileName: z.string().optional(),
        }),
      },
      // Recalcul complet du rapprochement a la demande (~3 s) : meme traitement que le cron de
      // 5h20, sans attendre la nuit apres un import ou une correction faite hors du tableau.
      RECOMPUTE: {
        method: 'POST' as const,
        endpoint: 'gers_reconciliations/recompute',
        schema: z.object({}),
      },
      // Recalcul d'UNE fiche (et de son code postal), depuis l'en-tête de son contrôle : le GERS
      // publie son CIP mais le rapprochement datait d'avant un changement de CIP (22/10/2026).
      REFRESH_COMPANY: {
        method: 'POST' as const,
        endpoint: 'gers_reconciliations/companies/:companyId/refresh',
        schema: z.object({ companyId: z.union([z.number(), z.string()]) }),
      },
      // « Retablir » depuis la liste des titulaires a e-mail provisoire : la fiche est rendue a
      // l'ancien compte propose (ligne d'historique `historyId`). Eligibilite rejouee cote API,
      // 409 si la fiche ou la proposition a change depuis l'affichage.
      PROVISIONAL_OWNER_RESTORE: {
        method: 'POST' as const,
        endpoint: 'pharmacy_provisional_owner_emails/:companyId/restore',
        schema: z.object({
          companyId: z.number().int().positive(),
          historyId: z.number().int().positive(),
        }),
      },
      APPLY: {
        method: 'POST',
        endpoint: 'gers_reconciliations/apply',
        schema: z.object({
          // Liste UNIQUE, partagee avec le type GersApplyAction (types/group/gers.ts). Une
          // action posee a l'ecran mais absente du schema est rejetee ICI, avant l'appel, avec
          // un message illisible (« Invalid option: expected one of… ») : le bouton existe, le
          // serveur sait traiter, et rien ne part. C'est ce qui rendait « Nom titulaire »,
          // « Nom titulaire Store » et le refus de fusion inoperants.
          action: z.enum(GERS_APPLY_ACTIONS),
          ids: z.array(z.number().int()).min(1).max(2000),
          // Une seule ligne, comparee par l'admin : accepte un candidat « faible ».
          force: z.boolean().optional(),
          // Creation d'UNE ligne : e-mail d'invitation (case decochee par defaut), seulement
          // avec un vrai e-mail (Eurodep ou saisi). Ignore par le serveur pour plusieurs lignes.
          sendInvitation: z.boolean().optional(),
          // Creation d'UNE ligne, saisie de la boite « Creer la fiche » (relue par l'API, 422 si
          // l'e-mail est deja porte par un compte). E-mail ou telephone vide = aucun (e-mail
          // generique du CIP) ; nom vide = titulaire propose (CNOP fiable, sinon GERS).
          email: z.union([z.literal(''), z.string().trim().email().max(180)]).optional(),
          phone: z
            .string()
            .trim()
            .max(30)
            .regex(/^[+\d\s.\-/()]*$/)
            .optional(),
          lastname: z.string().trim().max(100).optional(),
          firstname: z.string().trim().max(100).optional(),
          // Vide = aucun SIRET ; sinon 14 chiffres (separateurs toleres), refuse s'il est deja
          // celui d'une autre fiche. Le verdict INSEE ne bloque pas.
          siret: z
            .string()
            .trim()
            .max(24)
            .regex(/^[\d\s.-]*$/)
            .optional(),
        }),
      },
      // MEME action sur TOUTES les lignes du filtre en cours (lot 7 « Qualite des fiches ») :
      // `query` = la chaine de requete de la liste (pastille, filtres de colonnes, zone), que
      // l'API rejoue par les filtres de GET /gers_reconciliations ; elle ne garde que les lignes
      // eligibles. dryRun (defaut) : compteurs, rien n'est ecrit ; sinon application, 422 au-dela
      // de 2 000 lignes eligibles.
      APPLY_FILTER: {
        method: 'POST' as const,
        endpoint: 'gers_reconciliations/apply_filter',
        schema: z.object({
          action: z.enum(GERS_APPLY_ACTIONS),
          query: z.string().max(8000),
          dryRun: z.boolean(),
        }),
      },
      // Titulaires « sans nom » : application en masse des propositions SURES (GERS recent ou
      // CNOP >= 90 sans ambiguite). MEME corps pour l'apercu (dryRun:true, 200 + compteurs,
      // rien n'est ecrit) et la demande d'ecriture reelle (dryRun:false, 202 + mise en file
      // asynchrone, rapport CSV envoye par e-mail au demandeur) — c'est le serveur qui decide
      // la reponse selon `dryRun`. `minScore` volontairement absent des appels ecran : l'UI ne
      // propose pas ce reglage, le defaut serveur (90) s'applique. Cf. GERS_REFERENCE.md
      // « Application en masse des propositions SURES ».
      APPLY_SURE_OWNER_NAMES: {
        method: 'POST' as const,
        endpoint: 'gers_reconciliations/apply_sure_owner_names',
        schema: z.object({
          dryRun: z.boolean().optional(),
          source: z.enum(GERS_SURE_OWNER_NAME_SOURCES).optional(),
          minScore: z.number().int().min(75).max(100).optional(),
        }),
      },
    },
    MODULE_ACCESS: {
      SET_USER: {
        method: 'POST',
        endpoint: 'module_access/user',
        schema: z.object({
          userId: z.number(),
          module: z.string().min(1),
          granted: z.boolean(),
          company: z.union([z.string(), z.number()]).optional(),
        }),
      },
    },
    CRM_RELATION: {
      RELATION_EXPORT: {
        method: 'POST',
        endpoint: 'crm_relation_summary/export',
        asyncExport: true,
        schema: z.object({
          from: z.string().optional(),
          to: z.string().optional(),
          partner: z.number().optional(),
          campaign: z.number().optional(),
          // Meme format que le resume : `<entite>:<id>` (ex. sound:774). Sans cette cle, Zod
          // retirait le programme du payload et le fichier ne suivait plus le filtre de l'ecran.
          program: z
            .string()
            .regex(/^[a-z_]+:\d+$/)
            .optional(),
          entity: z.string().optional(),
        }),
      },
    },
    // Dashboard admin v2 — export asynchrone « pharmacies a actionner » (score de priorite
    // d'action). POST /admin/action-priorities/export -> 202 { status:'queued' } : le classeur
    // XLSX est genere en tache de fond puis envoye par email au demandeur.
    ACTION_PRIORITIES: {
      EXPORT: {
        method: 'POST',
        endpoint: 'admin/action-priorities/export',
        asyncExport: true,
        schema: z.object({
          dateStart: z.string().optional(),
          dateEnd: z.string().optional(),
          actionTypes: z.array(z.string()).optional(),
          entity: z.string().optional(),
          // Inclut les pharmacies deprioritisees (snooze actif) dans le classeur exporte.
          includeSnoozed: z.boolean().optional(),
        }),
      },
      // Deprioriser une pharmacie (snooze). POST /admin/action-priorities/snoozes -> 201.
      // reason 'treated' sans `until` = +7 jours cote serveur ; 'absent' requiert un `until`.
      SNOOZE: {
        method: 'POST',
        endpoint: 'admin/action-priorities/snoozes',
        schema: z.object({
          companyId: z.number(),
          reason: z.enum(['treated', 'absent', 'other']),
          until: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
          comment: z.string().optional(),
        }),
      },
      // Annuler un snooze. DELETE /admin/action-priorities/snoozes/{id} -> 204.
      // `snoozeId` resout la path variable :snoozeId.
      UNSNOOZE: {
        method: 'DELETE',
        endpoint: 'admin/action-priorities/snoozes/:snoozeId',
        schema: z.object({ snoozeId: z.number() }),
      },
    },
    // Dashboard admin v2 — coefficients (poids) des signaux du score de priorite d'action.
    // POST /admin/action-score-coefficients : upsert en lot de la config tunable
    // (multiplicateur par type de signal + ordre d'affichage). Reponse { saved: number }.
    ACTION_SCORE_COEFFICIENT: {
      UPSERT: {
        method: 'POST',
        endpoint: 'admin/action-score-coefficients',
        schema: z.object({
          items: z.array(
            z.object({
              actionType: z.string().min(1),
              coefficient: z.number().min(0).max(100),
              position: z.number().int(),
            })
          ),
        }),
      },
    },
    // Parametrage cle/valeur du call center : POST /admin/call-center-settings (upsert par code).
    // `value` tableau d'ids : code `priority_partner_ids` (laboratoires prioritaires, 20 au
    // plus — meme plafond que la validation serveur, qui verifie en plus que chaque id est un
    // laboratoire existant).
    CALL_CENTER_SETTING: {
      UPSERT: {
        method: 'POST',
        endpoint: 'admin/call-center-settings',
        schema: z.object({
          code: z.string().min(1),
          value: z.union([
            z.number(),
            z.string(),
            z.null(),
            z.array(z.number().int().positive()).max(20),
          ]),
        }),
      },
    },
    // Reglage d'un type de tache (super admin). Le corps REMPLACE le reglage : chaque champ est
    // envoye. Code, origine et module sont figes cote API.
    CRM_TASK_TYPE: {
      UPDATE: {
        method: 'PATCH',
        endpoint: 'crm_task_types/:id',
        schema: z.object({
          id: z.number(),
          label: z.string().min(1),
          assignmentMode: z.enum([
            'author_choice',
            'person',
            'pharmacy_manager',
            'service',
            'people_pool',
          ]),
          defaultServiceId: z.number().nullable(),
          userIds: z.array(z.number()),
          defaultDueDays: z.number().int().min(0).max(365).nullable(),
          notifyMembers: z.boolean(),
          active: z.boolean(),
        }),
      },
    },
    // Services eligibles a un controle de fiches : la liste REMPLACE la precedente.
    PHARMACY_CONTROL_SETTING: {
      UPDATE: {
        method: 'PUT',
        endpoint: 'pharmacy_control_settings/:code',
        schema: z.object({ code: z.string().min(1), serviceIds: z.array(z.number()) }),
      },
    },
    CRM_TASK_TAG: {
      ADD: {
        method: 'POST',
        endpoint: 'crm_task_tags',
        schema: z.object({
          name: z.string().min(1),
          active: z.boolean().optional(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'crm_task_tags/:id',
        schema: z.object({
          id: z.number(),
          name: z.string().min(1),
          active: z.boolean().optional(),
        }),
      },
      DELETE: { method: 'DELETE', endpoint: 'crm_task_tags/:id' },
    },
    DOCUMENT: {
      // Editeur de la base documentaire : le type est obligatoire cote API (NotBlank) et fige
      // par son processor ; sans lui la creation repondait 422.
      ADD: {
        method: 'POST',
        endpoint: 'documents',
        schema: z.object({
          name: z.string().min(1),
          path: z.string().min(1),
          type: z.literal(DOCUMENT_TYPE_DOCUMENTARY_SPACE),
          company: z.string().nullable().optional(),
          entite: z.string().optional(),
          country: z.string().nullable().optional(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'documents/:id',
        schema: z.object({
          id: z.number(),
          name: z.string().min(1),
          path: z.string().min(1),
          type: z.literal(DOCUMENT_TYPE_DOCUMENTARY_SPACE),
          company: z.string().nullable().optional(),
          entite: z.string().optional(),
          country: z.string().nullable().optional(),
        }),
      },
      DELETE: { method: 'DELETE', endpoint: 'documents/:id' },
      // Signature GROUPEE (plan signature-unifiee-documents) : N documents dans UNE requete
      // Yousign. `documents` = 'all' (tout signer) ou liste de cles issues de
      // ApiQueryEndpoint GROUP.DOCUMENT.TO_SIGN_ME. Le serveur re-resout la liste et
      // auto-embarque les contrats prerequis des factures selectionnees. Reponse :
      // { urlRedirectSign, batchId, documents, excluded }. Gate serveur : flag unified_signature.
      SIGN_BATCH: {
        method: 'POST',
        endpoint: 'documents/electroniquesignature',
        schema: z.object({
          documents: z.union([z.literal('all'), z.array(z.string()).min(1)]),
          redirectUrl: z.string().min(1),
          // Prérequis facture (miroir du flux unitaire) : téléphone (OTP SMS Yousign) et
          // SIRET, persistés côté serveur + contrôle INSEE quand le lot vise des factures.
          phoneNumber: z.string().optional(),
          siret: z.string().optional(),
        }),
      },
      // Generation/signature electronique de contrat (Universign). Renvoie { urlRedirectSign }.
      GET_SIGN_CONTRACT: {
        method: 'POST',
        endpoint: 'documents/electroniquesignature',
        schema: z
          .object({
            redirectUrl: z.string().min(1),
            type: z.string().min(1),
          })
          .passthrough(),
      },
    },
    LEGAL_DOCUMENT: {
      // Publication d'une nouvelle version d'un document légal (ROLE_SUPER_ADMIN).
      // Chaque POST crée une version immuable horodatée (pas de PATCH/DELETE).
      PUBLISH: {
        method: 'POST',
        endpoint: 'legal_documents',
        schema: legalDocumentPublishSchema,
      },
      // L'utilisateur courant accepte une version d'un document contractuel (CGU).
      // Idempotent cote back : re-accepter une version deja acceptee renvoie la trace
      // existante. Passer le `referenceVersion` de legal_documents/pending, sinon 400.
      ACCEPT: {
        method: 'POST',
        endpoint: 'legal_document_acceptances',
        schema: legalDocumentAcceptSchema,
      },
    },
    // Desinscription newsletter publique : route Symfony native POST /api/v2/unsubscribe
    // (NewsletterUnsubscribeController). Body { email, cip } -> renvoie true. Pas d'auth.
    NEWSLETTER: {
      UNSUBSCRIBE: {
        method: 'POST',
        endpoint: 'unsubscribe',
        schema: z.object({
          email: z.string().min(1),
          cip: z.string().min(1),
        }),
      },
    },
    // Ping de presence : controller LastPingController (Post /lastPing) met a jour la date de
    // derniere connexion selon l'entite. Renvoie un booleen JSON.
    LAST_PING: {
      PING: {
        method: 'POST',
        endpoint: 'lastPing',
        schema: z.object({
          entity: z.string().optional(),
        }),
      },
    },
    // Dashboard admin (Model DashboardAdmin, Post /dashboardAdmin via DashboardAdminController).
    // Peut etre lourd. Renvoie { id, entity, data: [...] }.
    DASHBOARD: {
      GET: {
        method: 'POST',
        endpoint: 'dashboardAdmin',
        schema: z.object({
          entity: z.string(),
        }),
      },
    },
    INVOICES: {
      // Archive ZIP des factures SIGNEES (PDF + reponses aux questionnaires pour Insight).
      // POST /invoices/archive_export { companyId?, entite?, year? } -> 202 { status: 'queued' } ;
      // le ZIP est genere en asynchrone (Messenger) puis envoye par email au demandeur.
      // `companyId` n'est lu que pour un ADMIN (fiche pharmacie) : cote pharmacien le serveur
      // impose son propre perimetre et ignore ce qui est envoye.
      // `programId` (ADMIN) exporte TOUTES les officines d'un programme depuis l'onglet
      // « Factures Signées » : exclusif de `companyId`, qui exporte une seule pharmacie.
      // `entite` designe alors la colonne programme a lire cote serveur.
      ARCHIVE_EXPORT: {
        method: 'POST',
        endpoint: 'invoices/archive_export',
        asyncExport: true,
        schema: z.object({
          companyId: z.number().optional(),
          entite: z.string().optional(),
          year: z.number().optional(),
          programId: z.number().optional(),
          // Periode de SIGNATURE, AAAA-MM-JJ, bornes incluses.
          dateFrom: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
          dateTo: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        }),
      },
      // Grand livre NetSuite d'une pharmacie sur une periode, compte fournisseur OU client :
      // POST /invoices/ledger_export { account, companyId?, dateFrom, dateTo } (AAAA-MM-JJ,
      // bornes incluses) -> 202 { status: 'queued' }. XLSX envoye par email au demandeur.
      // `companyId` n'est lu que pour un ADMIN, comme ARCHIVE_EXPORT.
      LEDGER_EXPORT: {
        method: 'POST',
        endpoint: 'invoices/ledger_export',
        asyncExport: true,
        schema: z.object({
          // Grand livre fournisseur (Facturation) ou client Pharmedistore (Mes commandes).
          account: z.enum(['vendor', 'customer']),
          companyId: z.number().optional(),
          dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        }),
      },
      // Archive ZIP des factures et avoirs PHARMEDISTORE (commandes CS-Cart) d'une pharmacie,
      // sur une periode de dates de commande : POST /store_orders/invoices_export
      // { companyId?, dateFrom?, dateTo? } (AAAA-MM-JJ, bornes incluses) -> 202 { status: 'queued' }.
      // Meme partage que ARCHIVE_EXPORT : `companyId` n'est lu que pour un ADMIN.
      STORE_ORDERS_ARCHIVE_EXPORT: {
        method: 'POST',
        endpoint: 'store_orders/invoices_export',
        asyncExport: true,
        schema: z.object({
          companyId: z.number().optional(),
          dateFrom: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
          dateTo: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        }),
      },
      // Teletransmission ADMIN d'un lot de BUDGETS hors plateforme (lignes « a generer »
      // comprises). POST /invoices/teletransmit { items? | count? } -> 202
      // { status: 'queued', dispatched }. Le serveur intersecte avec les candidats
      // reellement eligibles (programme FZ termine, pas signee/transmise) : `dispatched`
      // est le compte REEL mis en file. Le handler GENERE la facture si besoin puis la
      // VALIDE (valid + pa_pending) ; le cron ERP de 04h20 transmet a NetSuite.
      TELETRANSMIT: {
        method: 'POST',
        endpoint: 'invoices/teletransmit',
        schema: z.object({
          items: z
            .array(
              z.object({
                entity: z.enum(['sound', 'store_bag']),
                budgetId: z.number().int().positive(),
              })
            )
            .max(1000)
            .optional(),
          count: z.number().int().min(1).max(1000).optional(),
        }),
      },
      // Export ASYNCHRONE de reddition des comptes (factures HORS PLATEFORME Sound/PLV payees sur
      // une periode, date de paiement NetSuite, tous labos ou un seul).
      // POST /invoices/account_report_export { dateFrom, dateTo, industryId? }
      // -> 202 { status: 'queued' }, XLSX envoye par e-mail au demandeur.
      ACCOUNT_REPORT_EXPORT: {
        method: 'POST',
        endpoint: 'invoices/account_report_export',
        asyncExport: true,
        schema: z
          .object({
            dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            industryId: z.number().int().positive().optional(),
          })
          .refine(v => v.dateFrom <= v.dateTo, { path: ['dateTo'] }),
      },
      // Lien de telechargement ZIP/CSV des factures exportees (Post /factures/exported_link,
      // InvoiceDownloadExportedAtController) -> URL signee AWS JSON. Aucun consommateur front migre
      // pour l'instant, endpoint declare pour usage REST futur.
      GET_LINK_DOWNLOAD_ZIP: {
        method: 'POST',
        endpoint: 'factures/exported_link',
        schema: z.object({
          date: z.string(),
          entity: z.string(),
        }),
      },
      // Export filtre des factures (Post /factures/export-by-filter, InvoiceExportedByFilterController).
      // Genere un fichier CSV/ZIP sur S3 -> renvoie { keyFile, linkSign }. Les dates peuvent etre null.
      EXPORT_FILTER: {
        method: 'POST',
        endpoint: 'factures/export-by-filter',
        schema: z
          .object({
            entity: z.string().min(1),
            type: z.string().min(1),
            edit_begin: z.string().nullable().optional(),
            edit_end: z.string().nullable().optional(),
            gen_begin: z.string().nullable().optional(),
            gen_end: z.string().nullable().optional(),
            start: z.string().nullable().optional(),
            end: z.string().nullable().optional(),
            create_begin: z.string().nullable().optional(),
            create_end: z.string().nullable().optional(),
            invoiceSigned: z.string().nullable().optional(),
          })
          .passthrough(),
      },
    },
    POINTS_SHOP_HISTORY: {
      MANUAL_CREDIT: { method: 'POST', endpoint: 'points_shop_histories/manual-credit' },
      UPDATE: { method: 'PATCH', endpoint: 'points_shop_histories/:id' },
    },
    INDUSTRY: {
      // Import en masse des utilisateurs du labo (cle S3 + option d'envoi des identifiants).
      // Traitement asynchrone : reponse 202, rapport par email au demandeur.
      USERS_IMPORT: { endpoint: 'industry/users/import', method: 'POST' },
      // Export XLSX ASYNCHRONE des destinataires d'un envoi d'e-mails de campagne, cote
      // LABORATOIRE (22/09/2026) : memes filtres que l'ecran, 202 puis classeur par e-mail au
      // demandeur. Reserve a la capacite d'extraction du labo (Siege, DR) ; le handler rejoue
      // le perimetre du demandeur — ses officines, jamais davantage.
      PROGRAM_EMAIL_RECIPIENTS_EXPORT: {
        method: 'POST',
        endpoint: 'industry/program_email_campaigns/:id/recipients/export',
        schema: programEmailRecipientsExportSchema,
        asyncExport: true,
      },
      // Export complet des donnees du labo (Espace Industry, page Parametres, Siege uniquement).
      // POST /industry/data_export { dateStart, dateEnd } (ISO) -> 202 { status: 'queued' } ;
      // le ZIP est genere en asynchrone (Messenger) puis envoye par email au demandeur.
      DATA_EXPORT: {
        method: 'POST',
        endpoint: 'industry/data_export',
        asyncExport: true,
        schema: z.object({
          dateStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          dateEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        }),
      },
      // Export ZIP des PDF de contrats SELECTIONNES (Espace Industry, ecran Documents & contrats,
      // Siege uniquement). POST /industry/contracts_export { documentIds } -> 202 { status:'queued' } ;
      // le ZIP est genere en asynchrone (Messenger) puis envoye par email au demandeur. Les ids sont
      // re-verifies cote serveur (scope labo fail-closed) : un id hors perimetre est ignore.
      CONTRACTS_EXPORT: {
        method: 'POST',
        endpoint: 'industry/contracts_export',
        asyncExport: true,
        schema: z.object({
          documentIds: z.array(z.number().int().positive()).min(1).max(500),
        }),
      },
      // Export XLSX de l'ecran « Mes pharmacies » (Espace Industry). POST
      // /industry/pharmacies/export { filtres de l'ecran } -> 202 { status: 'queued' } ; le fichier
      // est genere en asynchrone (Messenger) puis envoye par email au demandeur. Le perimetre labo
      // n'est JAMAIS transmis : il est resolu serveur. Cf. `.claude/rules/exports.md`.
      // Derogation de droit d'espace labo (admin) : POST cree l'ecart, DELETE revient au defaut.
      ACCESS_RIGHT_CREATE: {
        method: 'POST',
        endpoint: 'industry_access_rights',
        schema: z.object({
          company: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
          role: z.enum(['siege', 'dr', 'delegue']),
          action: z.string().max(60),
          allowed: z.boolean(),
        }),
      },
      ACCESS_RIGHT_DELETE: {
        method: 'DELETE',
        endpoint: 'industry_access_rights/:id',
      },
      // Export XLSX ASYNCHRONE generique des tableaux de l'espace labo (Facturation, souhaits
      // PLV, participants campagne, refus LGO, documents, zones) : 202 + email au demandeur.
      // Scope labo + territoire + fenetre programsVisibleFrom rejoues serveur.
      TABLE_EXPORT: {
        method: 'POST',
        endpoint: 'industry/table_exports',
        asyncExport: true,
        schema: z.object({
          table: z.enum([
            'invoices',
            'campaign_participants_sound',
            'campaign_participants_plv',
            'plv_wishes',
            'lgo_refused_orders',
            'documents',
            'zones',
            // Suivi d'une campagne partenaire (ecran BO) : une ligne par BUDGET de programme.
            // Doit rester aligne sur IndustryTableExportRequest::TABLES cote API.
            'partner_campaign_tracking',
            // Repartition GX / BS d'une campagne (section du detail campagne, espace labo) :
            // une ligne par OFFICINE, ventilee par enveloppe.
            'campaign_axis_breakdown',
          ]),
          // Filtres de l'ecran, scalaires uniquement (le fichier correspond a l'affichage).
          filters: z.record(z.string(), z.any()).nullish(),
        }),
      },
      PHARMACIES_EXPORT: {
        method: 'POST',
        endpoint: 'industry/pharmacies/export',
        asyncExport: true,
        schema: z.object({
          search: z.string().max(255).nullish(),
          administrativeRegion: z.string().max(255).nullish(),
          // Zone COMMERCIALE du labo (secteur), distincte de la zone administrative ci-dessus.
          companyZone: z.number().int().positive().nullish(),
          partnerSubGroup: z.number().int().positive().nullish(),
          partnerCluster: z.number().int().positive().nullish(),
          // Filtres CONTRATS de l'ecran : memes valeurs que l'API (fail-closed serveur).
          contractCadre: z.enum(['signed', 'missing']).nullish(),
          contractCampaign: z.enum(['application', 'ponctuel', 'missing']).nullish(),
          programme: z
            .string()
            .regex(/^(all|current|(sound|plv)-\d+)$/)
            .nullish(),
          // Decision de participation (avec un programme precis) : meme regle que la liste.
          // `expired` = delai de signature expire — valeur propre au FILTRE (le serveur la
          // traite a part, `rejected` ne ramenant que les vrais refus). Sans elle ici, l'export
          // d'une liste filtree sur « Délai expiré » etait refuse avant meme de partir.
          participationDecision: z.enum(['rejected', 'accepted', 'pending', 'expired']).nullish(),
          // Deep-link « Pharmacies sans UGA » (carte de couverture du zonage) : le fichier doit
          // porter le meme perimetre que l'ecran.
          codeUgaEmpty: z.boolean().nullish(),
        }),
      },
      // Export Generique / Biosimilaire par officine et par mois (22/10/2026) : perimetre et
      // borne de 24 mois reverifies serveur. Ouvert a toute l'equipe du labo, delegue compris.
      PHARMACY_AXIS_EXPORT: {
        method: 'POST',
        endpoint: 'industry/pharmacies/axis_export',
        asyncExport: true,
        schema: z.object({
          dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        }),
      },
    },
    COMPANY_ZONE: {
      ADD: { method: 'POST', endpoint: 'company_zones' },
      UPDATE: { method: 'PATCH', endpoint: 'company_zones/:id' },
      DELETE: { method: 'DELETE', endpoint: 'company_zones/:id' },
      ADD_ASSIGNMENT: { method: 'POST', endpoint: 'company_zone_assignments' },
      DELETE_ASSIGNMENT: { method: 'DELETE', endpoint: 'company_zone_assignments/:id' },
      // Rattachement d'UGA a une zone. Ajout EN MASSE : le corps porte `codes: string[]`, un
      // secteur portant une dizaine d'UGA. Le serveur reprojette les pharmacies dans la foulee.
      //
      // `codeUga` porte le PREMIER code et n'est pas redondant : la validation Symfony s'execute
      // AVANT le processor, sur l'entite deserialisee — sans ce champ, la ressource part vide et
      // part en 422 avant d'avoir ete lue. Les codes suivants sont crees par le processor.
      ADD_UGA: {
        method: 'POST',
        endpoint: 'company_zone_ugas',
        schema: z.object({
          zone: z.string(),
          codeUga: z.string().regex(/^[0-9]{2}[A-Z0-9]{3}$/),
          codes: z.array(z.string().regex(/^[0-9]{2}[A-Z0-9]{3}$/)).min(1),
        }),
      },
      DELETE_UGA: { method: 'DELETE', endpoint: 'company_zone_ugas/:id' },
      // Affectation des delegues et DR depuis l'export « Zones » complete (06/10/2026) :
      // multipart (champ `file`, + `apply=1` pour ecrire — simulation sinon), poste par fetch
      // direct (fetchRest serialise en JSON). Schema Zod du fichier : ZoneUserRolesImportDialog.
      IMPORT_USER_ROLES: { method: 'POST' as const, endpoint: 'company_zones/import_user_roles' },
      // Un compte ajoute (ou retire) dans un role sur toutes les zones du labo ou une selection
      // (22/10/2026 : le directeur des ventes DR de toutes les zones). Simulation sans `apply`.
      BULK_USER_ROLE: {
        method: 'POST' as const,
        endpoint: 'company_zones/bulk_user_role',
        schema: z.object({
          userId: z.number().int().positive(),
          role: z.enum(['dr', 'delegue']),
          action: z.enum(['add', 'remove']),
          zoneIds: z.array(z.number().int().positive()).min(1).optional(),
          apply: z.boolean(),
        }),
      },
      // Code UGA MANQUANT d'une pharmacie, saisi depuis la carte « Pharmacies sans UGA »
      // (06/10/2026). Le serveur ne remplace jamais un code pose et refuse un code d'un autre
      // departement que la pharmacie ; il projette aussitot la pharmacie dans sa zone.
      SET_PHARMACY_UGA: {
        method: 'POST' as const,
        endpoint: 'company_zone_uga_coverage/pharmacy_code_uga',
        schema: z.object({
          pharmacyId: z.number().int().positive(),
          codeUga: z.string().regex(/^[0-9]{2}[A-Z0-9]{3}$/),
        }),
      },
    },
    COMPANY_LIST_CONTACT: {
      // Entity CompanyListContact (Post via CompanyListContactItemProcessor : rattache la company
      // courante puis reconcilie les pharmacies depuis keyFile.filePath). `deleteContact` est lu du
      // body brut par le processor -> passthrough.
      ADD: {
        method: 'POST',
        endpoint: 'company_list_contacts',
        schema: z
          .object({
            name: z.string().nullable().optional(),
            keyFile: z
              .object({
                newNameFileOrigin: z.string().nullable().optional(),
                filePath: z.string().nullable().optional(),
              })
              .passthrough()
              .nullable()
              .optional(),
            deleteContact: z.boolean().optional(),
          })
          .passthrough(),
      },
      // Entity CompanyListContact (Patch via CompanyListContactItemProcessor).
      UPDATE: {
        method: 'PATCH',
        endpoint: 'company_list_contacts/:id',
        schema: z
          .object({
            id: z.number(),
            name: z.string().nullable().optional(),
            keyFile: z
              .object({
                newNameFileOrigin: z.string().nullable().optional(),
                filePath: z.string().nullable().optional(),
              })
              .passthrough()
              .nullable()
              .optional(),
            deleteContact: z.boolean().optional(),
          })
          .passthrough(),
      },
      // POST /company_list_contacts_report_file (controller CompanyListContactReportFileController)
      // -> { report: string(json) }. Consomme via le formulaire Uploader/Dropzone (couple Apollo)
      // -> endpoint declare mais consommateur laisse en Apollo.
      REPORT_FILE: {
        method: 'POST',
        endpoint: 'company_list_contacts_report_file',
        schema: z.object({
          filePath: z.string(),
        }),
      },
    },
    CONDITION_COMMERCIAL: {
      // Corps `groupConditionCommercial:write` — groupement/laboratoire en IRI Company.
      ADD: {
        method: 'POST',
        endpoint: 'group_condition_commercials',
        schema: z.object({
          name: z.string().min(1),
          targetType: z.enum(['groupement', 'cip_list', 'laboratoire']),
          groupement: z.string().nullable().optional(),
          laboratoire: z.string().nullable().optional(),
          cipList: z.array(z.string()).nullable().optional(),
          entity: z.string().nullable().optional(),
          comment: z.string().nullable().optional(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'group_condition_commercials/:id',
        schema: z.object({
          id: z.number(),
          name: z.string().min(1).optional(),
          targetType: z.enum(['groupement', 'cip_list', 'laboratoire']).optional(),
          groupement: z.string().nullable().optional(),
          laboratoire: z.string().nullable().optional(),
          cipList: z.array(z.string()).nullable().optional(),
          entity: z.string().nullable().optional(),
          comment: z.string().nullable().optional(),
        }),
      },
      DELETE: { method: 'DELETE', endpoint: 'group_condition_commercials/:id' },
    },
    CONTRACT: {
      DELETE_INSIGHT: { method: 'DELETE', endpoint: 'contract_insights/:id' },
      DELETE_SOUND: { method: 'DELETE', endpoint: 'contrat_sounds/:id' },
      DELETE_STORE: { method: 'DELETE', endpoint: 'contrat_stores/:id' },
      DELETE_STORE_BAG: { method: 'DELETE', endpoint: 'contrat_store_bags/:id' },
      DELETE_SCHOOL: { method: 'DELETE', endpoint: 'contrat_schools/:id' },
      DELETE_LIFESCAN: { method: 'DELETE', endpoint: 'contrat_lifescans/:id' },
      REACTIVE_SOUND: { method: 'POST', endpoint: 'contrat_sounds/:id/reactive' },
      REACTIVE_STORE_BAG: { method: 'POST', endpoint: 'contrat_store_bags/:id/reactive' },
      // Operation Post declaree sur ContractInsight : uriTemplate `/contrat_insights/{id}/reactive`
      // (prefixe `contrat`, distinct du Delete par defaut `contract_insights/{id}`).
      REACTIVE_INSIGHT: { method: 'POST', endpoint: 'contrat_insights/:id/reactive' },
      // Contrats de CAMPAGNE partenaire (application / ponctuel) : pas de table legacy, l'`:id`
      // est l'id DOCUMENT. Le contrat reste en base, marque invalide (`invalidatedAt`).
      INVALIDATE_PARTNER: {
        method: 'POST',
        endpoint: 'documents/:id/partner_contract_invalidate',
      },
      REACTIVE_PARTNER: { method: 'POST', endpoint: 'documents/:id/partner_contract_reactivate' },
    },
    COMPANY: {
      DELETE: { method: 'DELETE', endpoint: 'companies/:companyId' },
      // PATCH /companies/{id} (processor CompanyPostAndPatchProcessor). Corps très large et
      // variable selon le type de company -> schéma `.passthrough()`. Il ne FERME que les
      // réglages « Campagnes GX / BS » (admin-only côté serveur), dont une valeur hors enum
      // ou une chaîne vide produirait une ValueError PHP (500) au lieu d'un 422 :
      //  - `campaignAllocationMode` : enum fermé, jamais vide ni null ;
      //  - les 3 forfaits + le seuil de nouvelle pose : nombre >= 0 ou `null` — JAMAIS `''` ;
      //  - les 2 plafonds (lot 1) : ENTIER >= 1 ou `null` — un `0` desactiverait toute
      //    allocation de consommables sans le dire, et `''` ferait un 500 sur un int.
      UPDATE: {
        method: 'PATCH',
        endpoint: 'companies/:companyId',
        schema: z
          .object({
            // Territoire d'une fiche URPS : la regle est relue par le serveur (422 si invalide).
            urpsZipPrefixes: z.array(z.string()).nullable().optional(),
            // Modules ouverts a l'espace groupement / chaine (fiche groupement / chain, admin).
            groupementModules: z
              .array(z.enum(['sound', 'storeBag', 'school', 'store']))
              .nullable()
              .optional(),
            campaignAllocationMode: z.enum(['single', 'dual_axis']).optional(),
            campaignLogisticsAdminFee: z.number().nonnegative().nullable().optional(),
            campaignRadioMonthlyFee: z.number().nonnegative().nullable().optional(),
            campaignRadioSetupFee: z.number().nonnegative().nullable().optional(),
            // Remise radio + PLV en POURCENTAGE (0 a 100) ; `null` = le moteur applique 10.
            campaignRadioPlvDiscountPercent: z.number().min(0).max(100).nullable().optional(),
            // `null` = nouvelle pose radio DESACTIVEE (pas « valeur par defaut »).
            campaignNewInstallationThreshold: z.number().positive().nullable().optional(),
            // `null` = regle desactivee : la radio en place sert quel que soit le montant.
            campaignRadioPreferenceThreshold: z.number().positive().nullable().optional(),
            campaignDefaultMaxUnitsPerPharmacy: z
              .number()
              .int()
              .min(1)
              .max(1000)
              .nullable()
              .optional(),
            campaignFixedPlvMaxPerProduct: z.number().int().min(1).max(50).nullable().optional(),
            // Ordre des criteres consommables (defaut du LABO) : liste de 3 cles distinctes
            // ou `null` (= la fiche ne fixe rien, le moteur applique son ordre).
            campaignConsumableCriteriaOrder: consumableCriteriaOrderSchema.nullable().optional(),
            // Date limite de signature des contrats de campagne (laboratoire, admin-only cote
            // serveur) : un booleen STRICT (jamais `''`) et N, « signature jusqu'au Nᵉ jour du
            // programme » — entier de 1 a 90 (bornes de `Company::$campaignSignatureDeadlineDays`)
            // ou `null` (= defaut serveur, 7). Le reglage ne fait que PROPOSER la date a la
            // creation d'une campagne : il ne modifie aucune campagne existante.
            campaignSignatureDeadlineEnabled: z.boolean().optional(),
            campaignSignatureDeadlineDays: z.number().int().min(1).max(90).nullable().optional(),
            // « Ma centrale » (groupement/chaine) : id du GROUPE UTILISATEUR CS-Cart de la
            // centrale, ou `null` (aucune centrale). JAMAIS `''` : colonne INT cote API.
            storeDefaultUsergroupId: z.number().int().positive().nullable().optional(),
          })
          .passthrough(),
      },
      // Rattache (ou détache avec null) le modèle de CONTRAT CADRE partenaire à une company
      // industry (partner « à sous-groupes »). PATCH companies/:companyId, corps validé Zod :
      // IRI d'un signable_document_template ou null. Sécurité serveur = COMPANY_EDIT (admin).
      SET_CADRE_TEMPLATE: {
        method: 'PATCH',
        endpoint: 'companies/:companyId',
        schema: z.object({
          partnerContractCadreTemplate: z
            .string()
            .regex(/^\/api\/v2\/signable_document_templates\/\d+$/)
            .nullable(),
        }),
      },
      // Modele du contrat d'APPLICATION partenaire : meme mecanique que le cadre, sur la
      // fiche du laboratoire (simplification : plus de parametrage par programme Sound/Bag).
      SET_APPLICATION_TEMPLATE: {
        method: 'PATCH',
        endpoint: 'companies/:companyId',
        schema: z.object({
          partnerContractApplicationTemplate: z
            .string()
            .regex(/^\/api\/v2\/signable_document_templates\/\d+$/)
            .nullable(),
        }),
      },
      // Modele du contrat PONCTUEL partenaire : signe par les pharmacies CLASSIQUES /
      // non-taggees a chaque campagne (matrice 2026-08 — cadre + application = premium).
      SET_PONCTUEL_TEMPLATE: {
        method: 'PATCH',
        endpoint: 'companies/:companyId',
        schema: z.object({
          partnerContractPonctuelTemplate: z
            .string()
            .regex(/^\/api\/v2\/signable_document_templates\/\d+$/)
            .nullable(),
        }),
      },
      // POST /companies (processor CompanyPostAndPatchProcessor). Corps `company:post` :
      // gros formulaire (companyName, type, country, email, adresse, IRIs groupement/manager...).
      // Schema permissif `.passthrough()` car les champs varient selon le type de company.
      ADD: {
        method: 'POST',
        endpoint: 'companies',
        schema: z.object({}).passthrough(),
      },
      // PUT /company/{id}/healthProfessionalSegmentation (CrudHealthProfessionalSegmentationController).
      // Segmentation d'une structure PDS : modeExercice, specialite, nbPractitioners, urbanSection.
      CREATE_OR_UPDATE_HEALTH_PROFESSIONAL_SEGMENTATION: {
        method: 'PUT',
        endpoint: 'company/:id/healthProfessionalSegmentation',
        schema: z.object({ id: z.number() }).passthrough(),
      },
      // PUT /company/{id}/activeStoreBag (ActiveStoreBagPharmacyController). `id` resout la path
      // variable, corps lu brut : { enabled, statusOnboardingStoreBag }. Reponse: true.
      ACTIVE_STORE_BAG: {
        method: 'PUT',
        endpoint: 'company/:id/activeStoreBag',
        schema: z.object({
          id: z.number(),
          enabled: z.boolean().optional(),
          statusOnboardingStoreBag: z.string().nullable().optional(),
        }),
      },
      // Applique l'etablissement INSEE choisi. `denominationLegale` alimente la RAISON
      // SOCIALE ; `companyName` renomme l'officine et n'est envoye que si l'admin l'a
      // explicitement demande (case decochee par defaut) — les deux ne se confondent pas.
      // `naf` est l'activite de l'etablissement choisi : il suit le SIRET, sans case a cocher.
      APPLY_INSEE_CONTROL: {
        method: 'PATCH',
        endpoint: 'companies/:companyId/insee-control/apply',
        schema: z.object({
          companyId: z.union([z.number(), z.string()]),
          selectedSiret: z.string().trim().min(1),
          statusSociety: z.string().nullable().optional(),
          denominationLegale: z.string().trim().min(1).optional(),
          companyName: z.string().trim().min(1).optional(),
          address: z.string().nullable().optional(),
          zipCode: z.string().nullable().optional(),
          city: z.string().nullable().optional(),
          // Adresse INSEE différente (D-11) : déménagement, INSEE en principale, ou gardée en
          // secondaire. Absent : aucune adresse n'est envoyée.
          addressMode: z.enum(['move', 'insee_main', 'keep_current']).optional(),
          // Case « UGA du GERS » : l'API relit le code sur la ligne GERS de la fiche.
          codeUgaFromGers: z.boolean().optional(),
          naf: z.string().trim().max(10).nullable().optional(),
          // Provenance de la ligne validee. `eurodep` (SIRET propose par Eurodep) : un premier
          // SIRET pose sur une fiche vide est historise cote API.
          source: z.string().trim().max(30).nullable().optional(),
          // Date du changement de SIRET (fin de la ligne d'historique) : la reprise INSEE
          // proposée à l'écran, modifiable ; absente = aujourd'hui (17/10/2026).
          changedAt: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
          // Début de la ligne d'historique : création INSEE de l'ancien SIRET, modifiable ;
          // absent = l'API la relit à l'INSEE, puis la création de la fiche.
          startedAt: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        }),
      },
      MERGE_COMPANIES: {
        method: 'POST',
        endpoint: 'merge-companies/:companyId',
      },
      // « Ne pas fusionner » (dialogue de controle, super admin) : la paire de fiches est
      // enregistree dans gers_merge_rejection — la table du refus de la page GERS — et n'est
      // plus proposee, ni dans le dialogue ni sur la page GERS. Reponse : { rejected: bool }.
      MERGE_REJECT: {
        method: 'POST' as const,
        endpoint: 'pharmacies/:companyId/merge_rejections',
        schema: z.object({
          companyId: z.number().int().positive(),
          otherCompanyId: z.number().int().positive(),
          reason: z.string().trim().max(255).optional(),
        }),
      },
      // Retablit un rapprochement ecarte : la paire redevient proposable. { restored: bool }.
      MERGE_RESTORE: {
        method: 'POST' as const,
        endpoint: 'pharmacies/:companyId/merge_rejections/restore',
        schema: z.object({
          companyId: z.number().int().positive(),
          otherCompanyId: z.number().int().positive(),
        }),
      },
      // Archivage du CIP, option du geste « Changer le CIP » : `oldCip` vide = le CIP courant
      // part dans l'historique ; renseigné = seul cet ancien CIP est historisé.
      ARCHIVE_CIP: {
        method: 'POST',
        endpoint: 'companies/:companyId/archive-cip',
        schema: z.object({
          oldCip: z.string().trim().nullable().optional(),
          // Le GERS publie encore le CIP courant : l'admin confirme qu'un nouveau CIP est attribué.
          gersPublishedConfirmed: z.boolean().optional(),
        }),
      },
      // Suppression d'une fiche pharmacie avec ses comptes (CompanyDeletionController, super
      // admin) : la fiche part en soft delete, les comptes sont désactivés et leur e-mail archivé
      // — ou conservés. `deleteStoreAccount` (opt-in) supprime aussi le compte client
      // Pharmedistore s'il n'a aucune commande. Réponse : { companyId, accounts: [{ email, owner,
      // fate, archivedEmail }], store: { deleted, reason } | null } — ce qui a RÉELLEMENT été fait.
      DELETE_WITH_ACCOUNTS: {
        method: 'DELETE',
        endpoint: 'companies/:companyId/deletion',
        schema: z.object({
          companyId: z.union([z.number(), z.string()]),
          deleteStoreAccount: z.boolean(),
        }),
      },
      // Nouveau CIP pose sur la fiche (CompanyChangeCipController) : le CIP courant part
      // dans « Anciens proprietaires » (pharmacy_user_history, evenement `cip_change`)
      // avant d'etre remplace. 422 si le CIP est deja celui de la fiche ou deja pris.
      CHANGE_CIP: {
        method: 'POST',
        endpoint: 'companies/:companyId/change-cip',
        schema: z.object({
          companyId: z.union([z.number(), z.string()]),
          newCip: z.string().trim().min(1).max(20),
          reason: z.string().trim().max(255).nullable().optional(),
          // DEMENAGEMENT, optionnel : une officine qui bouge change de CIP, d'adresse et
          // souvent de SIRET d'un coup. Envoyes dans le MEME appel, ils ne laissent qu'UNE
          // ligne d'historique, portant tout l'etat quitte.
          siret: z.string().trim().max(20).optional(),
          address: z.string().trim().max(255).optional(),
          zipCode: z.string().trim().max(10).optional(),
          city: z.string().trim().max(255).optional(),
        }),
      },
      // PUT /userPharmacyHolder/{id} (UserPharmacyHolderController -> updatePharmacyHolder).
      // « Ajouter un nouveau titulaire ». Mot de passe FACULTATIF (l'API en genere un ;
      // il n'atteint le titulaire que si `sendInvitation` est coche a l'ecran, et qu'un
      // compte est reellement cree). `archiveCurrentHolder` ne vaut que si l'e-mail saisi est
      // celui du titulaire en place : le compte actuel est alors archive, son e-mail libere
      // (suffixe numerique) et un compte NEUF reprend l'adresse.
      USER_UPDATE_HOLDER: {
        method: 'PUT',
        endpoint: 'userPharmacyHolder/:companyId',
        schema: z
          .object({
            companyId: z.union([z.number(), z.string()]),
            email: z.string().trim().email(),
            firstname: z.string().trim().min(1),
            lastname: z.string().trim().min(1),
            // Longueur minimale portée par le schéma du formulaire : ici la chaîne vide
            // (champ touché puis vidé) doit passer, elle vaut « pas de mot de passe ».
            password: z.string().optional().nullable(),
            siret: z.string().optional().nullable(),
            archiveCurrentHolder: z.boolean().optional(),
            // Case a cocher du formulaire : commande l'e-mail d'invitation (identifiants).
            // Absente = pas d'envoi, c'est le defaut cote API.
            sendInvitation: z.boolean().optional(),
            // Compte deja titulaire d'une autre pharmacie : il la GARDE (multi-officines) au
            // lieu d'en changer. Absente = il en change, comme a l'import.
            keepOtherPharmacies: z.boolean().optional(),
            // Motif OBLIGATOIRE, ecrit avec l'auteur sur l'entree « Anciens proprietaires »
            // (lot 6 du chantier « Qualite des fiches ») ; « Autre » exige un detail.
            reasonCode: z.enum(HOLDER_CHANGE_REASONS),
            reasonDetail: z.string().trim().max(200).optional().nullable(),
            // RPPS du titulaire choisi au CNOP : pose sur le compte s'il n'en a pas.
            rppsNumber: z.string().trim().optional().nullable(),
          })
          .passthrough()
          .refine(
            value =>
              value.reasonCode !== HOLDER_CHANGE_REASON_REQUIRING_DETAIL || !!value.reasonDetail,
            { path: ['reasonDetail'], message: 'Précisez le motif' }
          ),
      },
      // Changement de PROPRIETAIRE d'une pharmacie : archive l'etat courant (titulaire,
      // CIP, SIRET, TVA, adresse) dans « Anciens proprietaires » avant d'appliquer le
      // nouveau SIRET — controle INSEE bloquant cote serveur. `holder` est optionnel :
      // sans lui, le titulaire ne change pas.
      OWNER_CHANGE: {
        method: 'POST',
        endpoint: 'companies/:companyId/owner_change',
        schema: z.object({
          siret: z.string().regex(/^\d{14}$/),
          tva: z.string().nullable().optional(),
          reason: z.string().trim().min(1),
          holder: z
            .object({
              email: z.string().trim().email(),
              firstname: z.string().trim().min(1),
              lastname: z.string().trim().min(1),
              // Titulaire choisi au CNOP (pre-remplissage depuis une tache de cession) : 11 chiffres.
              rppsNumber: z
                .string()
                .regex(/^\d{11}$/)
                .nullable()
                .optional(),
            })
            .nullable(),
        }),
      },
      // Fiche client Eurodep (onglet « Donnees Eurodep » du controle). `companyId` resout la
      // path variable ; le schema le retire du corps. Reponse : l'etat relu (PharmacyEurodepState).
      // Masquer (ou reafficher) l'alerte de statut pour toute l'equipe.
      EURODEP_ALERT: {
        method: 'POST',
        endpoint: 'pharmacies/:companyId/eurodep/alert',
        schema: z.object({ dismissed: z.boolean() }),
      },
      // Reprendre une coordonnee Eurodep sur la fiche ; `applied` dit ce qui a ete ecrit.
      EURODEP_CONTACT: {
        method: 'POST',
        endpoint: 'pharmacies/:companyId/eurodep/contact',
        schema: z.object({ field: z.enum(['contactEmail', 'ownerEmail', 'phone']) }),
      },
      // Propositions d'e-mail de titulaire importees d'un fichier CIP + e-mails (30/10/2026) :
      // upload multipart direct (`file`, `label`, `apply`), simulation par defaut.
      OWNER_EMAIL_PROPOSAL_IMPORT: { method: 'POST', endpoint: 'owner_email_proposals/import' },
      // Ecarter une adresse proposee par un import : elle sort de la liste et n'est plus reproposee.
      OWNER_EMAIL_PROPOSAL_DISMISS: {
        method: 'POST',
        endpoint: 'companies/:companyId/owner_email_proposals/dismiss',
        schema: z.object({ email: z.string().email() }),
      },
      ENABLE_EMAIL_SIGNATURE: {
        method: 'POST',
        endpoint: 'companies/:companyId/enable-email-signature',
      },
      VALIDATE_SIRET_PROCEDURE: {
        method: 'POST',
        endpoint: 'companies/:companyId/validate-siret-procedure',
      },
      CHANGE_VALIDATION_STATUS: {
        method: 'POST',
        endpoint: 'companies/:companyId/validation_status',
      },
      // PATCH /companies/{id}/owner (merge-patch). `companyId` resout la path variable,
      // le corps n'envoie que `ownerUser` (IRI du user a designer titulaire/owner).
      // 422 si le user vise n'est pas membre de la company.
      CHANGE_OWNER: {
        method: 'PATCH',
        endpoint: 'companies/:companyId/owner',
        schema: z.object({
          ownerUser: z.string().regex(/^\/api\/v2\/users\/\d+$/),
        }),
      },
      // Geste UNIQUE « Changer de titulaire ou de propriétaire » (France, plan « une fiche par
      // SIREN », lot 4). Le SIREN décide : même SIREN = changement de titulaire (compte
      // transmis), SIREN différent = cession (fiche successeur, fiche actuelle close),
      // `personChanges: false` sans nouveau SIREN = correction. OWNERSHIP_PREVIEW dit ce que
      // ferait le geste sans rien écrire (appelé en fetchRest : une mutation invaliderait la
      // fiche à chaque frappe). La réponse d'OWNERSHIP dit ce qui s'est RÉELLEMENT produit.
      OWNERSHIP: {
        method: 'POST',
        endpoint: 'companies/:companyId/ownership',
        schema: ownershipSchema,
      },
      OWNERSHIP_PREVIEW: {
        method: 'POST',
        endpoint: 'companies/:companyId/ownership/preview',
        schema: ownershipSchema,
      },
      // Transfert d'un programme entre la fiche close et sa successeur (D-1), dans les deux sens
      // et depuis l'une ou l'autre : budget non facturé, tâches Insight non terminées. Vers la
      // successeur, la participation est remise à zéro (elle signe à son tour) ; rendue à la
      // fiche close, elle est gardée. Ce qui est facturé ne bouge pas.
      PROGRAM_TRANSFER: {
        method: 'POST',
        endpoint: 'companies/:companyId/program_transfer',
        schema: z.object({
          module: z.enum(['sound', 'storebag', 'insight']),
          programmeId: z.number().int().positive(),
          reason: z.string().trim().min(1).max(255),
          direction: z.enum(['toSuccessor', 'toPredecessor']).optional(),
          // Super admin : les tâches terminées non facturées partent aussi (18/10/2026).
          force: z.boolean().optional(),
        }),
      },
      // « Laisser où il est » (18/10/2026) : même route, `direction: keep`. Rien ne bouge, le
      // programme sort des « Programmes à transférer » ; motif facultatif.
      PROGRAM_KEEP: {
        method: 'POST',
        endpoint: 'companies/:companyId/program_transfer',
        schema: z.object({
          module: z.enum(['sound', 'storebag', 'insight']),
          programmeId: z.number().int().positive(),
          side: z.enum(['predecessor', 'successor']),
          reason: z.string().trim().max(255).optional(),
          direction: z.literal('keep'),
        }),
      },
      // Date de cession corrigée après coup, depuis la fiche close ou sa successeur : clôture,
      // période du cédant et accès suivent ; factures et programmes ne bougent pas.
      CESSION_DATE: {
        method: 'POST',
        endpoint: 'companies/:companyId/cession_date',
        schema: z.object({
          date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          reason: z.string().trim().min(1).max(200),
        }),
      },
      // Adresses de la fiche (admin, D-11) : une secondaire devient principale, une secondaire est
      // retirée, ou l'adresse du GERS devient principale. L'ancienne principale est gardée.
      ADDRESS_BOOK_ACTION: {
        method: 'POST',
        endpoint: 'companies/:companyId/address_book',
        schema: z.object({
          action: z.enum(['promote', 'remove', 'adopt_gers']),
          addressId: z.number().int().positive().optional(),
        }),
      },
      // « Corriger le SIRET » (admin) : le SIRET enregistré était mal saisi, même société.
      // L'historique garde le SIRET erroné, marqué comme tel, avec le motif.
      SIRET_CORRECTION: {
        method: 'POST',
        endpoint: 'companies/:companyId/siret_correction',
        schema: z.object({
          siret: z.string().regex(/^\d{14}$/),
          reason: z.string().trim().min(1).max(255),
        }),
      },
      // « Signaler un changement de société » (pharmacien, COMPANY_EDIT) : le SIRET d'une autre
      // société devient une tâche pour la Qualité des données ; la fiche n'est pas modifiée.
      SIREN_CHANGE_REQUEST: {
        method: 'POST',
        endpoint: 'companies/:companyId/siren_change_request',
        schema: z.object({
          siret: z.string().regex(/^\d{14}$/),
        }),
      },
      // Demande du pharmacien à la Comptabilité (COMPANY_EDIT, front#818) : option TVA sur les
      // débits (une seule ouverte, réponse `alreadyOpen`) ou demande libre, message obligatoire.
      ACCOUNTING_REQUEST: {
        method: 'POST',
        endpoint: 'companies/:companyId/accounting_request',
        schema: z
          .object({
            subject: z.enum(['vat_on_debits', 'other']),
            message: z.string().trim().max(2000).optional(),
          })
          .refine(data => data.subject !== 'other' || !!data.message, { path: ['message'] }),
      },
      // « Lier comme prédécesseur » (super admin) : deux fiches de SIREN différents ne se
      // fusionnent jamais (D-6) ; on chaîne la plus ancienne, close, à la plus récente.
      // `kind` : `period` (défaut, période précédente de CETTE officine — accrochée en cascade
      // au-dessus de la plus ancienne fiche de la chaîne si besoin) ou `regroupement` (une AUTRE
      // officine regroupée dans celle-ci, seule la fiche est close). `closedAt` facultatif
      // (YYYY-MM-DD) : sans lui, l'API choisit (aujourd'hui, ou début de la fiche d'accroche).
      // La fiche `companyId` peut être close (16/10/2026).
      PREDECESSOR_LINK: {
        method: 'POST',
        endpoint: 'companies/:companyId/predecessor',
        schema: z.object({
          predecessorId: z.number().int().positive(),
          reason: z.string().trim().min(1).max(255),
          kind: z.enum(['period', 'regroupement']).optional(),
          closedAt: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        }),
      },
      // « Annuler le rattachement » (super admin, 03/11/2026) : la fiche close par un REGROUPEMENT
      // fait à tort rouvre avec son CIP, son titulaire sort de la lecture seule, les programmes
      // partis chez la successeur pendant la clôture reviennent. Refus : 422 { refusal }.
      SUCCESSION_UNLINK: {
        method: 'POST',
        endpoint: 'companies/:companyId/succession_unlink',
        schema: z.object({
          reason: z.string().trim().min(1).max(200),
        }),
      },
      // Echange de sieges entre associes d'une pharmacie (ROLE_ADMIN) : le co-titulaire
      // `userId` devient titulaire, le titulaire en place passe co-titulaire — rien n'est
      // archive, ce n'est pas une cession. `companyId` resout la path variable. La reponse
      // dit ce qui s'est REELLEMENT produit (formerHolderDemoted, delegationTransferred).
      PROMOTE_CO_HOLDER: {
        method: 'POST',
        endpoint: 'companies/:companyId/co-holder-promotion',
        schema: z.object({
          userId: z.number().int().positive(),
        }),
      },
    },
    // Paramétrage partenaire (couple pharmacy <-> industry). Écriture ROLE_ADMIN.
    // Le couple porte DEUX axes indépendants, tous deux nullables : `subGroup` (segmentation)
    // et `cluster`. CREATE : POST avec IRIs pharmacy/industry (les axes sont optionnels — le
    // setting peut être créé « nu »). Contrainte unique par couple (re-POST même couple ->
    // erreur). UPDATE : PATCH par `settingId`, un axe à la fois (`null` = retirer).
    // 422 si l'axe appartient à une autre industry.
    COMPANY_PARTNER_SETTING: {
      CREATE: {
        method: 'POST',
        endpoint: 'company_partner_settings',
        schema: z.object({
          pharmacy: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
          industry: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
          subGroup: z
            .string()
            .regex(/^\/api\/v2\/company_sub_groups\/\d+$/)
            .nullable()
            .optional(),
          cluster: z
            .string()
            .regex(/^\/api\/v2\/company_clusters\/\d+$/)
            .nullable()
            .optional(),
          // Nom de l'officine chez le labo (chaine libre, normalisee serveur ; `null` = retrait).
          partnerPharmacyName: z.string().max(255).nullable().optional(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'company_partner_settings/:settingId',
        schema: z.object({
          subGroup: z
            .string()
            .regex(/^\/api\/v2\/company_sub_groups\/\d+$/)
            .nullable()
            .optional(),
          cluster: z
            .string()
            .regex(/^\/api\/v2\/company_clusters\/\d+$/)
            .nullable()
            .optional(),
          // Nom de l'officine chez le labo (chaine libre, normalisee serveur ; `null` = retrait).
          partnerPharmacyName: z.string().max(255).nullable().optional(),
        }),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'company_partner_settings/:settingId',
      },
    },
    // Historique des exports du header (« Téléchargements »). HIDE retire une ligne de MA
    // liste (204) : masquage serveur, la ligne reste la trace de l'extraction. Pas de corps ;
    // `exportId` part dans le chemin.
    EXPORT_DOWNLOADS: {
      HIDE: {
        method: 'DELETE',
        endpoint: 'me/export_downloads/:exportId',
        schema: z.object({}),
      },
    },
    // Import des affectations segmentation + cluster depuis un fichier deja televerse
    // (keyFile S3). BO admin : `industry` (id) cible le partner choisi. Espace industry :
    // industry forcee serveur (ne PAS passer industry). Reponse : message (string).
    // L'ecriture unitaire des deux axes passe par COMPANY_PARTNER_SETTING.UPDATE.
    COMPANY_PARTNER_SETTING_SUB_GROUP: {
      PROCESS_IMPORT: {
        method: 'POST',
        endpoint: 'company_partner_settings/import',
        schema: z.object({
          keyFile: z.string(),
          industry: z.union([z.string(), z.number()]).optional(),
        }),
      },
    },
    // Campagnes multi-programme partenaire — moteur de proposition de repartition (L2).
    // PROPOSAL : fichier CIP (keyFile S3) + budget + produits -> proposition JSON {rows, summary}.
    // PROCESS_IMPORT : reimport de la proposition XLSX ajustee -> creation PartnerCampaign +
    //   1 a 4 programmes Sound (un par entree de `soundPrograms`, chacun avec son type et ses
    //   spots) + programme StoreBag, en DRAFT. Reponse TOUJOURS asynchrone, quelle que soit la
    //   taille du fichier : {async: true, rowsCount} (202) apres un parse synchrone qui valide
    //   la structure du fichier (422 sinon). Le rapport (compteurs, une ligne par programme
    //   radio si N > 1, erreurs de lignes) est envoye par e-mail au demandeur — aucun id de
    //   programme cree n'est renvoye au front.
    // BO admin : `industry` (id) requis ; espace industry : NE PAS passer industry (force serveur).
    // Les exports XLSX (allocation_proposal_export, allocation_template_export) sont des
    // telechargements binaires : fetchBlob direct dans le composant, pas de mutation declaree
    // ici. Ils recoivent aussi `soundPrograms` (labels -> en-tetes de colonnes budget).
    PARTNER_CAMPAIGN: {
      // Archive ZIP des preuves photo de la campagne. Repond 202 : la generation est
      // ASYNCHRONE et l'archive part par e-mail au demandeur (aucun telechargement navigateur
      // pour un volume de photos, cf. .claude/rules/exports.md).
      PROOFS_EXPORT: {
        method: 'POST',
        endpoint: 'partner_campaigns/:campaignId/proofs_export',
        schema: z.object({ campaignId: z.union([z.string(), z.number()]) }),
        asyncExport: true,
      },
      // Retire une entree de l'historique des calculs. Le CLASSEUR S3 survit (garde une trace
      // de ce qui a ete calcule) ; un calcul deja importe est refuse par le serveur (409).
      ALLOCATION_RUN_DELETE: {
        method: 'DELETE',
        endpoint: 'partner_campaigns/allocation_runs/:runId',
        schema: z.object({
          runId: z.union([z.string(), z.number()]),
          industry: z.union([z.string(), z.number()]).optional(),
        }),
      },
      PROPOSAL: {
        method: 'POST',
        endpoint: 'partner_campaigns/allocation_proposal',
        // Source des CIP : fichier S3 (keyFile obligatoire). Le budget par pharmacie vient de la
        // 2e colonne du fichier CIP (plus de champ budget global ni de ciblage par filtres).
        // En `dual_axis`, le fichier porte DEUX colonnes de montant (cip ; montant gx ;
        // montant bs) et le ciblage par filtres est refuse (422).
        // Reponse : {rows, summary, products, anomalies, pharmacyContext?, mode, runId}.
        schema: z.object({
          keyFile: z.string().min(1),
          productIds: z.array(z.number()),
          // Labels/axes des programmes radio : le serveur les normalise depuis le lot 5
          // (ils alimentent `params.soundProgramLabels` de la trace) — un axe inconnu ou
          // plus de 2 programmes en dual font desormais 422 SUR LA PROPOSITION.
          soundPrograms: z
            .array(campaignSoundProgramInputSchema)
            .min(1)
            .max(MAX_SOUND_PROGRAMS)
            .optional(),
          // `false` = calcul NON trace (recalculs d'ecran a la volee). Defaut serveur : true.
          persist: z.boolean().optional(),
          industry: z.union([z.string(), z.number()]).optional(),
          // Regles de calcul modifiees POUR CE CALCUL (lot 1). ABSENT = comportement
          // strictement inchange : le serveur applique la fiche du labo puis ses defauts.
          // L'EXPORT XLSX doit recevoir EXACTEMENT le meme objet (cf. `handleDownloadXlsx`).
          settings: campaignAllocationSettingsSchema.optional(),
        }),
      },
      // Prevalidation du fichier d'import (meme parseur que l'import reel, sans ecriture).
      // `soundPrograms` (1..4, facultatif = compat) : seul le NOMBRE d'entrees compte — il
      // fixe le contrat de colonnes budget attendu (N = 1 : « budget sound (montant EUR) » ;
      // N > 1 : une colonne « budget sound {i} - {label} (montant EUR) » par programme).
      // Reponse: { countSound, countPlv, totalBudgetSound, totalPlvQuantity, unknownCips[],
      //   soundPrograms: [{ index, count, budget }] (une entree par programme 1..N) }.
      // Erreur de structure = 422 avec le message du parseur.
      VALIDATE_IMPORT: {
        method: 'POST',
        endpoint: 'partner_campaigns/allocation_validate',
        // En `dual_axis`, la reponse porte en plus `axes: {gx:{count,budget}, bs:{...}}`,
        // derive de `soundPrograms` par index (gx = 1, bs = 2), les deux axes toujours
        // presents (a `{count:0, budget:0}` si l'axe n'a aucune ligne).
        schema: z.object({
          keyFile: z.string().min(1),
          soundPrograms: z
            .array(z.object({ label: z.string(), axis: z.enum(['gx', 'bs']).optional() }))
            .min(1)
            .max(MAX_SOUND_PROGRAMS)
            .optional(),
          // Apercu d'un ajout a des programmes existants (19/10/2026) : la reponse porte alors
          // `targets[]` (pharmacies a ajouter / deja presentes, par programme vise).
          partnerCampaignId: z.number().positive().optional(),
          targetPrograms: campaignTargetProgramsSchema.optional(),
          industry: z.union([z.string(), z.number()]).optional(),
        }),
      },
      PROCESS_IMPORT: {
        method: 'POST',
        endpoint: 'partner_campaigns/allocation_import',
        schema: z.object({
          keyFile: z.string().min(1),
          campaignName: z.string().min(1),
          // Nom interne + nom court (facultatifs), repris par la campagne et les programmes.
          nameInternal: z.string().min(1).max(255).optional(),
          nameShort: z.string().min(1).max(255).optional(),
          // Radio : 1 a 4 programmes Sound, chacun avec son libelle (en-tete de colonne
          // budget), son type de programme (preset serveur), ses spots du labo et, en
          // `dual_axis`, son axe (`gx` / `bs` — au plus 2 programmes, 422 au-dela).
          soundPrograms: z.array(campaignSoundProgramInputSchema).min(1).max(MAX_SOUND_PROGRAMS),
          dateStart: z.string().min(1),
          dateEnd: z.string().min(1),
          budgetTotal: z.number().positive().optional(),
          // DATE LIMITE DE SIGNATURE du contrat de campagne (jour inclus, `AAAA-MM-JJ`). Lue
          // par le serveur pour un ADMIN seulement (ignoree pour un compte laboratoire, et pour
          // une vague rattachee a une campagne existante, qui garde sa date). Cle ABSENTE = le
          // serveur pose la date proposee par le reglage du laboratoire ; `null` = campagne
          // creee SANS date limite. Ne pas confondre avec l'ancienne date limite de REFUS
          // (`participationRefusalDeadline`, retiree le 10/09/2026 : sens oppose).
          signatureDeadline: campaignDaySchema.nullable().optional(),
          // Rattachement des programmes de la vague a une campagne EXISTANTE du labo
          // (multi-sous-programmes) au lieu d'en creer une nouvelle.
          partnerCampaignId: z.number().positive().optional(),
          // Volets verses dans des programmes EXISTANTS de cette campagne (exige
          // `partnerCampaignId`) : pharmacies deja presentes ecartees, programme inchange.
          targetPrograms: campaignTargetProgramsSchema.optional(),
          // Trace du CALCUL de repartition a l'origine de cet import (lot 5) : la campagne
          // creee y est rattachee. FAIL-CLOSED cote serveur — un run inconnu ou d'un autre
          // laboratoire fait 422, il n'est jamais ignore en silence.
          runId: z.number().positive().optional(),
          industry: z.union([z.string(), z.number()]).optional(),
        }),
      },
      // RENOMMAGE et statut d'une campagne (ROLE_ADMIN, merge-patch : seules les cles envoyees
      // sont ecrites). `name` ne peut pas etre vide (422 cote API) ; `nameShort` est le NOM
      // AFFICHE AU PHARMACIEN (contrats, « Mes partenaires ») et `nameInternal` le nom interne
      // — `null` les VIDE, le nom affiche retombe alors sur `name`. E-mails : `{{program_name}}`
      // d'un e-mail de campagne rend le nom affiche, `{{campaign_name}}` rend toujours `name`.
      UPDATE: {
        method: 'PATCH',
        endpoint: 'partner_campaigns/:campaignId',
        schema: z.object({
          name: z.string().trim().min(1).max(255).optional(),
          nameShort: z.string().trim().max(255).nullable().optional(),
          nameInternal: z.string().trim().max(255).nullable().optional(),
          status: z.enum(['draft', 'active', 'archived']).optional(),
          // Facture radio unique GX + BS par officine (21/10/2026) : l'eteindre rend la facture
          // par budget ; une facture deja emise ne change pas.
          singleRadioInvoice: z.boolean().optional(),
          // La date limite de signature n'est PAS ici : elle est hors du groupe
          // `partnerCampaign:update` cote API et ne se modifie que par SIGNATURE_DEADLINE
          // ci-dessous, qui annonce son impact avant d'ecrire.
        }),
      },
      // MODIFICATION DE LA DATE LIMITE DE SIGNATURE d'une campagne (ROLE_ADMIN). Meme appel
      // pour l'APERCU (`dryRun: true`, rien n'est ecrit) et pour l'ECRITURE (`dryRun: false`) ;
      // la reponse a la meme forme dans les deux cas (CampaignSignatureDeadlineResult,
      // types/group/partnerCampaign.ts). `signatureDeadline` est REQUIS dans le corps : `null`
      // est une valeur (« retirer la date limite »), une cle absente fait 422. Une date passee
      // est acceptee — c'est l'apercu qui dit ce qu'elle fermera.
      // L'apercu doit etre appele avec `invalidateQueriesOnSuccess: false` : il n'ecrit rien,
      // inutile de relancer toutes les requetes de l'ecran de suivi.
      SIGNATURE_DEADLINE: {
        method: 'POST',
        endpoint: 'partner_campaigns/:campaignId/signature_deadline',
        schema: z.object({
          signatureDeadline: campaignDaySchema.nullable(),
          dryRun: z.boolean(),
        }),
      },
      // REOUVERTURE D'UNE OFFICINE fermee par echeance sur une campagne (ROLE_ADMIN) : leve la
      // fermeture de TOUS ses budgets radio et PLV de la campagne (jamais un vrai refus) et pose
      // `until` (jour INCLUS, aujourd'hui ou plus tard) en date derogatoire de l'officine. Meme
      // appel pour l'APERCU (`dryRun: true`, rien n'est ecrit — stock re-engage par produit
      // compris) et pour l'ECRITURE ; la reponse a la meme forme dans les deux cas
      // (CampaignSignatureReopeningResult, types/group/partnerCampaign.ts). 422 explique si la
      // date est passee, si l'officine n'est pas dans la campagne ou si elle n'est pas fermee.
      // `dryRun` est REQUIS ici : cote serveur il vaut `true` par defaut, un oubli ferait
      // prendre un apercu pour une ecriture. Apercu : `invalidateQueriesOnSuccess: false`.
      SIGNATURE_REOPENING: {
        method: 'POST',
        endpoint: 'partner_campaigns/:campaignId/signature_reopening',
        schema: z.object({
          pharmacyId: z.number().int().positive(),
          until: campaignDaySchema,
          dryRun: z.boolean(),
        }),
      },
      // Refus de TOUTE la campagne par le pharmacien (D-03) : tous ses budgets Sound + PLV
      // rattaches a la campagne dont la fenetre de refus court encore. Bouton « Refuser la
      // campagne » de l'ecran contrats, a cote de « Tout signer ».
      PARTICIPATION_REJECT: {
        method: 'POST',
        endpoint: 'partner_campaigns/:campaignId/participation_reject',
        schema: z.object({ reason: z.string().optional() }),
      },
    },
    // Onboarding partenaire (couple pharmacie <-> labo). Écriture pharmacien.
    // CREATE : POST avec IRIs pharmacy/industry (+ préférences optionnelles) quand aucun
    // onboarding n'existe encore (onboardingId null). UPDATE : PATCH par `onboardingId`
    // (préférences seules). Supports whitelistés : plv|radio|sac|affiche (422 sinon).
    PARTNER_ONBOARDING: {
      CREATE: {
        method: 'POST',
        endpoint: 'partner_onboardings',
        schema: z.object({
          pharmacy: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
          industry: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
        }),
      },
    },
    // Preferences PLV reliees a un PRODUIT reel (plus aucune saisie libre) : chaque appel pose
    // l'etat cible du produit. La pharmacie est resolue serveur.
    // `en_place` est reserve aux PLV FIXES et n'ecrit PAS une preference : il inscrit
    // l'equipement pose, seule source de cet etat (revenir a non/souhaite le retire).
    PARTNER_PLV_PREFERENCE: {
      SET: {
        method: 'POST',
        endpoint: 'partner_plv_preferences',
        schema: z.object({
          industry: z.number(),
          // UN PRODUIT, ou UNE CATEGORIE de souhait (07/10/2026) : le souhait d'une categorie
          // est recopie serveur sur chacun de ses produits. Avec `category` et
          // `targetState: en_place`, `product` designe LE materiel pose de la categorie.
          product: z.number().optional(),
          category: z.number().optional(),
          // `non_defini` EFFACE la preference (retour a « jamais arbitre »), il ne la met pas
          // a « non » : le moteur de repartition ne bloque que sur un refus EXPLICITE, un
          // produit jamais arbitre reste servable.
          // `plv_prochaine_campagne` (ligne radio seulement) : souhait PONCTUEL de PLV a la
          // place de la radio, eteint par la campagne qui le sert ; `souhaite` l'annule.
          targetState: z.enum([
            'non',
            'souhaite',
            'en_place',
            'non_defini',
            'plv_prochaine_campagne',
          ]),
          // Pharmacie cible : ADMIN depuis la fiche back-office, et compte LABORATOIRE
          // depuis la fiche pharmacie de son espace (perimetre impose serveur). Ignore
          // pour un pharmacien, qui n'arbitre que sa propre officine.
          pharmacy: z.number().optional(),
        }),
      },
    },
    // Demande d'envoi d'un equipement PLV fixe (pharmacie premium). Cree la commande CS-Cart.
    PARTNER_PLV_EQUIPMENT_ORDER: {
      CREATE: {
        method: 'POST',
        endpoint: 'partner_plv_equipment_orders',
        schema: z.object({
          industry: z.number(),
          product: z.number(),
        }),
      },
    },
    // Demandes portant sur une PLV FIXE : etre equipe de ce qu'on n'a pas, ou signaler ce qui
    // arrive a ce qu'on a. RIEN n'est commande a la creation — la demande part en validation.
    // La pharmacie est resolue SERVEUR.
    PARTNER_PLV_REPLACEMENT: {
      CREATE: {
        method: 'POST',
        endpoint: 'partner_plv_replacement_requests',
        schema: z.object({
          industry: z.number(),
          product: z.number(),
          // Liste FERMEE, revalidee serveur : le motif doit s'accorder a l'etat reel du
          // materiel (`equipment` seulement si absent, les quatre autres seulement si pose).
          reasonInput: z.enum(['equipment', 'damaged', 'never_received', 'lost', 'renewal']),
          // Pas de `comment` : aucun texte libre d'un pharmacien (01/10/2026).
          // CLE S3 obtenue sur GET_UPLOAD_URL, jamais une URL : une URL signee expire.
          photoKey: z.string().optional(),
        }),
      },
      // Retrait par l'officine tant que la demande n'est pas traitee. L'appartenance est
      // verifiee cote serveur (une operation POST d'item n'est pas scopee par API Platform).
      CANCEL: {
        method: 'POST',
        endpoint: 'partner_plv_replacement_requests/:id/cancel',
        schema: z.object({ id: z.number() }),
      },
      // Decision unitaire OU en masse : `ids` porte la selection. Le retour dit ce qui est
      // REELLEMENT passe (`decided`) et ce qui a ete ecarte, avec sa raison (`refused`).
      DECISION: {
        method: 'POST',
        endpoint: 'plv_replacement_requests/decision',
        schema: z.object({
          ids: z.array(z.number()).min(1),
          accepted: z.boolean(),
          // OBLIGATOIRE au refus cote serveur : repris tel quel dans l'e-mail au pharmacien.
          decisionReasonInput: z.string().optional(),
        }),
      },
      // REJEU de l'execution d'une demande DEJA validee dont la commande n'est pas partie.
      // Il existe parce qu'une validation dont la commande a echoue etait bloquee A VIE :
      // `decide()` refuse toute demande qui n'est plus ouverte, donc plus rien ne relancait
      // l'envoi. ADMIN SEUL : c'est un geste d'exploitation, pas un arbitrage de laboratoire.
      //
      // AUCUN CORPS : tout est dans l'identifiant. `id` n'est la que pour construire l'URL —
      // le schema le valide, le corps envoye reste vide cote serveur (`deserialize: false`).
      REPLAY: {
        method: 'POST',
        endpoint: 'plv_replacement_requests/:id/replay',
        schema: z.object({ id: z.number() }),
      },
      // URL presignee pour la photo : le fichier va DIRECTEMENT du navigateur vers S3.
      GET_UPLOAD_URL: {
        method: 'POST',
        endpoint: 'partner-plv-replacement-photos/upload-url',
        schema: z.object({ extension: z.string() }),
      },
    },
    // DEMANDE DE MATERIEL deposee par un ADMIN au nom d'une officine (L9) : le geste « envoi ou
    // retour de materiel » du pave CRM ecrit une DEMANDE, nee acceptee et executee — plus un
    // mouvement de parc directement. N objets, nommes par fiche produit OU librement (jamais les
    // deux). `industry` requis pour la PLV, ignore pour le Sound (resolu serveur). Le retour dit
    // qui a servi (`fulfilmentMode`), la commande ou le motif d'echec, et le pave cree.
    MATERIAL_REQUEST: {
      CREATE: {
        method: 'POST',
        endpoint: 'admin/material_requests',
        schema: z.object({
          pharmacy: z.number().int().positive(),
          entity: z.enum(['PHARMEDISOUND', 'PHARMEDISTOREBAG']),
          industry: z.number().int().positive().optional(),
          // Envoi ou échange : « À valider » ; retour : exécuté tout de suite (01/10/2026).
          direction: z.enum(['send', 'return', 'exchange']),
          // Envoi : equipment, renewal, lost, never_received ; retour et échange : les trois motifs
          // de la production (breakage, malfunction, other). `damaged` reste accepté (anciennes).
          reasonInput: z.enum([
            'equipment',
            'damaged',
            'never_received',
            'lost',
            'renewal',
            'breakage',
            'malfunction',
            'other',
          ]),
          comment: z.string().optional(),
          notifyLogistics: z.boolean(),
          // Retour seulement : crée l'étiquette de retour DPD avec le retour (plafond Pickup).
          dpdWeightKg: z.number().positive().max(20).optional(),
          lines: z
            .array(
              // Plus de matériel « sans fiche » (W-04) : chaque ligne nomme un produit.
              z.object({
                product: z.number().int().positive(),
                quantity: z.number().int().min(1).max(999),
                supplyMode: z.enum(['purchase', 'loan']),
                // Ce qui revient : la ligne de parc Sound cochée, contre laquelle le retour se solde.
                sourceDetail: z.number().int().positive().optional(),
              })
            )
            .min(1),
          // Échange « différent » : le matériel RÉCUPÉRÉ dans le parc, distinct de ce qui part.
          // Absent, l'échange est « identique » (ce qui revient est ce qui part).
          returnLines: z
            .array(
              z.object({
                product: z.number().int().positive(),
                quantity: z.number().int().min(1).max(999),
                supplyMode: z.enum(['purchase', 'loan']),
                // Ce qui revient : la ligne de parc Sound cochée, contre laquelle le retour se solde.
                sourceDetail: z.number().int().positive().optional(),
              })
            )
            .optional(),
        }),
      },
    },
    // Signature électronique d'un contrat partenaire depuis la checklist d'onboarding.
    // POST /documents/electroniquesignature (même endpoint/UX que les autres signatures Universign).
    // Corps FIXE côté API : { type, partnerId, programmeId? } — type=contrat_cadre_partner (contrat
    // cadre du partner) ou contrat_application_partner (une application/programme donné). programmeId
    // n'est requis que pour une application. redirectUrl (optionnel) ramène l'utilisateur au retour.
    // Réponse : { urlRedirectSign }. La mutualisation (une signature pour un groupe) est gérée serveur.
    PARTNER_CONTRACT: {
      SIGN: {
        method: 'POST',
        endpoint: 'documents/electroniquesignature',
        schema: z.object({
          type: z.enum([
            'contrat_cadre_partner',
            'contrat_application_partner',
            'contrat_ponctuel_partner',
          ]),
          partnerId: z.number(),
          programmeId: z.number().optional(),
          // Désambiguïse l'id programme entre Sound et StoreBag (tables distinctes).
          module: z.enum(['sound', 'store_bag']).optional(),
          redirectUrl: z.string().min(1).optional(),
        }),
      },
    },
    // Segmentation laboratoire (liste PLATE). Écriture ROLE_ADMIN (BO). La company est passée
    // en IRI au CREATE. `tier` (classique | premium) pilote le moteur de contrats.
    // DELETE échoue si l'entrée est utilisée par un setting (FK RESTRICT).
    COMPANY_SUB_GROUP: {
      CREATE: {
        method: 'POST',
        endpoint: 'company_sub_groups',
        schema: z.object({
          company: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
          code: z.string().min(1),
          name: z.string().min(1),
          description: z.string().optional(),
          tier: z.enum(['classique', 'premium']),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'company_sub_groups/:subGroupId',
        schema: z.object({
          code: z.string().min(1),
          name: z.string().min(1),
          description: z.string().optional(),
          tier: z.enum(['classique', 'premium']),
        }),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'company_sub_groups/:subGroupId',
      },
    },
    // Clusters d'une company industry — second axe, sans palier. Mêmes règles d'écriture que
    // COMPANY_SUB_GROUP. DELETE échoue si le cluster est porté par un setting (FK RESTRICT).
    COMPANY_CLUSTER: {
      CREATE: {
        method: 'POST',
        endpoint: 'company_clusters',
        schema: z.object({
          company: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
          code: z.string().min(1),
          name: z.string().min(1),
          description: z.string().optional(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'company_clusters/:clusterId',
        schema: z.object({
          code: z.string().min(1),
          name: z.string().min(1),
          description: z.string().optional(),
        }),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'company_clusters/:clusterId',
      },
    },
    // Groupements DU LABORATOIRE — TROISIEME axe, sans palier ni tarification : c'est le
    // decoupage commercial propre au labo (jamais le groupement/chaine de pharmacies).
    // CATEGORIES DE SOUHAIT PLV d'un laboratoire (07/10/2026) : referentiel ADMIN de la fiche
    // labo. `name` = nom interne du labo (« Kit partenaire »), `customerLabel` = libelle montre
    // au pharmacien (« Jupe de comptoir »). Supprimer une categorie detache ses produits, qui
    // redeviennent un reglage admin invisible du pharmacien.
    PARTNER_PLV_CATEGORY: {
      CREATE: {
        method: 'POST',
        endpoint: 'partner_plv_categories',
        schema: z.object({
          industry: z.string().regex(/^\/api\/v2\/companies\/\d+$/),
          name: z.string().min(1).max(100),
          customerLabel: z.string().min(1).max(100),
          position: z.number().int().optional(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'partner_plv_categories/:categoryId',
        schema: z.object({
          name: z.string().min(1).max(100).optional(),
          customerLabel: z.string().min(1).max(100).optional(),
          position: z.number().int().optional(),
        }),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'partner_plv_categories/:categoryId',
      },
    },
    PHARMACY: {
      // PUT /company/{id}/segmentation (CrudPharmacySegmentationController). `id` resout la path
      // variable, le corps est lu brut (champs de segmentation : turnover, dispensarySize,
      // hoursOpen, certificate, urbanSection, missions, etc.). Schema `.passthrough()` car les
      // champs dependent du schema de formulaire (pays/role). Reponse: objet segmentation.
      CREATE_OR_UPDATE_SEGMENTATION: {
        method: 'PUT',
        endpoint: 'company/:id/segmentation',
        schema: z.object({ id: z.number() }).passthrough(),
      },
      // POST /pharmacies/update (UpdatePharmaciesController). Import CSV asynchrone :
      // { keyFile (cle S3), entity }. Reponse: declenche un mail de rapport.
      PROCESS_UPDATE: {
        method: 'POST',
        endpoint: 'pharmacies/update',
        schema: z.object({
          keyFile: z.string().nullable().optional(),
          entity: z.string().nullable().optional(),
        }),
      },
      // POST /pharmacies/italy_placeholders (PharmacyPlaceholderBatchProcessor) : creation en
      // masse de fiches « A configurer » italiennes (CIP F/Acréer<N>, statut Panel), admins qui
      // voient l'Italie. Asynchrone (202) : rapport XLSX envoye par e-mail au demandeur.
      // Reponse : { count, status: 'queued', message }.
      CREATE_ITALY_PLACEHOLDERS: {
        method: 'POST' as const,
        endpoint: 'pharmacies/italy_placeholders',
        schema: z.object({
          count: z.union([
            z.literal(10),
            z.literal(20),
            z.literal(30),
            z.literal(40),
            z.literal(50),
            z.literal(100),
          ]),
          groupementId: z.number().int().positive().nullable().optional(),
        }),
      },
      // POST /pharmacy_creation (assistant « Ajouter une pharmacie », France, 23/10/2026) : fiche
      // + compte titulaire. L'API rejoue tout (CIP libre, SIRET et SIREN, e-mail, RPPS) et répond
      // 201 { companyId, cip, cipNature, gersStatus, emailGeneric, invitationSent, rppsWritten,
      // rppsSkipped, siretActive } ou 422 qui dit quoi faire. Natures du CIP et motifs écrits ici
      // comme dans CipNature / CipUnknownReason (API) et types/group/pharmacyCreation.ts.
      CREATE_ASSISTED: {
        method: 'POST' as const,
        endpoint: 'pharmacy_creation',
        schema: z
          .object({
            cipNature: z.enum(['gers', 'not_in_gers', 'provisional', 'none']),
            cip: z
              .string()
              .regex(/^\d{7}$/)
              .nullable(),
            cipUnknownReason: z.enum(['opening', 'not_provided', 'overseas', 'other']).nullable(),
            cipNatureNote: z.string().max(255).nullable(),
            companyName: z.string().trim().min(1).max(255),
            address: z.string().trim().min(1),
            zipCode: z.string().regex(/^\d{5}$/),
            city: z.string().trim().min(1),
            siret: z.string().regex(/^(\d{14})?$/),
            allowSharedSiren: z.boolean(),
            lastname: z.string().trim().min(1).max(100),
            firstname: z.string().max(100),
            rpps: z
              .string()
              .regex(/^\d{11}$/)
              .nullable(),
            email: z.string(),
            phone: z.string(),
            sendInvitation: z.boolean(),
          })
          .refine(body => (body.cipNature === 'none') === (body.cip === null), {
            message: 'CIP requis, sauf sans CIP',
          })
          .refine(body => body.cipNature !== 'none' || body.cipUnknownReason !== null, {
            message: 'Motif requis sans CIP',
          }),
      },
      // POST /pharmacies/inactive_archive (PharmacyInactiveArchiveController, ROLE_ADMIN).
      // Archivage EN MASSE des fiches inactives selectionnees : motif OBLIGATOIRE, 200 fiches
      // au plus, inactivite reverifiee cote API. Chaque fiche passe par changeStatus(EXCLUDE) :
      // historique, Store desactive, « Controle a faire » decoche, aucun e-mail. Reponse :
      // { requested, archived, archivedIds, skipped[{id, cip, reason}], errors[{id, cip, message}] }.
      ARCHIVE_INACTIVE: {
        method: 'POST' as const,
        endpoint: 'pharmacies/inactive_archive',
        schema: z.object({
          ids: z.array(z.number().int().positive()).min(1).max(200),
          reason: z.string().trim().min(1).max(2000),
        }),
      },
    },
    BANK: {
      CREATE: { method: 'POST', endpoint: 'banques' },
      UPDATE: { method: 'PUT', endpoint: 'banques/:bankId' },
      DELETE: { method: 'DELETE', endpoint: 'banques/:bankId' },
      // POST multipart (champ `file`) : /rib/analyze -> OCR/extraction RIB.
      // Reponse { iban, bic, beneficiare, domiciliation, confidence }.
      // NB : envoi FormData (pas de body JSON) — voir composant RibScanUpload qui poste
      // directement en multipart (fetchRest serialise le body en JSON, incompatible ici).
      ANALYZE_RIB: { method: 'POST', endpoint: 'rib/analyze' },
    },
    INVOICE: {
      SIGN: {
        method: 'POST',
        endpoint: 'factures/:invoiceId/sign',
      },
      // Initie (ou reprend) le parcours de mandat de facturation électronique 17B —
      // crée le party Invopop + workflow, renvoie { status, link } (lien iframe). Idempotent.
      PA_MANDATE_START: {
        method: 'POST',
        endpoint: 'pa_mandate/me/start',
      },
      // ADMIN (fiche pharmacie › Partenaires) : relit le mandat 17B chez Invopop, après un
      // « Run again » dans la console. `id` = id de la pharmacie. Ne relance pas le workflow.
      ADMIN_PA_MANDATE_REFRESH: {
        method: 'POST',
        endpoint: 'admin/pa_mandates/:id/refresh',
        schema: z.object({ id: z.number().int().positive() }),
      },
      // OUTIL DE RECETTE (API dev/test uniquement, 404 ailleurs) : crée un mandat 17B pour
      // un SIREN arbitraire, sans fiche pharmacie — sert à peupler des participants dans la
      // sandbox Invopop (réseau Peppol de TEST).
      SANDBOX_PA_MANDATE_START: {
        method: 'POST',
        endpoint: 'dev/pa_mandates',
        schema: z.object({
          siren: z.string().regex(/^\d{9}$/),
          name: z.string().min(1),
          signerFirstname: z.string().optional(),
          signerLastname: z.string().optional(),
          email: z.string().email().optional(),
          // '' = ligne annuaire sur le SIREN nu, 'PMG' = suffixe du parcours pharmacien.
          inboxSuffix: z.string().regex(/^[A-Za-z0-9-]*$/),
        }),
      },
      EXPORTED_LINK: {
        method: 'POST',
        endpoint: 'factures/exported_link',
      },
      GENERATE_SOUND: {
        method: 'POST',
        endpoint: 'sound_invoices/facture_generate/:programmePharmacieBudgetId',
      },
      GENERATE_INSIGHT: {
        method: 'POST',
        endpoint: 'insight_invoices/facture_generate/:programmeId/:userId',
      },
      GENERATE_STOREBAG: {
        method: 'POST',
        endpoint: 'store_bag_invoices/facture_generate/:programmePharmacieBudgetId',
      },
      FORCE_INVOICE_SOUND: {
        method: 'PATCH',
        endpoint: 'sound_programme_pharmacie_budgets/:budgetId',
      },
      FORCE_INVOICE_INSIGHT: {
        method: 'PATCH',
        endpoint: 'repartition_programmes/:budgetId',
      },
      FORCE_INVOICE_STOREBAG: {
        method: 'PATCH',
        endpoint: 'store_bag_program_pharmacy_budgets/:budgetId',
      },
    },
    REASSIGN_TO_OWNER: {
      STORE_BAG_BUDGET: {
        method: 'PATCH',
        endpoint: 'store_bag_program_pharmacy_budgets/:id',
      },
      SOUND_BUDGET: {
        method: 'PATCH',
        endpoint: 'sound_programme_pharmacie_budgets/:id',
      },
      REPARTITION_PROGRAMME: {
        method: 'PATCH',
        endpoint: 'repartition_programmes/:id',
      },
    },
    PHARMACY_USER_HISTORY: {
      CHANGE_PRINCIPAL: {
        method: 'POST',
        endpoint: 'pharmacy_user_history/change_principal_history/:userId/pharmacy/:companyId',
      },
      MERGE_USER: {
        method: 'POST',
        endpoint: 'pharmacy_user_history/merge_user_to_principal/:userId/pharmacy/:companyId',
      },
      // « Marquer comme erreur de saisie » / « Annuler le marquage » sur une ligne de
      // « Anciens propriétaires » (admin). Motif obligatoire au marquage.
      SIRET_ERRONEOUS: {
        method: 'POST',
        endpoint: 'pharmacy_user_history/:historyId/siret_erroneous',
        schema: z.object({
          erroneous: z.boolean(),
          reason: z.string().trim().max(255).optional(),
        }),
      },
      // « Modifier les dates » d'un changement de SIRET, de CIP ou d'adresse (17/10/2026) ; une
      // cession passe par « Modifier la date de cession » (PharmacyHistoryDatesService).
      DATES: {
        method: 'POST',
        endpoint: 'pharmacy_user_history/:historyId/dates',
        schema: z.object({
          startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        }),
      },
    },
    USERS: {
      // Adresses e-mail du compte (admin) : ajouter une secondaire, en faire l'adresse de
      // connexion (l'ancienne passe en secondaire ; boutique seulement si demandé), archiver.
      EMAIL_ACTION: {
        method: 'POST',
        endpoint: 'users/:userId/emails',
        schema: z.object({
          action: z.enum(['add', 'promote', 'archive']),
          email: z.string().trim().email().max(180).optional(),
          emailId: z.number().int().positive().optional(),
          replaceStoreLogin: z.boolean().optional(),
          source: z.enum(['eurodep', 'gers', 'cnop', 'manual', 'import', 'history']).optional(),
        }),
      },
      // POST /user_management_preferences : upsert (par utilisateur courant + feature) d'une
      // preference de gestion (ex: mode d'affichage des filtres de tableau v1/v2, layout du
      // dashboard admin). Body { feature, category, version, payload? }. Reponse: la preference
      // persistee. `payload` = JSON libre optionnel (ex: { order: string[], hidden: string[] }
      // pour la perso de la grille du dashboard admin). Cote back, payload null n'ecrase pas la
      // valeur existante (upsert version-only conserve le layout precedent).
      MANAGEMENT_PREFERENCE_UPSERT: {
        method: 'POST',
        endpoint: 'user_management_preferences',
        schema: z.object({
          // ADMIN uniquement : cible un autre utilisateur (fiche backoffice) — l'API
          // exige ROLE_ADMIN quand present, sinon upsert sur l'utilisateur du token.
          targetUserId: z.number().int().optional(),
          feature: z.string(),
          category: z.string().nullable().optional(),
          version: z.number().int(),
          payload: z
            .object({
              order: z.array(z.string()).optional(),
              hidden: z.array(z.string()).optional(),
              // Feature 'start_page' : nom unique d'une route front, ou null (= page par defaut).
              route: z.string().max(64).nullable().optional(),
              // Feature 'favorite_pages' : noms de routes epinglees, dans l'ordre d'affichage.
              // ATTENTION : toute cle absente de ce schema est SILENCIEUSEMENT supprimee par
              // Zod avant l'envoi — une nouvelle feature a payload doit etre declaree ici.
              routes: z.array(z.string().max(64)).max(12).optional(),
              // Feature 'ringover_call_mode' (lib/ringover/preferences.ts) : voie d'appel de
              // l'agent (softphone integre ou rappel serveur) + appareil sonne en `callback`.
              mode: z.enum(['sdk', 'callback']).optional(),
              device: z.enum(['ALL', 'APP', 'WEB', 'SIP', 'MOB', 'EXT']).optional(),
            })
            .optional(),
        }),
      },
      DELETE: { method: 'DELETE', endpoint: 'users/:id' },
      // Provisionnement du compte client « Ma centrale » d'un membre de groupement/chaine
      // (POST /users/{id}/store-account). `id` resout la
      // path variable ; `linkExisting` (ADMIN seulement) autorise le rattachement d'un
      // compte CS-Cart existant sans commande. Reponse : { status, userIdStore, usergroup,
      // storefront, message } — le toast n'annonce QUE le statut reellement obtenu.
      PROVISION_STORE_ACCOUNT: {
        method: 'POST',
        endpoint: 'users/:id/store-account',
        schema: z.object({
          id: z.number(),
          linkExisting: z.boolean().optional(),
        }),
      },
      // POST /users/{id}/pharmacy-transfer (UserPharmacyTransferController) : un MEMBRE
      // D'EQUIPE change de pharmacie, son compte le suit (fonction, formations, points) ; ses
      // delegations sont retirees. Reponse : { userId, from, to, removedDelegations }.
      TRANSFER_PHARMACY: {
        method: 'POST',
        endpoint: 'users/:id/pharmacy-transfer',
        schema: z.object({
          id: z.number(),
          companyId: z.number().int().positive(),
        }),
      },
      // POST /users/{id}/store-account/disable (UserStoreAccountDisableController) : coupe
      // l'acces « Ma centrale » (compte CS-Cart desactive, menu masque, lien conserve).
      DISABLE_STORE_ACCOUNT: {
        method: 'POST',
        endpoint: 'users/:id/store-account/disable',
        schema: z.object({
          id: z.number(),
        }),
      },
      // Back: POST /api/v2/users/{id}/merge body { sourceId } (UserMergeController).
      // :userId = NOUVEAU compte (target, {id}). sourceId (ANCIEN compte) envoye dans le body.
      MERGE: {
        method: 'POST',
        endpoint: 'users/:userId/merge',
      },
      // Export complet asynchrone (GetCollection /users/users_export_all/{type}, controller
      // ExportUsersAllController -> enqueue un job et retourne un message JSON). C'est une "fausse
      // query" GraphQL declenchee a la demande -> traitee en mutation cote front (methode GET, le
      // type passe en variable de chemin :type, pas de body envoye sur un GET).
      EXPORT_FULL: {
        method: 'GET',
        endpoint: 'users/users_export_all/:type',
        schema: z.object({
          type: z.string(),
        }),
      },
      // PATCH /users/{id} (UserPatchProcessor). Corps merge-patch ouvert : les consommateurs
      // envoient des sous-ensembles tres varies du profil (email, role, companyId,
      // changeCompanyIdDefault, phoneNumber, language, rppsNumber, city, registrationComplete...).
      // `id` resout la path variable. Reponse: l'objet User (groupe user:post).
      UPDATE: {
        method: 'PATCH',
        endpoint: 'users/:id',
        schema: z
          .object({
            id: z.number(),
          })
          .passthrough(),
      },
      // POST /userTeam (UserTeamSelfRegistrationController). Inscription publique d'un membre
      // d'equipe pharmacie via lien d'invitation cooptation (visiteur non authentifie).
      // Protege par X-Api-Token, pas de JWT requis.
      SELF_REGISTER: {
        method: 'POST',
        endpoint: 'userTeam',
        schema: z
          .object({
            email: z.string().email(),
            companyId: z.string(),
          })
          .passthrough(),
      },
      // POST /users (UserPostProcessor, security USER_CREATE). Creation d'un membre d'equipe.
      // Le flag GraphQL `register` n'existe pas en REST : la logique d'inscription est deduite
      // cote back (roles/typeCompany). Schema ouvert (formulaires varies + sponsorship/rppsNumber...).
      ADD: {
        method: 'POST',
        endpoint: 'users',
        schema: z
          .object({
            email: z.string().nullable().optional(),
            typeCompany: z.string().optional(),
            // « Ma centrale » (groupement/chaine) : cree en
            // plus du compte group un compte CLIENT CS-Cart portant le groupe utilisateur
            // de la centrale. Toujours present dans le schema de la branche
            // groupement/chaine (defaut `false`) — l'API l'ignore quand le flag est OFF.
            createStoreAccount: z.boolean().optional(),
            // Facultatif. Contrôlé côté API (PhoneValidatorService) pour un appelant non-admin.
            phonePersonnal: z.string().optional(),
          })
          .passthrough(),
      },
      // POST /userPharmacy (UsersCreatePharmacyController). Inscription auto-portee d'un compte
      // pharmacie. Schema ouvert (companyId requis cote back + champs profil).
      USER_ADD: {
        method: 'POST',
        endpoint: 'userPharmacy',
        schema: z
          .object({
            companyId: z.number(),
          })
          .passthrough(),
      },
      // POST /users/{id}/updateRolesUser (ApiUserRolesController). Body { roles: string[] }.
      UPDATE_ROLES: {
        method: 'POST',
        endpoint: 'users/:id/updateRolesUser',
        schema: z
          .object({
            id: z.number(),
            roles: z.array(z.string()),
          })
          .passthrough(),
      },
      // POST /users/{id}/suspend (UsersTeamSuspendController) : toggle enabled. Pas de corps.
      SUSPEND: {
        method: 'POST',
        endpoint: 'users/:id/suspend',
        schema: z.object({ id: z.number() }),
      },
      // POST /users/{id}/reset-store-password (UserResetStorePasswordController). Le user est
      // resolu cote back par l'email du corps. `id` (placeholder de chemin) tolere mais non requis.
      // Reponse: { success: boolean, message: string }.
      RESET_STORE_PASSWORD: {
        method: 'POST',
        endpoint: 'users/:id/reset-store-password',
        schema: z.object({
          email: z.string().email(),
        }),
      },
      // POST /users/{id}/reset-otp (ROLE_ADMIN) : invalide les validations OTP actives du
      // user (re-OTP au prochain login). Reponse { success, deleted }.
      RESET_OTP: {
        method: 'POST',
        endpoint: 'users/:id/reset-otp',
        schema: z.object({
          id: z.number(),
        }),
      },
      // NEWSLETTER_UPDATE retire : le flag User.newsletter est deprecie cote API et la
      // route PATCH /users/{id}/newsletter n'existe plus. L'abonnement marketing se
      // pilote canal par canal via MAILJET_EMAILING.UPDATE_USER_PREFERENCES.
      // POST /users/{id}/newPassword (ApiChangePasswordController). Body { password, confirmPassword }.
      // Utilise quand un id d'utilisateur cible est connu (admin/backoffice).
      CHANGE_PASSWORD: {
        method: 'POST',
        endpoint: 'users/:id/newPassword',
        schema: z
          .object({
            id: z.number(),
            password: z.string(),
            confirmPassword: z.string(),
          })
          .passthrough(),
      },
      // POST /users/newPassword (meme controller, sans id) : change le mot de passe de
      // l'utilisateur courant. Variante self-service quand aucun id cible n'est fourni.
      CHANGE_PASSWORD_SELF: {
        method: 'POST',
        endpoint: 'users/newPassword',
        schema: z
          .object({
            password: z.string(),
            confirmPassword: z.string(),
          })
          .passthrough(),
      },
      // PATCH /users/{id}/avatar. Corps merge-patch { avatarImage: string }.
      AVATAR_UPDATE: {
        method: 'PATCH',
        endpoint: 'users/:id/avatar',
        schema: z.object({
          id: z.number(),
          avatarImage: z.string(),
        }),
      },
      // PATCH /users/{id}/toggle_delegate_manager (UserToggleDelegateManagerProcessor).
      // Body { entity: PHARMEDISCHOOL | PHARMEDISTORE | PHARMEDICARE | DELEGATE_PHARMACIEN_CO_TITU }.
      // GraphQL utilisait userId -> resout la path variable :id ici.
      ACTIVE_DELEGATE_MANAGER_COMPANY: {
        method: 'PATCH',
        endpoint: 'users/:id/toggle_delegate_manager',
        schema: z.object({
          id: z.number(),
          entity: z.string(),
        }),
      },
      // POST /users/activeEntityUserIndustry (ApiActiveEntityUserIndustryController).
      // Body { entity: INSIGHT | SCHOOL | STORE }. Reponse: { token, refresh_token }.
      ACTIVE_ENTITY_USER_INDUSTRY: {
        method: 'POST',
        endpoint: 'users/activeEntityUserIndustry',
        schema: z.object({
          entity: z.string(),
        }),
      },
      // POST /invitation/team (InvitationsController). Invite des emails/telephones dans l'equipe
      // officinale d'une company. Reponse: { success, message, data: { emails:{valid,invalid},
      // phones:{valid,invalid} } }.
      INVITE_USERS_TEAM: {
        method: 'POST',
        endpoint: 'invitation/team',
        schema: z.object({
          companyId: z.number().optional(),
          emails: z.array(z.string()),
          phones: z.array(z.string()).optional(),
        }),
      },
    },
    USERS_ADMIN: {
      // POST /userAdmin (UserAdminController, ROLE_SUPER_ADMIN). Creation d'un admin backoffice.
      ADD: {
        method: 'POST',
        endpoint: 'userAdmin',
        schema: z
          .object({
            email: z.string().email(),
          })
          .passthrough(),
      },
      // PUT /userAdmin/{id} (UserAdminController). Mise a jour d'un admin backoffice.
      UPDATE: {
        method: 'PUT',
        endpoint: 'userAdmin/:id',
        schema: z
          .object({
            id: z.number(),
          })
          .passthrough(),
      },
      // POST /users_admin/bulk_update (ROLE_SUPER_ADMIN) : operation groupee de la liste —
      // ajouter / retirer des roles et des services a plusieurs comptes. Reponse
      // { updated, unchanged, skipped: [{ id, name, reason }] }.
      BULK_UPDATE: {
        method: 'POST',
        endpoint: 'users_admin/bulk_update',
        schema: z.object({
          userIds: z.array(z.number().int().positive()).min(1).max(500),
          addRoles: z.array(z.string()),
          removeRoles: z.array(z.string()),
          addServiceIds: z.array(z.number().int().positive()),
          removeServiceIds: z.array(z.number().int().positive()),
        }),
      },
    },
    // Referentiel des SERVICES internes (Gestion RH) — CRUD reserve ROLE_SUPER_ADMIN.
    // `parentId` : service parent d'un sous-service (un seul niveau, verifie cote API).
    ADMIN_SERVICE: {
      ADD: {
        method: 'POST',
        endpoint: 'admin_services',
        schema: z.object({
          name: z.string().min(2),
          parentId: z.number().nullable(),
          email: z.string().email().nullable(),
          dailyTaskReport: z.boolean(),
          mcpAccess: z.boolean(),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'admin_services/:id',
        schema: z.object({
          id: z.number(),
          name: z.string().min(2),
          parentId: z.number().nullable(),
          email: z.string().email().nullable(),
          dailyTaskReport: z.boolean(),
          mcpAccess: z.boolean(),
        }),
      },
      // Ajoute et retire des membres SANS toucher a leurs autres services (lot T-A).
      MEMBERS: {
        method: 'POST',
        endpoint: 'admin_services/:id/members',
        schema: z.object({
          id: z.number(),
          add: z.array(z.number()).optional(),
          remove: z.array(z.number()).optional(),
        }),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'admin_services/:id',
        schema: z.object({ id: z.number() }),
      },
    },
    USERS_INDUSTRY: {
      // POST /userIndustry (UserIndustryCreateController). Inscription d'un compte industriel.
      // Le flag GraphQL `register` n'existe pas en REST. Schema ouvert (email + companyAdd/entities...).
      ADD: {
        method: 'POST',
        endpoint: 'userIndustry',
        schema: z
          .object({
            email: z.string().email(),
          })
          .passthrough(),
      },
    },
    CNOP: {
      REINDEX_PHARMACISTS: { method: 'POST', endpoint: 'cnop_pharmacists/reindex' },
    },
    CAPTURE_SCREEN: {
      // Entity GroupCaptureScreen (Post /group_capture_screen via GroupCaptureScreenProcessor qui
      // dechiffre queryCrypted avant persistance). Renvoie l'objet (group_capture_screen:write).
      SEND_CAPTURE: {
        method: 'POST',
        endpoint: 'group_capture_screen',
        schema: z
          .object({
            keyFile: z.string().min(1),
            queryCrypted: z.string().optional(),
            // Preuve PAR PRODUIT (PLV fixe) : lignes que cette photo prouve — validées
            // côté serveur contre le budget du jeton (fail-closed).
            productPharmacyIds: z.array(z.number().int().positive()).optional(),
            // Métadonnées du fichier d'origine pour la garde anti-doublon serveur (409 si
            // même empreinte ou même couple nom+taille déjà reçu sur le budget).
            fileName: z.string().max(255).optional(),
            fileSize: z.number().int().positive().optional(),
            checksum: z
              .string()
              .regex(/^[a-f0-9]{64}$/)
              .optional(),
          })
          .passthrough(),
      },
    },
    GAME: {
      CREATE: { method: 'POST', endpoint: 'games' },
      UPDATE: { method: 'PATCH', endpoint: 'games/:gameId' },
      DELETE: { method: 'DELETE', endpoint: 'games/:gameId' },
      SAVE_RESULT: { method: 'PATCH', endpoint: 'game_matches/:matchId/result' },
      GENERATE_STRUCTURE: { method: 'POST', endpoint: 'games/generate-structure' },
      JOIN: { method: 'POST', endpoint: 'games/:gameId/join' },
      PREDICT: { method: 'PATCH', endpoint: 'user_games/:userGameId/predict' },
    },
    COOPTATION: {
      // Cooptation generique : invite des emails dans l'equipe officinale de l'utilisateur courant.
      INVITE: { method: 'POST', endpoint: 'cooptation/invite' },
    },
    AWS: {
      // Genere un lien signe S3 pour telecharger un fichier (Model DownloadS3, Post `/get_link_file`).
      // Reponse: { linkSign: string }
      GET_LINK_FILE: {
        method: 'POST',
        endpoint: 'get_link_file',
        schema: z.object({ keyFile: z.string().min(1) }),
      },
      // Genere un lien S3 signe d'UPLOAD (PUT) pour une cle donnee. Reponse: { linkSign, keyFile }
      // allowAccessWithoutSign est tolere mais ignore cote API (LinkAwsUploadController).
      CREATE_LINK_FILE: {
        method: 'POST',
        endpoint: 'create_link_file',
        schema: z.object({
          keyFile: z.string().min(1),
          allowAccessWithoutSign: z.boolean().optional(),
        }),
      },
      // Upload S3 public token-gated (header X-Api-Token = PUBLIC_API_TOKEN backend).
      // Restreint aux keyFile commencant par `capture_client/`. Reponse: { linkSign, keyFile }
      CREATE_LINK_FILE_PUBLIC: {
        method: 'POST',
        endpoint: 'create_link_file_public',
        schema: z.object({ keyFile: z.string() }),
      },
    },
    BANNER: {
      CREATE: { method: 'POST', endpoint: 'banners' },
      UPDATE: { method: 'PATCH', endpoint: 'banners/:bannerId' },
      DELETE: { method: 'DELETE', endpoint: 'banners/:bannerId' },
    },
    // Actualite pharmacie. Contrat : pharmedigroup-api/docs/ACTU_PHARMACIE.md.
    NEWS: {
      // Etat utilisateur d'un item (lu / sauvegarde / ecarte). id -> :id, le reste a plat
      // dans le body (merge-patch). Renvoie NewsItemView.
      UPDATE_STATE: {
        method: 'PATCH',
        endpoint: 'news_items/:id/state',
        schema: z.object({
          id: z.number(),
          read: z.boolean().optional(),
          saved: z.boolean().optional(),
          dismissed: z.boolean().optional(),
        }),
      },
      // Marque lus tous les items visibles (hors caches/ecartes). Renvoie { updated: number }.
      READ_ALL: {
        method: 'POST',
        endpoint: 'news_items/read_all',
        schema: z.object({}),
      },
    },
    NEWS_ADMIN: {
      SOURCE_CREATE: {
        method: 'POST',
        endpoint: 'admin/news_sources',
        schema: z.object({
          name: z.string().min(1),
          feedUrl: z.string().min(1),
          siteUrl: z.string().optional(),
          defaultPriority: z.enum(['critical', 'important', 'info']),
          paywalled: z.boolean().optional(),
          fetchFullContent: z.boolean().optional(),
          active: z.boolean().optional(),
        }),
      },
      SOURCE_UPDATE: {
        method: 'PATCH',
        endpoint: 'admin/news_sources/:id',
        schema: z.object({
          id: z.number(),
          name: z.string().min(1).optional(),
          feedUrl: z.string().min(1).optional(),
          siteUrl: z.string().optional(),
          defaultPriority: z.enum(['critical', 'important', 'info']).optional(),
          paywalled: z.boolean().optional(),
          fetchFullContent: z.boolean().optional(),
          active: z.boolean().optional(),
        }),
      },
      SOURCE_DELETE: {
        method: 'DELETE',
        endpoint: 'admin/news_sources/:id',
        schema: z.object({ id: z.number() }),
      },
      // Collecte immediate asynchrone (202). Pas de corps hormis l'id de chemin.
      SOURCE_FETCH_NOW: {
        method: 'POST',
        endpoint: 'admin/news_sources/:id/fetch',
        schema: z.object({ id: z.number() }),
      },
      ITEM_UPDATE: {
        method: 'PATCH',
        endpoint: 'admin/news_items/:id',
        schema: z.object({
          id: z.number(),
          hidden: z.boolean().optional(),
          pinned: z.boolean().optional(),
          priority: z.enum(['critical', 'important', 'info']).optional(),
          category: z
            .enum([
              'availability',
              'safety',
              'reimbursement',
              'regulation',
              'missions',
              'drugs',
              'good_practice',
              'recommendations',
              'profession',
              'other',
            ])
            .optional(),
        }),
      },
      // Relance le resume IA en asynchrone (202).
      ITEM_RESUMMARIZE: {
        method: 'POST',
        endpoint: 'admin/news_items/:id/resummarize',
        schema: z.object({ id: z.number() }),
      },
    },
    SECURITY: {
      IP_BLACKLIST_CREATE: { method: 'POST', endpoint: 'security/ip_blacklists' },
      IP_BLACKLIST_UPDATE: { method: 'PATCH', endpoint: 'security/ip_blacklists/:id' },
      IP_BLACKLIST_DELETE: { method: 'DELETE', endpoint: 'security/ip_blacklists/:id' },
      UA_BLACKLIST_CREATE: { method: 'POST' as const, endpoint: 'security/user_agent_blacklists' },
      UA_BLACKLIST_DELETE: {
        method: 'DELETE' as const,
        endpoint: 'security/user_agent_blacklists/:id',
      },
    },
    USER_MESSAGE_NOTIFICATION: {
      SEND_BY_FILTER_USER: {
        method: 'POST',
        endpoint: 'user_message_notifications/sendNotificationByFilterUser',
      },
      COUNT_TARGETS: {
        method: 'POST',
        endpoint: 'user_message_notifications/countTargets',
      },
      // `{ id, email? }` : `email` = une des adresses proposees par RESEND_OPTIONS.
      RESEND: {
        method: 'POST',
        endpoint: 'user_message_notifications/:id/resend',
        schema: userMessageResendSchema,
      },
      UPDATE_STATUS: { method: 'PATCH', endpoint: 'user_message_notifications/:id/status' },
    },
    CONTACT_VERIFICATION: {
      CONFIRM: { method: 'POST', endpoint: 'me/contact-verification/my-informations/confirm' },
      CHECK_SIRET: {
        method: 'POST',
        endpoint: 'me/contact-verification/my-informations/check-siret',
      },
      REMOVE_MEMBER: {
        method: 'POST',
        endpoint: 'me/contact-verification/my-team/remove',
      },
      APPLY_SUGGESTION: {
        method: 'PATCH',
        endpoint: 'me/contact-verification/my-team/apply-suggestion',
      },
      COUNTER_CREATE: {
        method: 'POST',
        endpoint: 'me/contact-verification/my-counter/create',
      },
      COUNTER_SWITCH: {
        method: 'POST',
        endpoint: 'me/contact-verification/my-counter/switch',
      },
      COUNTER_PIN_SET: {
        method: 'POST',
        endpoint: 'me/contact-verification/my-counter/pin/set',
      },
      COUNTER_PIN_RESET: {
        method: 'POST',
        endpoint: 'me/contact-verification/my-counter/pin/reset',
      },
    },
  },
  SOUND: {
    REMOTE_ACCESS: {
      UPDATE: { method: 'PUT', endpoint: 'companies/:companyId/remote_access' },
    },
    // Regie Dploy (pharmedigroup-api/docs/SOUND_REGIES.md).
    DPLOY: {
      // Relit tout de suite chez Dploy boutiques et pubs eligibles (sans corps).
      REFRESH: { method: 'POST', endpoint: 'dploy/refresh', schema: z.object({}) },
      // Rattache une boutique a une pharmacie (IRI), ou la detache (null).
      LINK_SHOP: {
        method: 'PATCH',
        endpoint: 'dploy_shops/:shopId',
        schema: z.object({
          shopId: z.number().int().positive(),
          company: z
            .string()
            .regex(/^\/api\/v2\/companies\/\d+$/)
            .nullable(),
        }),
      },
    },
    // « Je veux le spot » (newsletter) : ajoute le spot public au planning, sans session.
    // Reponse { status: 'added' | 'already_scheduled' } ; 404 si le lien est invalide.
    PUBLIC_SPOT_OPT_IN: { method: 'POST', endpoint: 'public/sound/spot/:token' },
    // « Spots disponibles » : la pharmacie est celle de la session, jamais un paramètre. 404 quand
    // le spot ne lui est plus proposé. ADD reçoit { days } (1 = lundi … 7 = dimanche, 422 si un
    // jour ne convient pas) et répond { status: 'added' | 'already_scheduled' }.
    AVAILABLE_SPOTS: {
      ADD: { method: 'POST', endpoint: 'me/sound/available_spots/:spotId/add' },
      DECLINE: { method: 'POST', endpoint: 'me/sound/available_spots/:spotId/decline' },
    },
    // Installation Sound (#768) : écriture PARTIELLE, un champ null est laissé tel quel.
    INSTALLATION: {
      UPDATE: { method: 'PUT', endpoint: 'companies/:companyId/sound_installation' },
    },
    // Corrections du parc depuis la fiche (08/10/2026) : remplacer un modèle, déclarer du
    // matériel en place, matériel personnel — appliquées ensemble, journalisées une par une.
    PARC: {
      UPDATE: { method: 'PUT', endpoint: 'companies/:companyId/sound_parc' },
    },
    // Diagnostic IA du lecteur Ulysse (Claude Haiku, 13/10/2026) : logs 7 jours + état du poste,
    // rien n'est écrit. Corps `{companyId}`. Types : types/sound/ulysseLog.ts.
    ULYSSE_LOG_ANALYSIS: { method: 'POST', endpoint: 'sound/ulysse_logs/analysis' },
    // Rapport d'eligibilite Saooti annote, en MULTIPART (champ `file`, `apply=1` pour appliquer) :
    // envoye par fetch + FormData, la validation Zod porte sur le fichier avant l'envoi.
    SAOOTI_TRACK_DECISION_IMPORT: {
      POST: { method: 'POST', endpoint: 'sound/saooti_track_decision_imports' },
    },
    PROGRAM: {
      // Formulaire programme radio : corps large -> schema `.passthrough()`. Il ne FERME que
      // `campaignAxis` (labos en mode « Deux axes GX / BS »), dont une chaine hors enum ferait
      // lever une ValueError PHP par `SoundProgramme::setCampaignAxis()`. `null` = sans axe.
      ADD: {
        method: 'POST',
        endpoint: 'sound_programmes',
        schema: soundProgrammeCampaignSchema,
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'sound_programmes/:programId',
        schema: soundProgrammeCampaignSchema,
      },
      DELETE: { method: 'DELETE', endpoint: 'sound_programmes/:programId' },
      // Generation auto du programme a partir d'un CSV (ou manuel). SoundGenerateProgrammeWithCsvController.
      // :programId = id programme, :generateType = 'generate' | 'manual' | 'simulation'. Body { keyFile? }.
      // Reponse = message (string), dispatch async cote back.
      ADD_AUTO_GENERATE: {
        method: 'POST',
        endpoint: 'sound_programmes/:programId/import_generation_auto/:generateType',
      },
      SPOT_LIST_EXPORT: { method: 'POST', endpoint: 'sound_programmes/list_spot_export' },
      // Export du rapport de stats. Body { programIds: number[] }. Si programIds non vide, renvoie
      // une URL S3 signee (string). Sinon dispatch async + renvoie 'ok' (string).
      STAT_RAPPORT: { method: 'POST', endpoint: 'sound_programmes/stat_rapport' },
      // Lance le rapport de suivi (bad job / no connect). Dispatch async, renvoie un message (string).
      SUIVI_REPORT_EXPORT: { method: 'POST', endpoint: 'sound_programmes_suivi_export' },
      SOUNDCAST_CREATE_CAMPAIGN: {
        method: 'POST',
        endpoint: 'sound_programmes/:programId/soundcast_campaign',
      },
      // Liaison programme <-> gabarit de document signable (paramétrage convention Sound).
      SAVE_CONVENTION_BINDING: {
        method: 'PUT',
        endpoint: 'sound_programmes/:programId/convention_binding',
        schema: conventionBindingSchema,
      },
      UNLINK_CONVENTION_TEMPLATE: {
        method: 'DELETE',
        endpoint: 'sound_programmes/:programId/convention_binding',
      },
    },
    PROGRAM_PHARMACY_BUDGET: {
      // Ajout multiple de pharmacies (avec budget) a un programme — back: postMultiPharmaciesProgramController.
      // Payload: { programmeId, pharmacies: [{ companyId, soundBudget }] }. user + segment deduits cote back.
      ADD_MULTI: { method: 'POST', endpoint: 'sound_programme_pharmacie_budgets_multi' },
      DELETE: { method: 'DELETE', endpoint: 'sound_programme_pharmacie_budgets/:id' },
      // Refus / acceptation UNITAIRES du pharmacien retires le 22/09/2026 (D-13) : la
      // decision se prend dans « Mes partenaires » (GROUP.PARTNER_CAMPAIGN.PARTICIPATION_REJECT).
      // Annulation admin d'un refus : endpoint dedie JOURNALISE (v2) — remplace l'ancien
      // PATCH generique qui perdait la date du refus. Reponse: ParticipationInfo recalcule.
      PARTICIPATION_CANCEL_REJECT: {
        method: 'POST',
        endpoint: 'sound_programme_pharmacie_budgets/:budgetId/participation_cancel_reject',
        schema: z.object({}),
      },
      // Refus saisi par un admin pour le compte de la pharmacie (D-14) : motif obligatoire,
      // journalise au nom de l'admin. Reponse : le statut de participation recalcule.
      PARTICIPATION_ADMIN_REJECT: {
        method: 'POST',
        endpoint: 'sound_programme_pharmacie_budgets/:budgetId/participation_admin_reject',
        schema: z.object({ reason: z.enum(PARTICIPATION_REJECT_REASONS) }),
      },
      // Forcage admin « autoriser la diffusion sans contrat » au cas par cas (journalise) :
      // pendant Sound du force_no_contract PLV. Reponse: { allowBroadcastWithoutContract }.
      FORCE_NO_CONTRACT: {
        method: 'POST',
        endpoint: 'sound_programme_pharmacie_budgets/:budgetId/force_no_contract',
        schema: z.object({ enabled: z.boolean() }),
      },
      // Forcage du reporting (issue front #727) : ajoute des heures dans les semaines de la
      // pharmacie et recalcule toutes ses campagnes de ces semaines.
      // Reponse { changed, message, recomputed: [{ budgetId, programme, percentReporting }] }.
      GENERATE_REPORTING_REMAINING: {
        method: 'POST',
        endpoint: 'sound_programme_pharmacie_budgets/generate_reporting_remaining',
      },
      // Recalcul de l'avancement de la campagne et de ses voisines des memes semaines.
      // Reponse { id, percentReporting, message, recomputed }.
      FLUSH_REPORTING_BUDGET: {
        method: 'POST',
        endpoint: 'sound_programme_pharmacie_budgets/:id/flush_reporting_budget',
      },
    },
    PROGRAM_SPOT_PHARMACY: {
      DELETE: { method: 'DELETE', endpoint: 'sound_programme_spot_pharmacies/:programSpotId' },
      // Ajout multiple de spots a une pharmacie — SoundProgrammeSpotPharmacieController.
      // Body { programmeSpots: number[], pharmacieBudgetId: number, numberSpot: number }. Reponse string.
      ADD_MULTI: {
        method: 'POST',
        endpoint: 'sound_programme_spot_pharmacies_multi',
      },
      // Refus d'un spot programme : envoie un mail de decline. SendMailDeclineSpotProgrammeController.
      // Body { programmeSpotPharmacieId: number }. Reponse boolean.
      SEND_MAIL_DECLINE: {
        method: 'POST',
        endpoint: 'sound_programme_spot_pharmacie_action/send_mail_decline_spot_programme',
      },
    },
    SPOT: {
      // Creation d'un spot — SoundSpot Post (SoundSpotPostProcessor). Body { name, type, keyFile,
      // archive, partners[], scope, targetGroupement?(IRI), distribution? }. partners/companyListContactId
      // sont lus directement depuis le body par le processor. Reponse = spot cree.
      ADD: { method: 'POST', endpoint: 'sound_spots' },
      // Mise a jour d'un spot — SoundSpot Patch. :spotId resout le path. Body identique a ADD.
      UPDATE: { method: 'PATCH', endpoint: 'sound_spots/:spotId' },
      DELETE: { method: 'DELETE', endpoint: 'sound_spots/:spotId' },
      // Export XLSX des officines qui ont programme ce spot dans leur planning radio (issue #721).
      // POST /sound_spots/{id}/scheduling_export -> 202 ; genere en asynchrone (Messenger), envoye
      // par e-mail au demandeur et inscrit dans son historique de telechargements. Reserve aux
      // spots publics / de groupement : l'API repond 422 sur tout autre scope. :spotId resout le
      // path (lu sur la donnee brute) ; le schema rend un corps VIDE, car l'operation est un Post
      // sur SoundSpot : un corps { spotId } serait denormalise comme un spot.
      SCHEDULING_EXPORT: {
        method: 'POST',
        endpoint: 'sound_spots/:spotId/scheduling_export',
        asyncExport: true,
        schema: z.object({ spotId: z.number().int().positive() }).transform(() => ({})),
      },
      ASSOCIATE_SPOTS_MULTI: { method: 'POST', endpoint: 'sound_programme_spots_multi' },
      DISSOCIATE_PROGRAMME_SPOT: {
        method: 'DELETE',
        endpoint: 'sound_programme_spots/:programSpotId',
      },
      // Refus (POST) / ré-acceptation (DELETE) spot-par-spot d'un préroll « parc entier »
      // par le pharmacien. :programmeSpotId resout le path ; body { reason? } (strip).
      PARK_REFUSAL_ADD: {
        method: 'POST',
        endpoint: 'sound_programme_spots/:programmeSpotId/park_refusal',
        schema: z.object({ reason: z.string().optional() }),
      },
      PARK_REFUSAL_CANCEL: {
        method: 'DELETE',
        endpoint: 'sound_programme_spots/:programmeSpotId/park_refusal',
      },
      SOUNDCAST_CREATE_AD: {
        method: 'POST',
        endpoint: 'sound_programme_spots/:soundProgramSpotId/soundcast_ad',
      },
      ASSIGN_AD: {
        method: 'PATCH',
        endpoint: 'sound_programme_spots/:soundProgramSpotId/soundcast_ad_assign',
      },
      GENERATE_TEXT: {
        method: 'POST',
        endpoint: 'sound/spots/generate-text',
      },
      NORMALIZE_PRIVATE: {
        method: 'POST',
        endpoint: 'sound_spot/normalize-private',
      },
      // Generation MP3 TTS d'un spot via ElevenLabs. Body { prompt, voice, language?, backgroundMusic? }.
      // Controller GenerateVoiceController. Reponse { success, data: { audio_base64 } }.
      GENERATE_MP3: {
        method: 'POST',
        endpoint: 'sound-advertisements/generate',
        schema: z.object({
          prompt: z.string().min(1).max(5000),
          voice: z.string().min(1),
          language: z.enum(['fr', 'it']).optional(),
          backgroundMusic: z.string().optional(),
        }),
      },
    },
    REPORTING: {
      // Export des connexions Ulysse Connected : dispatch async + renvoie un message (string).
      // Controller SoundExportConnectedUlysseController (dateStart/dateEnd ignores cote back).
      EXPORT_ULYSSE_CONNECTED: {
        method: 'POST',
        endpoint: 'sound_reporting_ulysses_export',
      },
      // Import diffusion multi-programmes : dispatch async du traitement du CSV deja uploade.
      // SoundReportingSelectProgramController. Body { keyFile }. Reponse = message (string).
      PROCESS_IMPORT_FILE_MULTI_PROGRAM: {
        method: 'POST',
        endpoint: 'sound_reporting/post_reporting_with_csv_select_program',
      },
      // Calcul attribution potentielle par CIP : traite le CSV uploade et renvoie une URL S3 (string).
      // SoundReportingSelectCipController. Body { keyFile, totalBudget }.
      PROCESS_IMPORT_FILE_MULTI_CIP: {
        method: 'POST',
        endpoint: 'sound_reporting/post_reporting_with_csv_select_cip',
      },
    },
    CERTIFICATE: {
      // Generation de l'attestation de diffusion. GenerateCertificatePlaylistSoundController.
      // :id = company id. Body { type }. Reponse { urlRedirect }.
      GENERATE_CERTIFICATE: {
        method: 'POST',
        endpoint: 'company/:id/generateCertificatePlaylistSound',
      },
    },
    SAOOTI: {
      ADD_SAOOTI_ADS: { method: 'POST', endpoint: 'pharmacie_sound_saooti_ads' },
      UPDATE_SAOOTI_AD: { method: 'PATCH', endpoint: 'pharmacie_sound_saooti_ads/:id' },
      DELETE_SAOOTI_AD: { method: 'DELETE', endpoint: 'pharmacie_sound_saooti_ads/:id' },
      ADD_SAOOTI_PROGRAM: { method: 'POST', endpoint: 'pharmacie_sound_saooti_programs' },
      UPDATE_SAOOTI_PROGRAM: { method: 'PATCH', endpoint: 'pharmacie_sound_saooti_programs/:id' },
      DELETE_SAOOTI_PROGRAM: { method: 'DELETE', endpoint: 'pharmacie_sound_saooti_programs/:id' },
      APPLY_SAOOTI_PROGRAM: { method: 'POST', endpoint: 'pharmacie_sound_saooti_programs/apply' },
      COPY_DAY_TO_ALL: {
        method: 'POST',
        endpoint: 'pharmacie_sound_saooti_programs/copy-day-to-all',
      },
      APPLY_AI_PLANNING: {
        method: 'POST',
        endpoint: 'pharmacie_sound_saooti_programs/apply-ai-planning',
      },
      CLEAR_ALL_PROGRAMS: {
        method: 'POST',
        endpoint: 'pharmacie_sound_saooti_programs/clear-all',
      },
      UPDATE_PLAYER_LIST: {
        method: 'PATCH',
        endpoint: 'pharmacie_sound_playlist/:pharmacySoundId',
      },
      ADD_DEFAULT_PLAYLIST: {
        method: 'POST',
        endpoint: 'pharmacie_sound_saooti_default_playlists',
      },
      UPDATE_DEFAULT_PLAYLIST: {
        method: 'PATCH',
        endpoint: 'pharmacie_sound_saooti_default_playlists/:id',
      },
      DELETE_DEFAULT_PLAYLIST: {
        method: 'DELETE',
        endpoint: 'pharmacie_sound_saooti_default_playlists/:id',
      },
      ACTIVATE_PLANNING: { method: 'PUT', endpoint: 'company/:id/activeSound' },
    },
    PLANNING_AI: {
      SUGGEST: { method: 'POST', endpoint: 'sound/planning/suggest' },
    },
    PLAYLIST_IA: {
      UPLOAD: { method: 'POST', endpoint: 'sound/playlists-ia/:folder/upload' },
      DELETE_FILE: { method: 'DELETE', endpoint: 'sound/playlists-ia/:folder/files/:filename' },
      NORMALIZE: { method: 'POST', endpoint: 'sound/playlists-ia/:folder/normalize' },
      NORMALIZE_FILES: { method: 'POST', endpoint: 'sound/playlists-ia/:folder/normalize-files' },
      ANALYZE: { method: 'POST', endpoint: 'sound/playlists-ia/:folder/analyze' },
      GENERATE_SUNO: { method: 'POST', endpoint: 'sound/playlists-ia/:folder/generate-suno' },
    },
    DELIVERY_EQUIPMENT: {
      UPDATE: { method: 'PATCH', endpoint: 'sound_delivery_equipments/:id' },
      DELETE: { method: 'DELETE', endpoint: 'sound_delivery_equipments/:id' },
      // « Marquer reçu » : la date de réception est posée par le SERVEUR, pas envoyée par
      // l'écran — sinon deux postes en désaccord d'horloge écrivent deux vérités.
      // « Marquer reçu » avec son constat (01/10/2026) : complet, incomplet ou abîmé ; le détail
      // est exigé par l'API dès que le colis n'est pas complet.
      RECEIVED: {
        method: 'POST',
        endpoint: 'sound_delivery_equipments/:id/received',
        schema: z.object({
          id: z.number().int().positive(),
          receptionStatus: z.enum(['complete', 'incomplete', 'damaged']),
          receptionNote: z.string().trim().max(500).optional(),
        }),
      },
      // « Commander en boutique » un envoi resté à expédier (01/10/2026) : commande à 0 €.
      ORDER_IN_STORE: {
        method: 'POST',
        endpoint: 'sound_delivery_equipments/:id/order_in_store',
        schema: z.object({ id: z.number().int().positive() }),
      },
      // Relancer un retour non déposé : renvoie au pharmacien l'e-mail et le bon déjà rangés.
      // Une étiquette DPD expirée est refusée (409 `label_expired`) : en créer une nouvelle.
      RETURN_REMINDER: {
        method: 'POST',
        endpoint: 'sound_delivery_equipments/:id/return_reminder',
        schema: z.object({ id: z.number().int().positive() }),
      },
      // Étiquette de retour DPD : créée par la boutique, rangée sur le retour, envoyée au
      // pharmacien. Chaque appel crée un envoi chez DPD — le plafond est celui du réseau Pickup.
      DPD_RETURN_LABEL: {
        method: 'POST',
        endpoint: 'sound_delivery_equipments/:id/dpd_return_label',
        schema: z.object({
          id: z.number().int().positive(),
          weightKg: z.number().positive().max(20),
          // Remplacer une étiquette EXPIRÉE (relance d'un retour non déposé).
          replaceExpired: z.boolean().optional(),
        }),
      },
    },
  },
  INSIGHT: {
    PHARMACY: {
      DISABLED: { method: 'POST', endpoint: 'companies/:companyId/disabled' },
      // Import/maj des pharmacies depuis un fichier deja televerse (keyFile S3).
      // uriTemplate API: POST /pharmacies/import_file_pharmacies (ImportPharmaciesController).
      // Reponse: message (string).
      PROCESS_IMPORT: {
        method: 'POST',
        endpoint: 'pharmacies/import_file_pharmacies',
        schema: z.object({
          keyFile: z.string(),
          typeCompany: z.string(),
        }),
      },
      // Applique un rapport de CONTRÔLE sans erreur (le CSV du rapport, tokenKey compris,
      // repart dans la file d'import). 202 ; 422 si déjà appliqué ou non applicable.
      APPLY_IMPORT_REPORT: {
        method: 'POST',
        endpoint: 'me/pharmacy_import_reports/:reportId/apply',
        schema: z.object({ reportId: z.number().int().positive() }),
      },
      // Import des groupes utilisateur CS-Cart depuis un fichier deja televerse (keyFile S3).
      // ADMIN uniquement. Reponse : message (string).
      PROCESS_IMPORT_USER_GROUPS: {
        method: 'POST',
        endpoint: 'pharmacies/import_user_groups',
        schema: z.object({
          keyFile: z.string(),
        }),
      },
    },
    CHALLENGE: {
      // POST sans corps : `user` + `repartitionProgramme` resolvent les path variables
      // (uriTemplate API Platform: /repartition_program_user_challenges/{user}/{repartitionProgramme}).
      ACTIVE_USER_TEAM: {
        method: 'POST',
        endpoint: 'repartition_program_user_challenges/:user/:repartitionProgramme',
      },
    },
    // Transparence Sante (declaration reglementaire TS). Cf. ApiQueryEndpoint.INSIGHT.TRANSPARENCY.
    TRANSPARENCY: {
      // PATCH /transparency_entries/{id} (merge-patch+json) : exclure une ligne (status=excluded
      // + excludedReason obligatoire) ou la remettre en brouillon (status=draft). Transitions
      // controlees par TransparencyEntryStateManager cote API (422 si deja transmise).
      UPDATE_ENTRY: {
        method: 'PATCH',
        endpoint: 'transparency_entries/:id',
        schema: z.object({
          id: z.number(),
          status: z.enum(['draft', 'excluded']),
          excludedReason: z.string().min(1).optional(),
        }),
      },
      // POST /transparency_entries/{id}/adopt_annuaire (sans body, pas d'input declare
      // cote API Platform) : adopte les valeurs officielles de l'Annuaire Sante dans la
      // declaration puis re-pre-valide. 422 si deja transmise/ecartee, pas de RPPS, ou
      // ecart total (RPPS suspect). `id` resolu comme path variable (pas de schema).
      ADOPT_ANNUAIRE: {
        method: 'POST',
        endpoint: 'transparency_entries/:id/adopt_annuaire',
      },
      // POST /transparency_entries/{id}/edit_declaration : corrige a la main les valeurs
      // DECLAREES d'une ligne (montant, dates, objet de convention, beneficiaire...).
      // Semantique merge-patch : seules les clefs transmises dans `fields` sont touchees.
      // Une valeur null LEVE la correction (le champ retrouve la valeur de la fiche
      // source), la chaine vide vide volontairement le champ declare.
      // L'API memorise chaque correction par champ (la collecte suivante ne l'ecrase pas)
      // puis repasse la ligne en pre-validation : la reponse porte les issues restantes
      // dans openIssues, et le statut recalcule. Une ligne deja integree/publiee bascule
      // en action M et rejoint « A mettre a jour ». 422 si le lot est en cours d'envoi,
      // si la ligne est ecartee, ou si un champ n'est pas modifiable.
      EDIT_DECLARATION: {
        method: 'POST',
        endpoint: 'transparency_entries/:id/edit_declaration',
        schema: z.object({
          id: z.number(),
          fields: z.record(z.string(), z.string().nullable()),
        }),
      },
      // POST /transparency_entries/{id}/fix_rpps : corrige le RPPS du TITULAIRE (fiche source)
      // apres recherche Annuaire, et repasse la ligne en brouillon pour recollecte.
      FIX_RPPS: {
        method: 'POST',
        endpoint: 'transparency_entries/:id/fix_rpps',
        schema: z.object({
          id: z.number(),
          rpps: z.string().regex(/^\d{11}$/, 'Le RPPS doit contenir 11 chiffres.'),
        }),
      },
      // POST /transparency_entries/{id}/resend_as_modification : repasse en action M une
      // ligne rejetee par TS avec ERR_05 (« identifiant unique deja connu »). Elle repart
      // au prochain envoi. 422 si la ligne ne porte pas ce code.
      RESEND_AS_MODIFICATION: {
        method: 'POST',
        endpoint: 'transparency_entries/:id/resend_as_modification',
      },
      // POST /transparency_entries/validate_drafts : repasse les brouillons du declarant
      // en pre-validation. Un brouillon n'est pas envoyable et rien d'autre, dans l'ecran,
      // ne permet de l'en sortir (la pre-validation ne tourne sinon qu'a la collecte).
      VALIDATE_DRAFTS: {
        method: 'POST',
        endpoint: 'transparency_entries/validate_drafts',
        schema: z.object({ account: z.string().min(1) }),
      },
      // POST /transparency_entries/refresh_from_ts : verifie chez TS, par identifiant
      // unique, l'existence et l'etat des lignes du declarant (asynchrone, un appel HTTP
      // par ligne). Capte les declarations collees a la main sur le site depuis notre
      // export XLSX — leurs identifiants sont les notres. Repond 202 + nombre de lignes.
      REFRESH_FROM_TS: {
        method: 'POST',
        endpoint: 'transparency_entries/refresh_from_ts',
        schema: z.object({ account: z.string().min(1) }),
      },
      // POST /transparency_batches/{id}/poll : suit CE lot (redemande son etat a TS puis
      // rapproche ses retours de nos lignes). Rejouable sans effet de bord. 422 si le lot
      // n'a pas encore ete transmis.
      POLL_BATCH: {
        method: 'POST',
        endpoint: 'transparency_batches/:id/poll',
      },
      // POST /transparency_batches/send : constitue un lot avec les lignes pretes (VALIDATED/TO_UPDATE)
      // et le met en file d'envoi. Repond 201 avec le lot cree.
      SEND_BATCH: {
        method: 'POST',
        endpoint: 'transparency_batches/send',
        schema: z.object({
          account: z.string().min(1),
          limit: z.number().int().positive().optional(),
        }),
      },
      // PATCH /transparency_programs/{id} (merge-patch+json) : qualifie un programme Insight
      // pour le dispositif Transparence Sante (declarable=true), l'exclut (false), ou le
      // remet a qualifier (null). Porte aussi les surcharges des valeurs DECLAREES
      // (objet de convention, sujet publie, periode), sans toucher au programme lui-meme.
      // Tous les champs sont optionnels : l'API n'ecrase QUE ceux reellement transmis
      // (semantique merge-patch), une chaine vide valant effacement de la surcharge.
      // Cf. ApiQueryEndpoint.INSIGHT.TRANSPARENCY.PROGRAMS.
      UPDATE_PROGRAM: {
        method: 'PATCH',
        endpoint: 'transparency_programs/:id',
        schema: z.object({
          id: z.number(),
          declarable: z.boolean().nullable().optional(),
          conventionObject: z.string().min(1).optional(),
          informationConvention: z.string().max(255).optional(),
          declaredDateDebut: z.string().optional(),
          declaredDateFin: z.string().optional(),
        }),
      },
      // POST /transparency_programs/{id}/collect : (re)genere/rafraichit les lignes de
      // declaration du programme sur la periode [from, to[, avec verification Annuaire Sante
      // optionnelle. Reponse : { id, action, message, created, refreshed, validated, rejected,
      // adopted, errors }.
      COLLECT_PROGRAM: {
        method: 'POST',
        endpoint: 'transparency_programs/:id/collect',
        schema: z.object({
          id: z.number(),
          from: z.string().optional(),
          to: z.string().optional(),
          annuaireSante: z.boolean().optional(),
          adoptAnnuaire: z.boolean().optional(),
        }),
      },
      // POST /transparency_programs/{id}/send : constitue et envoie un lot avec les lignes
      // pretes du programme (limit optionnel). Envoi reel vers Transparence-Sante.
      SEND_PROGRAM: {
        method: 'POST',
        endpoint: 'transparency_programs/:id/send',
        schema: z.object({
          id: z.number(),
          limit: z.number().int().positive().optional(),
        }),
      },
      // POST /transparency_programs/{id}/poll : suit l'etat des lots en cours du programme
      // (reconciliation TS : integres/rejetes/publies).
      POLL_PROGRAM: {
        method: 'POST',
        endpoint: 'transparency_programs/:id/poll',
        schema: z.object({
          id: z.number(),
        }),
      },
    },
    // Declarations de transparence marquees envoyees EN MASSE, a une date choisie (retroactive
    // possible, jamais future) : selection de lignes (`documentIds`) ou tout un programme
    // (`programId` + filtre `completion` de l'ecran). `dryRun` = apercu sans ecriture.
    // Reponse : { count, updated, invoiceDelayDays }.
    TRANSPARENCY_DECLARATION: {
      MARK_SENT: {
        method: 'POST',
        endpoint: 'documents/transparency_declarations/mark_sent',
        schema: z.object({
          documentIds: z.array(z.number().int().positive()).optional(),
          programId: z.number().int().positive().nullable().optional(),
          completion: z.enum(['all', 'completed', 'incomplete']),
          // Seuil EPS de l'ecran : un programme entier ne marque que les lignes affichees.
          amountScope: z.enum(['all', 'declaration', 'authorization']).optional(),
          // Periode de signature de l'ecran (jours de Paris, bornes incluses).
          signedFrom: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .optional(),
          signedTo: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .optional(),
          sentAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          dryRun: z.boolean().optional(),
        }),
      },
      // N° EPS colle depuis eps.sante.gouv.fr, enregistre a la sortie du champ : le N° EST la
      // declaration (il pose la date, jamais ecrasee) ; vide = effacer, la declaration repasse a faire.
      EPS_NUMBER: {
        method: 'POST',
        endpoint: 'documents/transparency_declarations/eps_number',
        schema: z.object({
          documentId: z.number().int().positive(),
          epsNumber: z.string().max(200).nullable(),
        }),
      },
      // Commentaire de suivi de la declaration (petit bouton de la ligne) ; vide = effacer.
      EPS_COMMENT: {
        method: 'POST',
        endpoint: 'documents/transparency_declarations/eps_comment',
        schema: z.object({
          documentId: z.number().int().positive(),
          epsComment: z.string().max(1000).nullable(),
        }),
      },
      // Export XLSX asynchrone des declarations d'un programme (fichier envoye par e-mail).
      EXPORT: {
        method: 'POST',
        endpoint: 'documents/transparency_declarations/export',
        schema: z.object({
          programId: z.number().int().positive(),
          status: z.enum(['all', 'todo', 'done']).optional(),
          completion: z.enum(['all', 'completed', 'incomplete']).optional(),
          amountScope: z.enum(['all', 'declaration', 'authorization']).optional(),
        }),
      },
      // Reprise des N° EPS depuis le fichier de suivi : upload multipart direct (`file`,
      // `programId`, `apply`), simulation par defaut — cf. EpsImportDialog.
      EPS_IMPORT: { method: 'POST', endpoint: 'documents/transparency_declarations/eps_import' },
    },
    CONVENTION_SPECIALE: {
      UPDATE_SEND_AT: { method: 'PATCH', endpoint: 'convention_speciales/:id/send_at' },
    },
    CONTRACT_APPLICATION: {
      UPDATE_SEND_AT: { method: 'PATCH', endpoint: 'contract_applications/:id/send_at' },
    },
    // Nouveau systeme : document signable du programme (send_at porte par son Document), meme
    // marquage « declaration envoyee » que la convention speciale / le contrat d'application.
    SIGNABLE_DOCUMENT_INSTANCE: {
      UPDATE_SEND_AT: {
        method: 'PATCH',
        endpoint: 'signable_document_instances/:id/send_at',
        schema: signableInstanceSendAtSchema,
      },
    },
    CONVENTION_CADRE: {
      // Suppression (soft delete) d'une convention cadre. uriTemplate API: DELETE /convention_cadres/{id}.
      DELETE: { method: 'DELETE', endpoint: 'convention_cadres/:id' },
    },
    PRODUCT: {
      // Entity Product : POST /products et PATCH /products/{id} (security ROLE_SUPER_ADMIN).
      // `pot` envoye en IRI (parseIRIId('pots', ...)). Reponse: Product { id, ... }.
      ADD: {
        method: 'POST',
        endpoint: 'products',
        schema: z
          .object({
            title: z.string().optional(),
            name: z.string().optional(),
            description: z.string().nullable().optional(),
            category: z.string().optional(),
            value: z.number().optional(),
            enabled: z.boolean().optional(),
            pot: z.string().nullable().optional(),
          })
          .passthrough(),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'products/:id',
        schema: z
          .object({
            id: z.number(),
            title: z.string().optional(),
            name: z.string().optional(),
            description: z.string().nullable().optional(),
            category: z.string().optional(),
            value: z.number().optional(),
            enabled: z.boolean().optional(),
            pot: z.string().nullable().optional(),
          })
          .passthrough(),
      },
    },
    PROGRAM: {
      // Fenetre temporelle d'une enquete dans le programme (#661) : programmeId / surveyId en ids
      // scalaires (jamais d'IRI, cf. lib/insight/surveyWindow/schema.ts), dates 'YYYY-MM-DD' ou
      // null (= heritage de la date du programme). Le PATCH remplace les deux dates.
      SURVEY_WINDOW_CREATE: {
        method: 'POST',
        endpoint: 'programme_survey_windows',
        schema: surveyWindowCreateSchema,
      },
      SURVEY_WINDOW_UPDATE: {
        method: 'PATCH',
        endpoint: 'programme_survey_windows/:id',
        schema: surveyWindowUpdateSchema,
      },
      SURVEY_WINDOW_DELETE: {
        method: 'DELETE',
        endpoint: 'programme_survey_windows/:id',
        schema: surveyWindowDeleteSchema,
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'programmes/:id',
        // Corps merge-patch ; `id` resout la path variable. Champs optionnels (PATCH).
        // pot/partner envoyes en IRI (parseIRIId), maxResponseNumberJson en tableau JSON.
        schema: z.object({
          id: z.number(),
          nom: z.string().optional(),
          nomCourt: z.string().nullable().optional(),
          debut: z.string().optional(),
          finSaisie: z.string().optional(),
          maxResponseNumber: z.number().nullable().optional(),
          country: z.string().optional(),
          pot: z.string().nullable().optional(),
          partner: z.string().nullable().optional(),
          maxResponseNumberJson: z.array(z.any()).optional(),
          // Retours 05/10/2026 : grille de quotas active ou en sommeil, le Total s applique toujours (Programme::quotaEnabled).
          quotaEnabled: z.boolean().optional(),
          disabledSendMail: z.boolean().optional(),
          invoiceIfAllTask: z.boolean().optional(),
          displayLogoTargetPublic: z.boolean().optional(),
          target: z.string().nullable().optional(),
          invoiceAmountEquipe: z.number().optional(),
          numberTaskByUserTeam: z.number().optional(),
          description: z.string().optional(),
          challengeImagePath: z.string().optional(),
          independentChallengeCounter: z.boolean().optional(),
          isInvoiceAvailable: z.boolean().optional(),
          hasCustomEndConvention: z.boolean().optional(),
          customConventionSubject: z.string().optional(),
          customConventionForme: z.string().optional(),
          customConventionObjectifs: z.string().optional(),
          // Convention declaree a EPS et a Transparence Sante (dates 'YYYY-MM-DD' ou null).
          transparencyInformationConvention: z.string().max(255).nullable().optional(),
          transparencyDateDebut: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .optional(),
          transparencyDateFin: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .optional(),
        }),
      },
      IMPORT_CIP: { method: 'POST', endpoint: 'programmes/import_file_generate_mail_cip' },
      // Valide le programme et lance la creation des taches. Reponse : { generationLaunched,
      // pendingRepartitions } — generationLaunched = false quand aucune pharmacie n'attend.
      VALIDATED: {
        method: 'POST',
        endpoint: 'programmes/:id/validated',
        schema: z.object({ id: z.number() }),
      },
      // Export du rapport de stats Insight. Body { programIds: number[] }. Si programIds non vide,
      // renvoie une URL S3 signee (string). Sinon dispatch async + renvoie 'ok'. Analogue de SOUND.PROGRAM.STAT_RAPPORT.
      STAT_RAPPORT: { method: 'POST', endpoint: 'programmes/stat_rapport' },
      EXPORT_REPARTITION: { method: 'GET', endpoint: 'programmes/:id/exportRepartition' },
      // Telecharge un fichier de reporting du programme : POST { file } -> { linkSign } (URL S3 signee).
      // `id` resout la path variable, `file` est le corps lu par le controller.
      DOWNLOAD_FILE: {
        method: 'POST',
        endpoint: 'programmes/:id/download_file_programme',
        schema: z.object({ id: z.number(), file: z.string() }),
      },
      ADD_FILE_REPARTITION: {
        method: 'POST',
        endpoint: 'programmes/:programme/import_file_repartition',
      },
      // Liens publics PERSONNALISES : creation en masse depuis une liste d'emails
      // (idempotent par email). Reponse: {created, skipped, invalid, links}
      CREATE_PUBLIC_LINKS: {
        method: 'POST',
        endpoint: 'programmes/:id/public_links',
        schema: z.object({
          id: z.number(),
          emails: z.array(z.string()),
        }),
      },
      // Fichier client (DEC-03) : remplace la liste des CIP clients du programme.
      // Reponse: {count, invalid, stats}
      SET_CLIENT_SEGMENT: {
        method: 'POST',
        endpoint: 'programmes/:id/client_segment',
        schema: z.object({
          id: z.number(),
          cips: z.array(z.string()),
        }),
      },
      // Selection dans la base (DEC-04) : preview -> {count} ; sinon charge les
      // pharmacies dans la repartition -> {matched, created, skippedNoUser}
      SEGMENTATION_PICKER: {
        method: 'POST',
        endpoint: 'programmes/:id/segmentation_picker',
        schema: z.object({
          id: z.number(),
          regions: z.array(z.string()).optional(),
          urbanSections: z.array(z.string()).optional(),
          turnovers: z.array(z.string()).optional(),
          insightActiveOnly: z.boolean().optional(),
          preview: z.boolean().optional(),
        }),
      },
      ROBOT_ASK_NUMBER: {
        method: 'POST',
        endpoint: 'programmes/programme_ajax_list_pharmacy_select_to_generate_response',
      },
      ROBOT_GENERATE: {
        method: 'POST',
        endpoint: 'programmes/programme_ajax_create_consumer_to_generate_response',
      },
      // Genere automatiquement les reponses d'un programme pour UNE pharmacie (job async).
      // uriTemplate API: POST /programmes/generate_response_autocomplete_survey_by_pharmacy
      // (GenerateResponseControllerByPharmacy). Corps { programId, companyId }. Reponse { success, message }.
      ROBOT_GENERATE_BY_PHARMACY: {
        method: 'POST',
        endpoint: 'programmes/generate_response_autocomplete_survey_by_pharmacy',
        schema: z.object({
          programId: z.number(),
          companyId: z.number(),
        }),
      },
      // Creation d'un programme Insight. uriTemplate API: POST /programmes (ProgrammePostProcessor).
      // Reponse: Programme { id, ... } (groupe programInsight:post). `partner` n'est PAS gere a la creation.
      ADD: {
        method: 'POST',
        endpoint: 'programmes',
        schema: z
          .object({
            nom: z.string().optional(),
            nomCourt: z.string().nullable().optional(),
            debut: z.string().optional(),
            finSaisie: z.string().optional(),
            maxResponseNumber: z.number().nullable().optional(),
            country: z.string().optional(),
            pot: z.string().nullable().optional(),
            maxResponseNumberJson: z.array(z.any()).optional(),
            // Retours 05/10/2026 : grille de quotas active ou en sommeil, le Total s applique toujours (Programme::quotaEnabled).
            quotaEnabled: z.boolean().optional(),
            disabledSendMail: z.boolean().optional(),
            invoiceIfAllTask: z.boolean().optional(),
            displayLogoTargetPublic: z.boolean().optional(),
            target: z.string().nullable().optional(),
            invoiceAmountEquipe: z.number().optional(),
            numberTaskByUserTeam: z.number().optional(),
            description: z.string().optional(),
            challengeImagePath: z.string().optional(),
            independentChallengeCounter: z.boolean().optional(),
            isInvoiceAvailable: z.boolean().optional(),
            hasCustomEndConvention: z.boolean().optional(),
            customConventionSubject: z.string().optional(),
            customConventionForme: z.string().optional(),
            customConventionObjectifs: z.string().optional(),
          })
          .passthrough(),
      },
      DELETE: { method: 'DELETE', endpoint: 'programmes/:id' },
      ASSOCIATE_SURVEY: { method: 'POST', endpoint: 'programme_surveys_multi' },
      DISSOCIATE_SURVEY: { method: 'DELETE', endpoint: 'programme_surveys/:programme/:survey/' },
      // Liaison programme <-> gabarit de document signable (paramétrage convention Insight).
      SAVE_CONVENTION_BINDING: {
        method: 'PUT',
        endpoint: 'programmes/:id/convention_binding',
        schema: conventionBindingSchema,
      },
      UNLINK_CONVENTION_TEMPLATE: {
        method: 'DELETE',
        endpoint: 'programmes/:id/convention_binding',
      },
    },
    TASK: {
      DELETE: { method: 'DELETE', endpoint: 'taches/:id' },
      // Sauvegarde finale d'une tache (reponses + statut). uriTemplate API: PUT /taches/{id}/save
      // (SaveTaskController, groupe task:save). `id` resout la path variable.
      // Corps { jsonAnswers, status? } ; status defaut 'termines' cote back si != 'completed'.
      SAVE: {
        method: 'PUT',
        endpoint: 'taches/:id/save',
        schema: z.object({
          id: z.number(),
          jsonAnswers: z.string(),
          status: z.string().optional(),
        }),
      },
      // Sauvegarde intermediaire (brouillon) : PATCH /taches/{id} (operation standard, groupe task:put).
      // `id` resout la path variable, corps { jsonAnswersTmp }.
      UPDATE_TEMP: {
        method: 'PATCH',
        endpoint: 'taches/:id',
        schema: z.object({
          id: z.number(),
          jsonAnswersTmp: z.string(),
        }),
      },
      // Cree (ou recupere) une tache publique a partir d'un token de programme.
      // uriTemplate API: POST /task/public. Reponse: { id, programme: { id, displayLogoTargetPublic } }
      CREATE_PUBLIC: {
        method: 'POST',
        endpoint: 'task/public',
        schema: z.object({
          token: z.string(),
          email: z.string().nullable().optional(),
          taskId: z.number().optional(),
          // Lien PERSONNALISE (uuid) : reprise multi-appareil de la tache liee
          uid: z.string().optional(),
        }),
      },
      // Sauvegarde des reponses d'une tache en mode survey PUBLIC (non authentifie).
      // uriTemplate API: POST /task/public/{id}/answers (security PUBLIC_ACCESS, validation par token
      // chiffre dans le body — le meme token que POST /task/public). `id` resout la path variable.
      // temp:false = sauvegarde definitive (jsonAnswers + status), temp:true = brouillon (jsonAnswersTmp).
      // Reponse: Tache serialisee.
      PUBLIC_ANSWERS: {
        method: 'POST',
        endpoint: 'task/public/:id/answers',
        schema: z.object({
          token: z.string(),
          jsonAnswers: z.string().optional(),
          jsonAnswersTmp: z.string().optional(),
          temp: z.boolean().optional(),
          status: z.string().optional(),
        }),
      },
      // Lien signé pour une réponse fichier/photo en parcours public (sans JWT).
      // Le serveur choisit la clé S3 : elle reste privée, jamais une URL publique.
      PUBLIC_UPLOAD_LINK: {
        method: 'POST',
        endpoint: 'task/public/:id/upload_link',
        schema: z.object({
          id: z.number(),
          token: z.string().optional(),
          extension: z.string().optional(),
        }),
      },
    },
    CREDIT: {
      UPDATE: { method: 'PATCH', endpoint: 'credit_histories/:id' },
      // POST /credit_histories (processor CreditHistoryPostProcessor). Reponse: CreditHistory { id, ... }.
      // company/validUntil/remainingCredit/hasPaid sont calcules cote back ; on envoie amount + numberCredit.
      ADD: {
        method: 'POST',
        endpoint: 'credit_histories',
        schema: z
          .object({
            amount: z.number(),
            numberCredit: z.number().optional(),
          })
          .passthrough(),
      },
    },
    ORDER_SHOP: {
      CHANGE_STATUS: { method: 'POST', endpoint: 'order_shops/:id/changeStatusOrder' },
      GENERATE_AMAZON: { method: 'POST', endpoint: 'order_shops/:id/generateAmazonGiftCard' },
      // Ajoute un produit au panier (cache serveur). uriTemplate API: POST /order_shops/addProductToBasket.
      // Corps { productId, potId } lu par OrderShopAddProductValidController. Reponse: bool/string.
      ADD_PRODUCT: {
        method: 'POST',
        endpoint: 'order_shops/addProductToBasket',
        schema: z.object({
          productId: z.number(),
          potId: z.string(),
        }),
      },
      // Valide le panier. uriTemplate API: GetCollection /order_shops/validBasket/{pot} (OrderShopValidBasketController).
      // `pot` resout la path variable, aucun corps. Reponse: objet OrderShop / message.
      VALID_BASKET: { method: 'GET', endpoint: 'order_shops/validBasket/:pot' },
      // Genere toutes les cartes cadeaux Amazon en attente (envoi mail). Aucun corps.
      // uriTemplate API: POST /order_shops/generateAllAmazonGiftCards (GenerateAllAmazonGiftCardsController).
      GENERATE_ALL_AMAZON_GIFT_CARD: {
        method: 'POST',
        endpoint: 'order_shops/generateAllAmazonGiftCards',
      },
    },
    INVOICES: {
      // Export des reponses d'une facture : POST { output: 'api' } -> renvoie un objet
      // { keyFile, linkSign } (AwsS3Util::putObjectAndUrlSigned), l'URL signee est `linkSign`.
      // `id` resout la path variable (uriTemplate API: POST /factures/{id}/export_tasks_by_invoice).
      EXPORT_TASK_RESPONSE: {
        method: 'POST',
        endpoint: 'factures/:id/export_tasks_by_invoice',
        schema: z.object({ id: z.number(), output: z.string() }),
      },
    },
    REPARTITION: {
      DELETE: { method: 'DELETE', endpoint: 'repartition_programmes/:id' },
      ADD_PHARMACY_MULTI: { method: 'POST', endpoint: 'programme_pharmacies_repartition_multi' },
      // Augmente le montant attribue a une pharmacie sur un programme (issue #89).
      // Le montant ne peut pas etre inferieur a l'existant : l'API repartit uniquement la
      // difference en nouveaux questionnaires et rouvre la repartition (status = false),
      // il reste ensuite a cliquer sur « Valider le programme et créer les tâches ».
      INCREASE_BUDGET: {
        method: 'PATCH',
        endpoint: 'repartition_programmes/:id/increase_budget',
        schema: z.object({
          id: z.number(),
          newBudget: z.number().int().nonnegative(),
        }),
      },
      // Baisse du montant (front#755) : l'API refuse de descendre sous le cout des
      // questionnaires restants ; aucune tache ni facture n'est modifiee.
      DECREASE_BUDGET: {
        method: 'PATCH',
        endpoint: 'repartition_programmes/:id/decrease_budget',
        schema: z.object({
          id: z.number(),
          newBudget: z.number().int().nonnegative(),
        }),
      },
    },
    SURVEY: {
      EXPORT_STAT: {
        method: 'POST',
        endpoint: 'surveys/:id/exportStatSurvey',
        asyncExport: true,
      },
      // Export ZIP de toutes les reponses filtrees. uriTemplate API: POST /surveys/exportAllSurvey
      // (exportAllSurveyController). Corps { program, survey, dateStart?, dateEnd? }.
      // Reponse: URL S3 signee (string).
      EXPORT_ALL_SURVEY: {
        method: 'POST',
        endpoint: 'surveys/exportAllSurvey',
        schema: z.object({
          program: z.number().nullable().optional(),
          survey: z.number().nullable().optional(),
          dateStart: z.union([z.string(), z.date()]).nullable().optional(),
          dateEnd: z.union([z.string(), z.date()]).nullable().optional(),
        }),
      },
      DUPLICATE: { method: 'POST', endpoint: 'surveys/:id/duplicate' },
      CREATE: { method: 'POST', endpoint: 'surveys' },
      UPDATE: { method: 'PATCH', endpoint: 'surveys/:id' },
      // Publie la definition V99 courante (snapshot survey_publication).
      // uriTemplate API: POST /surveys/{id}/publish (PublishSurveyController).
      // Reponse: { id, versionNumber, publishedAt }
      PUBLISH: {
        method: 'POST',
        endpoint: 'surveys/:id/publish',
        schema: z.object({ id: z.number() }),
      },
      // Restaure la definition d'une publication dans le BROUILLON (sans republier).
      // Reponse: { restoredVersionNumber }
      RESTORE_PUBLICATION: {
        method: 'POST',
        endpoint: 'surveys/:id/restore_publication',
        schema: z.object({ id: z.number(), publicationId: z.number() }),
      },
      // Export XLSX des reponses V99 (1 ligne par tache). Reponse: { url } (S3 signee)
      V99_EXPORT: {
        method: 'POST',
        endpoint: 'surveys/:id/v99_export',
        // Filtres identiques aux stats : l'export reflete la vue filtree
        schema: z.object({
          id: z.number(),
          dateStart: z.string().optional(),
          dateEnd: z.string().optional(),
          publicationVersion: z.union([z.string(), z.number()]).optional(),
          ctxKey: z.string().optional(),
          ctxValue: z.string().optional(),
          // Statut (retours PMI) : 'all' (defaut), 'in_progress' ou 'complete'
          status: z.enum(['all', 'in_progress', 'complete']).optional(),
          // Colonnes de profil ET de contexte (prefixe `ctx:`) exportees. Un
          // tableau VIDE signifie "aucune colonne d'identification" — schema
          // OBLIGATOIRE ici : un champ non declare est silencieusement retire
          // par `schema.parse()` (cf. tanstackQuery.ts), ce qui videait ce
          // parametre avant meme l'envoi HTTP (retour PMI, etude 1667).
          columns: z.array(z.string()).optional(),
        }),
      },
      // Repondants FICTIFS "generation d'inters" (recette 14/08) :
      // {action: 'generate', count} ou {action: 'purge'}. Reponse: { testCount, ... }
      V99_TEST_DATA: {
        method: 'POST',
        endpoint: 'surveys/:id/v99_test_data',
        schema: z.object({
          id: z.number(),
          action: z.enum(['generate', 'purge']),
          count: z.number().optional(),
        }),
      },
      DELETE: { method: 'DELETE', endpoint: 'surveys/:id' },
      // Genere un lien S3 signe d'UPLOAD (PUT) pour une image d'enquete.
      // uriTemplate API: POST /surveys/{id}/create_link_aws_upload_image. `id` resout la path variable.
      // Reponse: { keyFile: string, linkSign: string }
      GET_LINK_UPLOAD: {
        method: 'POST',
        endpoint: 'surveys/:id/create_link_aws_upload_image',
        schema: z.object({
          id: z.number(),
          extension: z.string().optional(),
        }),
      },
      // Image de contenu V99 : PUT signe vers le bucket PUBLIC + URL CloudFront stable.
      // Reponse: { linkSign: string, publicUrl: string }
      V99_IMAGE_UPLOAD_LINK: {
        method: 'POST',
        endpoint: 'surveys/:id/v99_image_upload_link',
        schema: z.object({
          id: z.number(),
          extension: z.string().optional(),
        }),
      },
      // Relance IA sur question ouverte (lot C3) — public par token ou authentifié
      V99_OE_ASSIST: {
        method: 'POST',
        endpoint: 'task/public/:id/v99_oe_assist',
        schema: z.object({
          id: z.number(),
          token: z.string().optional(),
          // Le libellé n'est plus envoyé : le serveur le résout depuis la
          // définition publiée (le prompt ne doit pas venir du client).
          questionId: z.string(),
          answer: z.string(),
          language: z.string(),
        }),
      },
      // Import IA d'un questionnaire Word/PDF/texte (lot D1)
      V99_IMPORT_DOCUMENT: {
        method: 'POST',
        endpoint: 'surveys/:id/v99_import_document',
        schema: z.object({
          id: z.number(),
          language: z.string(),
          file: z.string().optional(),
          filename: z.string().optional(),
          text: z.string().optional(),
        }),
      },
      // Thématisation IA des verbatims d'une question ouverte (lot C4)
      V99_ANALYZE_VERBATIMS: {
        method: 'POST',
        endpoint: 'surveys/:id/v99_analyze_verbatims',
        schema: z.object({
          id: z.number(),
          questionId: z.string(),
          language: z.string(),
          filters: z.record(z.string(), z.string()).optional(),
        }),
      },
      // Pré-remplissage IA des traductions (lot C2 plan Forsta) — relecture humaine requise
      V99_TRANSLATE: {
        method: 'POST',
        endpoint: 'surveys/:id/v99_translate',
        schema: z.object({
          id: z.number(),
          texts: z.record(z.string(), z.string()),
          source: z.string(),
          target: z.string(),
        }),
      },
    },
    SIGNABLE_DOCUMENT_TEMPLATE: {
      CREATE: {
        method: 'POST',
        endpoint: 'signable_document_templates',
        schema: signableDocumentTemplateUpsertSchema,
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'signable_document_templates/:id',
        schema: signableDocumentTemplateUpsertSchema,
      },
      DELETE: { method: 'DELETE', endpoint: 'signable_document_templates/:id' },
      // multipart/FormData (champ `file`, .pdf) : consommé via fetch direct (fetchRest ne gère pas FormData)
      UPLOAD_PDF: { method: 'POST', endpoint: 'signable_document_templates/:id/source_pdf' },
      // Réponse binaire (application/pdf) : consommé via fetch direct + response.blob()
      PREVIEW: { method: 'POST', endpoint: 'signable_document_templates/:id/preview' },
    },
  },
  STORE: {
    COMPANY_COMPARE: {
      SYNC: { method: 'PATCH', endpoint: 'company_compare_store/:companyId/sync' },
    },
    // Catalogue « Ma centrale » du PHARMACIEN : prepare le compte client CS-Cart de la FICHE
    // (reprise du compte trouve par e-mail / CIP, sinon creation, puis groupes de la pharmacie)
    // — le geste que le SSO « Ouvrir Ma centrale » fait en silence, ici annonce a l'ecran.
    // Reponse : { status: 'linked' | 'already_linked', userIdStore }. Refus : 409 PROVISIONING_FAILED.
    PHARMACY_CATALOG: {
      PROVISION_ACCOUNT: {
        method: 'POST' as const,
        endpoint: 'pharmacy/store/account',
        schema: z.object({}),
      },
    },
    STRIPE_ACCOUNT: {
      UPDATE_LINK: { method: 'POST' as const, endpoint: 'companies/stripeAccountUpdate' },
      ACCOUNT_SESSION: { method: 'POST' as const, endpoint: 'companies/stripeAccountSession' },
      ONBOARDING_LINK: { method: 'POST' as const, endpoint: 'companies/stripeOnboarding' },
    },
    VENDOR_SUBSCRIPTION: {
      UPDATE_STATUS: {
        method: 'PUT' as const,
        endpoint: 'companies/:companyId/store_subscription_status',
      },
    },
    VENDOR_CONVERSATIONS: {
      REPLY: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_conversations/:threadId/reply',
      },
      CREATE_THREAD: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_conversations',
      },
      DELETE_ATTACHMENT: {
        method: 'DELETE' as const,
        endpoint: 'companies/vendor_conversations/:threadId/attachments/:attachmentId',
      },
    },
    CONTRACT: {
      // multipart/FormData : consommé via fetch direct (fetchRest ne gère pas FormData)
      UPLOAD: { method: 'POST' as const, endpoint: 'contracts/upload' },
    },
    VENDOR_ACCOUNTING: {
      // Export comptable XLSX du détail du solde, envoyé par e-mail (202 { queued, email })
      EXPORT: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_accounting/export',
        asyncExport: true,
      },
    },
    VENDOR_PRODUCTS: {
      CREATE: { method: 'POST' as const, endpoint: 'companies/vendor_products' },
      UPDATE: { method: 'PUT' as const, endpoint: 'companies/vendor_products/:productId' },
      AI_SUGGESTION: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_products/ai_suggestion',
      },
      IMPORT: { method: 'POST' as const, endpoint: 'companies/vendor_products_import' },
      EXPORT_ASYNC: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_products/export_async',
        asyncExport: true,
      },
    },
    VENDOR_PROMOTIONS: {
      CREATE: { method: 'POST' as const, endpoint: 'companies/vendor_promotions' },
      UPDATE: { method: 'PUT' as const, endpoint: 'companies/vendor_promotions/:promotionId' },
    },
    VENDOR_ORDERS: {
      ACTION: { method: 'PUT' as const, endpoint: 'companies/vendor_orders/:orderId' },
      UPLOAD_DOCUMENT: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_orders/:orderId/documents',
      },
      // Remet la commande en file de synchro vers l'ERP Odoo du vendeur (202 { queued: true })
      ERP_RESYNC: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_orders/:orderId/erp_resync',
      },
      // Relit tout de suite la vente Odoo liée à la commande : expéditions, remboursements,
      // factures (202 { queued: true } ; 409 no_erp_sale / return_closed / no_erp_connection)
      ERP_RETURN_SYNC: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_orders/:orderId/erp_return_sync',
      },
      // Passage forcé de la scrutation de la connexion du vendeur : { direction: 'send' | 'fetch' }
      ERP_SYNC: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_orders/erp_sync',
      },
      EXPORT_ASYNC: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_orders/export_async',
        asyncExport: true,
      },
    },
    // Gestes de l'opérateur outre-mer : doublons, aperçu et redescente, référence ERP, relecture
    OVERSEAS_ORDERS: {
      ACTION: {
        method: 'POST' as const,
        endpoint: 'companies/overseas_orders/:overseasOrderId/actions',
      },
    },
    // Modèle A : « Servir depuis la France » et « À commander » (onglet Outre-mer à servir).
    OVERSEAS_TO_SERVE: {
      ACTION: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_stock/overseas_to_serve/actions',
      },
    },
    // Gestes sur le panier du compte acheteur : démarrer, je passe commande, reprendre, arrêter
    OVERSEAS_SUPPLY: {
      ACTION: { method: 'POST' as const, endpoint: 'companies/overseas_supply' },
    },
    // « Stock et emplacements » : recalcul d'un instantané à la demande (lecture seule)
    VENDOR_STOCK: {
      REFRESH: { method: 'POST' as const, endpoint: 'companies/vendor_stock/refresh' },
      // « Réserver » / « Annuler la réservation » d'une vente France (Commandes à préparer)
      RESERVATION: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_stock/orders/reservation',
      },
    },
    // Logistique Store (front#771) : gestes (accepter, choisir l'entrepôt, expédier) et rafraîchissement
    PRODUCT_ENRICHMENT: {
      START_RUN: { method: 'POST' as const, endpoint: 'store/product_enrichment/runs' },
      DECIDE: { method: 'POST' as const, endpoint: 'store/product_enrichment/proposals/decide' },
      EDIT: {
        method: 'POST' as const,
        endpoint: 'store/product_enrichment/proposals/:proposalId/edit',
      },
      DECIDE_GAMME: { method: 'POST' as const, endpoint: 'store/product_enrichment/gammes' },
    },
    LOGISTICS: {
      ACTION: { method: 'POST' as const, endpoint: 'store/logistics/actions' },
      REFRESH: { method: 'POST' as const, endpoint: 'store/logistics/refresh' },
    },
    // Super admin : export de la base de l'outil LAP (multipart files[], storeCompanyId, mode)
    LAP_EXPORT_IMPORT: {
      POST: { method: 'POST' as const, endpoint: 'companies/lap_export_import' },
    },
    // Gestes du référentiel : bloquer, débloquer, modifier, publier, exporter ; import multipart
    VENDOR_CATALOG: {
      ACTION: { method: 'POST' as const, endpoint: 'companies/vendor_catalog' },
      IMPORT: { method: 'POST' as const, endpoint: 'companies/vendor_catalog/import' },
      LABORATORY_ACTION: { method: 'POST' as const, endpoint: 'companies/vendor_laboratories' },
    },
    VENDOR_DISPUTES: {
      ACTION: { method: 'PUT' as const, endpoint: 'companies/vendor_disputes/:disputeId' },
      UPLOAD_ATTACHMENTS: {
        method: 'POST' as const,
        endpoint: 'companies/vendor_disputes/:disputeId/attachments',
      },
      DELETE_ATTACHMENT: {
        method: 'DELETE' as const,
        endpoint: 'companies/vendor_disputes/:disputeId/attachments/:attachmentId',
      },
    },
    VENDOR_PLANNING_SHIPPINGS: {
      CREATE: { method: 'POST' as const, endpoint: 'companies/vendor_planning_shippings' },
      UPDATE: {
        method: 'PUT' as const,
        endpoint: 'companies/vendor_planning_shippings/:planningId',
      },
    },
    VENDOR_SHIPPINGS: {
      UPDATE: {
        method: 'PUT' as const,
        endpoint: 'companies/vendor_shippings/:shippingId',
      },
    },
    VENDOR_BETA: {
      EXIT: { method: 'POST' as const, endpoint: 'companies/vendor_exit_beta' },
    },
    // Ecran « Comptes en doublon » (Ma centrale) : fusion d'UNE PAIRE de comptes clients
    // CS-Cart. `from` est fusionne PUIS SUPPRIME, `to` est conserve — un POST par compte a
    // fusionner, toujours SEQUENTIELS cote ecran (jamais en parallele : le compte conserve
    // change d'etat a chaque appel). Reponse: types/store/StoreDuplicateAccount.ts
    // (StoreDuplicateAccountMergeResult). Erreurs 422 { code } : SAME_ACCOUNT | NOT_CUSTOMER |
    // USER_NOT_FOUND (compte introuvable, probablement deja fusionne) | BOTH_STRIPE_CUSTOMERS
    // (les deux comptes ont un client Stripe) | LINKED_TO_DIFFERENT_RECORDS (comptes relies a
    // des fiches/utilisateurs Group differents : traiter d'abord le doublon cote Group) ;
    // 503 { code: 'STORE_UNAVAILABLE' }.
    DUPLICATE_ACCOUNTS: {
      MERGE: {
        method: 'POST' as const,
        endpoint: 'store_duplicate_account_merges',
        schema: z.object({
          fromUserIdStore: z.number().int().positive(),
          toUserIdStore: z.number().int().positive(),
        }),
      },
    },
  },
  STOREBAG: {
    // Repartition de sachets selon la capacite (page Programmes PLV) : calcul sans rien
    // enregistrer. Reponse { url, fileName, summary } (types/storebag/CapacityAllocation.ts).
    CAPACITY_ALLOCATION: {
      RUN: {
        method: 'POST' as const,
        endpoint: 'store_bag_capacity_allocations',
        schema: z.object({
          keyFile: z.string().startsWith('store_bag/capacity_allocation/'),
          totalBags: z.number().int().positive().max(10_000_000),
          year: z.number().int().min(2020).max(2100),
        }),
      },
    },
    PRODUCT: {
      ADD_PRODUCT: {
        method: 'POST',
        endpoint: 'store_bag_products',
        schema: storeBagProductCampaignSchema,
      },
      UPDATE_PRODUCT: {
        method: 'PATCH',
        endpoint: 'store_bag_products/:productId',
        schema: storeBagProductCampaignSchema,
      },
      // Fiche de MATERIEL SOUND (nature `equipement_sound`) : seuls ces quatre champs
      // s'editent. Objet ferme : un champ PLV (partenaires, prix) ne part jamais d'ici.
      // `productId` resout la path variable sans partir dans le corps.
      UPDATE_SOUND_EQUIPMENT: {
        method: 'PATCH',
        endpoint: 'store_bag_products/:productId',
        schema: z.object({
          name: z.string().trim().min(1).max(255),
          archive: z.boolean(),
          netsuiteItemId: z.string().trim().max(100).nullable(),
          ean: z.string().trim().max(50).nullable(),
        }),
      },
      DELETE_PRODUCT: { method: 'DELETE', endpoint: 'store_bag_products/:productId' },
      // Reordonnancement drag & drop : ids dans l'ordre voulu -> position = index (pilote
      // l'ordre des colonnes produit du fichier de campagne).
      REORDER_PRODUCTS: {
        method: 'POST',
        endpoint: 'store_bag_products/reorder',
        schema: z.object({ ids: z.array(z.number().positive()).min(1) }),
      },
    },
    SHIPMENT: {
      // Export asynchrone de la liste de livraisons : XLSX genere en tache de fond (Messenger)
      // et envoye par email au demandeur. Payload = filtres de la liste.
      EXPORT: { method: 'POST' as const, endpoint: 'store_bag_shipments/export' },
    },
    DPD_INVOICE: {
      // Import de la facture mensuelle DPD : multipart (champ `file`), poste par fetch direct
      // (fetchRest serialise en JSON). Couts enregistres, rapport envoye par e-mail.
      IMPORT: { method: 'POST' as const, endpoint: 'store_bag/dpd_invoices/import' },
    },
    PROGRAM: {
      ADD_PROGRAM: { method: 'POST', endpoint: 'store_bag_programs' },
      EDIT_PROGRAM: { method: 'PATCH', endpoint: 'store_bag_programs/:programId' },
      DELETE: { method: 'DELETE', endpoint: 'store_bag_programs/:id' },
      // Export du rapport de stats. Payload { programIds: number[] }. Si non vide -> URL S3 (string),
      // sinon dispatch async (message). Analogue de SOUND.PROGRAM.STAT_RAPPORT.
      STAT_RAPPORT: { method: 'POST' as const, endpoint: 'store_bag_programs/stat_rapport' },
      // Generation auto du programme StoreBag a partir d'un CSV (ou manuel).
      // StoreBagGenerateProgrammeWithCsvController. :programId = id programme,
      // :generateType = 'generate' | 'manual' | 'simulation'. Body { keyFile? }. Reponse message (string).
      ADD_AUTO_GENERATE: {
        method: 'POST',
        endpoint: 'store_bag_programs/:programId/import_generation_auto/:generateType',
      },
      // Evaluation LIVE des garde-fous commande auto (CS-Cart/Stripe) + persistance du
      // snapshot sur les budgets du programme. Body vide ({}), :programId en path variable
      // (passe via la prop `variables` du useRestMutation). Reponse: collection de lignes
      // d'eligibilite (meme shape que GET auto_order_eligibility).
      AUTO_ORDER_ELIGIBILITY_REFRESH: {
        method: 'POST',
        endpoint: 'store_bag_programs/:programId/auto_order_eligibility_refresh',
        // `ids` optionnel : restreint la re-evaluation aux budgets coches (sinon programme entier).
        schema: z.object({ ids: z.array(z.number()).optional() }),
      },
      // Liaison programme <-> gabarit de document signable (paramétrage convention StoreBag).
      SAVE_CONVENTION_BINDING: {
        method: 'PUT',
        endpoint: 'store_bag_programs/:programId/convention_binding',
        schema: conventionBindingSchema,
      },
      UNLINK_CONVENTION_TEMPLATE: {
        method: 'DELETE',
        endpoint: 'store_bag_programs/:programId/convention_binding',
      },
    },
    PHARMACY: {
      // `storeBagPharmacyId` resout la path variable. `acceptAutoOrder` est le seul champ
      // patchable (groupe storeBagPharmacy:write). Le serveur exige un mandat SEPA valide
      // pour passer a true : 422 { code: 'MANDATE_REQUIRED' } / 503 { code: 'MANDATE_STATUS_UNAVAILABLE' }.
      UPDATE: {
        method: 'PATCH',
        endpoint: 'store_bag_pharmacies/:storeBagPharmacyId',
        schema: z.object({
          storeBagPharmacyId: z.number(),
          acceptAutoOrder: z.boolean(),
        }),
      },
    },
    MANDATE: {
      // Lien Stripe Checkout (mode setup) pour signer le mandat SEPA Pharmedistore.
      // Reponse { url }. `redirectRoute` (whiteliste serveur) ramene l'utilisateur
      // sur l'extranet apres signature.
      // Cree le compte Ma centrale s'il manque, puis renvoie le statut du mandat.
      // Idempotent. Appele au choix « commande automatique » — le GET du statut, lui,
      // ne cree rien. Erreur 422 { code: 'NO_STORE_ACCOUNT' } si CS-Cart a refuse.
      ENSURE_STORE_ACCOUNT: {
        method: 'POST',
        endpoint: 'store_bag/mandate/ensure_account',
        schema: z.object({}),
      },
      CREATE_SETUP_LINK: {
        method: 'POST',
        endpoint: 'store_bag/mandate/setup_link',
        schema: z.object({ redirectRoute: z.enum(['storebag_dialog']).optional() }),
      },
      // Creation server-side du mandat SEPA depuis un IBAN (ou depuis la banque de la
      // fiche entreprise si useCompanyBank=true — IBAN relu cote serveur, jamais envoye).
      // `consent: true` obligatoire. `replace: true` uniquement apres confirmation
      // explicite (422 { code: 'ACTIVE_MANDATE_EXISTS' } au premier POST).
      // Autres erreurs 422: IBAN_INVALID | CONSENT_REQUIRED | SEPA_DISABLED |
      // MANDATE_DECLINED (Stripe refuse ce compte : autre RIB) ;
      // 503 { code: 'MANDATE_STATUS_UNAVAILABLE' }.
      CREATE_FROM_IBAN: {
        method: 'POST',
        endpoint: 'store_bag/mandate/from_iban',
        schema: z.object({
          useCompanyBank: z.boolean(),
          iban: z.string().optional(),
          bic: z.string().optional(),
          consent: z.literal(true),
          replace: z.boolean().optional(),
        }),
      },
    },
    AUTO_ORDER: {
      // Recopie l'adresse de la fiche entreprise vers le compte Pharmedistore (billing +
      // shipping) et renvoie l'etat des garde-fous reevalue. Corps vide.
      // Erreur 422 { code: 'ADDRESS_SYNC_FAILED' }.
      FIX_STORE_ADDRESS: {
        method: 'POST',
        endpoint: 'store_bag/auto_order_check/fix_address',
        schema: z.object({}),
      },
      // Reevalue ET persiste les garde-fous des budgets en cours. A appeler quand le
      // mandat vient de devenir valide par un chemin qui ne passe par aucun de nos
      // endpoints — signature Stripe Checkout, qui revient par redirection navigateur.
      REFRESH_SELF_CHECK: {
        method: 'POST',
        endpoint: 'store_bag/auto_order_check/refresh',
        schema: z.object({}),
      },
    },
    PROGRAM_PHARMACY_BUDGET: {
      // Ajout multiple de pharmacies (avec budget) a un programme — back: postMultiPharmaciesProgramController.
      // Payload: { programmeId, pharmacies: [{ companyId, storeBagBudget }] }. user deduit cote back.
      ADD_MULTI: { method: 'POST', endpoint: 'store_bag_program_pharmacy_budgets_multi' },
      DELETE: { method: 'DELETE', endpoint: 'store_bag_program_pharmacy_budgets/:id' },
      // Refus / acceptation UNITAIRES du pharmacien retires le 22/09/2026 (D-13) : la
      // decision se prend dans « Mes partenaires » (GROUP.PARTNER_CAMPAIGN.PARTICIPATION_REJECT).
      // Annulation admin d'un refus : endpoint dedie JOURNALISE (v2) — remplace l'ancien
      // PATCH generique qui perdait la date du refus. Reponse: ParticipationInfo recalcule.
      PARTICIPATION_CANCEL_REJECT: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/:budgetId/participation_cancel_reject',
        schema: z.object({}),
      },
      // Refus saisi par un admin pour le compte de la pharmacie (D-14), y compris apres
      // expedition : motif obligatoire, journalise au nom de l'admin.
      PARTICIPATION_ADMIN_REJECT: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/:budgetId/participation_admin_reject',
        schema: z.object({ reason: z.enum(PARTICIPATION_REJECT_REASONS) }),
      },
      // Forcage admin « expedier sans contrat » au cas par cas (journalise, D-04).
      FORCE_NO_CONTRACT: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/:budgetId/force_no_contract',
        schema: z.object({ enabled: z.boolean() }),
      },
      // Meme forcage EN MASSE (operation multiple de l'ecran participants) : un seul appel
      // pour toute la selection, meme regle et meme journalisation cote serveur.
      FORCE_NO_CONTRACT_BULK: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/force_no_contract_bulk',
        schema: z.object({
          ids: z.array(z.number().int().positive()).min(1),
          enabled: z.boolean(),
        }),
      },
      // Derogation au depassement de CAPACITE DE DISTRIBUTION d'un participant (front#759) :
      // motif obligatoire, journalisee. `enabled: false` retire la derogation.
      // Reponse { capacityOverrideActive, capacityBlocking }.
      CAPACITY_OVERRIDE: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/:budgetId/capacity_override',
        schema: z.union([
          z.object({ reason: z.string().trim().min(5).max(1000) }),
          z.object({ enabled: z.literal(false) }),
        ]),
      },
      // Meme derogation EN MASSE (barre de selection de l'ecran participants), meme motif.
      // Reponse { requested, changed }.
      CAPACITY_OVERRIDE_BULK: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/capacity_override_bulk',
        schema: z.object({
          ids: z.array(z.number().int().positive()).min(1),
          reason: z.string().trim().min(5).max(1000),
        }),
      },
      // Endpoint custom (operation GET cote API Platform) : declenche la maj du statut de
      // la commande CS-Cart pour le budget pharmacie. Le path :id correspond au budget row id.
      UPDATE_STATUS_STORE: {
        method: 'GET',
        endpoint: 'store_bag_program_pharmacy_budgets/update_status_store/:id',
      },
      // Generation FORCEE des commandes auto CS-Cart pour une selection de budgets (admin).
      // Force le MODE d'eligibilite (selection manuelle) mais PAS les garde-fous
      // (contrats labo / compte store / adresse / mandat SEPA), qui restent bloquants.
      // Reponse: { dispatched, blocked: [{budgetId, reasons}], skipped: [{budgetId, reason}] }.
      GENERATE_AUTO_ORDERS: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/generate_orders',
        schema: z.object({ ids: z.array(z.number()).min(1) }),
      },
      // Generation SYNCHRONE d'UN budget (action de ligne) : la commande CS-Cart est creee
      // dans la requete et le verdict reel est retourne
      // ({ status: generated|blocked|skipped|error, orderIdStore?, reasons?, reason?, error? }).
      GENERATE_ORDER_NOW: {
        method: 'POST',
        endpoint: 'store_bag_program_pharmacy_budgets/:budgetId/generate_order_now',
        schema: z.object({ budgetId: z.number() }),
      },
    },
    // Ecran « Commandes automatiques » : generation groupee PAR PHARMACIE — toutes les
    // campagnes a commander d'un CIP dans UNE commande CS-Cart (un prelevement SEPA, un
    // frais de preparation), au lieu d'une commande par campagne.
    AUTO_ORDER_PHARMACY: {
      // Selection de PLUSIEURS pharmacies : une commande groupee par pharmacie, dispatchee
      // en file. `partnerIds` (facultatif) ne commande que les campagnes de ces laboratoires ;
      // une pharmacie qui n'en a aucune n'est pas dispatchee. Reponse: { dispatched, companyIds }.
      GENERATE_ORDERS: {
        method: 'POST',
        endpoint: 'store_bag_auto_order_pharmacies/generate_orders',
        schema: z.object({
          companyIds: z.array(z.number()).min(1),
          partnerIds: z.array(z.number()).optional(),
        }),
      },
      // UNE pharmacie, SYNCHRONE : la commande est creee dans la requete et le verdict reel
      // est retourne ({ orderIdStore, generated, blocked, skipped, errors }). `companyId`
      // resout la path variable ; `budgetIds` (facultatif) restreint aux campagnes choisies,
      // `partnerIds` (facultatif) aux campagnes de ces laboratoires.
      GENERATE_ORDERS_NOW: {
        method: 'POST',
        endpoint: 'store_bag_auto_order_pharmacies/:companyId/generate_orders_now',
        schema: z.object({
          companyId: z.number(),
          budgetIds: z.array(z.number()).optional(),
          partnerIds: z.array(z.number()).optional(),
        }),
      },
      // Re-evaluation des garde-fous des campagnes a commander d'une selection de pharmacies,
      // SANS rien commander. Appel LENT (CS-Cart + Stripe par pharmacie) : selection bornee.
      // Reponse: { refreshed, companyIds }.
      REFRESH_ELIGIBILITY: {
        method: 'POST',
        endpoint: 'store_bag_auto_order_pharmacies/refresh_eligibility',
        schema: z.object({ companyIds: z.array(z.number()).min(1) }),
      },
    },
    PROGRAM_PRODUCT_PHARMACY: {
      DELETE: { method: 'DELETE', endpoint: 'store_bag_program_product_pharmacies/:id' },
      // Ajout multiple de produits a une pharmacie — StoreBagProgramProductPharmacyController.
      // Body { storeBagProgrammeProducts: number[], pharmacieBudgetId: number, numberProduct: number }.
      ADD_MULTI: {
        method: 'POST',
        endpoint: 'store_bag_program_product_pharmacies_multi',
      },
    },
    PROGRAM_PRODUCT: {
      ASSOCIATE_MULTI: { method: 'POST', endpoint: 'store_bag_program_products_multi' },
      DISSOCIATE: { method: 'DELETE', endpoint: 'store_bag_program_products/:programProductId' },
    },
  },
  SCHOOL: {
    // Ouvre la case du jour : le gain est tire par le serveur, rien ne part du front.
    ADVENT_CALENDAR: {
      OPEN: {
        method: 'POST',
        endpoint: 'school/advent_calendar/open',
        schema: z.object({}),
      },
    },
    // Modération groupée des commentaires (pilule « À modérer » de Participants), admin School :
    // publier pose le modérateur, refuser efface aussi le texte ; la note n'est jamais touchée.
    // 200 lignes au plus, comme SchoolCommentModerationService::MAX_LINES.
    COMMENT_MODERATION: {
      method: 'POST',
      endpoint: 'school/comment_moderation',
      schema: z.object({
        ids: z.array(z.number().int().positive()).min(1).max(200),
        decision: z.enum(['publish', 'reject']),
      }),
    },
    // Lot d'evenements du parcours : repond toujours 202, le suivi ne casse jamais un parcours.
    EVENTS: {
      RECORD: {
        method: 'POST',
        endpoint: 'school/events',
        schema: z.object({
          events: z
            .array(
              z.object({
                type: z.enum([
                  'list_impression',
                  'training_view',
                  'video_play',
                  'video_progress',
                  'video_end',
                  'search',
                  'cooptation_view',
                  'cooptation_click',
                  'cooptation_invite_sent',
                ]),
                clientEventId: z.string(),
                sessionId: z.string(),
                device: z.string(),
                trainingId: z.number().int().positive().optional(),
                moduleId: z.number().int().positive().optional(),
                source: z.string().optional(),
                position: z.number().int().nonnegative().optional(),
                percent: z.number().int().optional(),
                query: z.string().max(200).optional(),
                resultsCount: z.number().int().nonnegative().optional(),
              })
            )
            .max(50),
        }),
      },
    },
    // Export XLSX du tableau de decision, livre par e-mail au demandeur.
    ANALYTICS: {
      EXPORT: {
        method: 'POST',
        endpoint: 'school/training_analytics/export',
        schema: z.object({
          from: z.string().optional(),
          to: z.string().optional(),
          companyId: z.number().int().positive().optional(),
          category: z.string().optional(),
        }),
        asyncExport: true,
      },
      // Personnes ciblées par une formation, nominatif : admins seulement, 403 pour un laboratoire.
      TARGETS_EXPORT: {
        method: 'POST',
        endpoint: 'school/training_analytics/:id/targets_export',
        schema: z.object({ id: z.number().int().positive() }),
        asyncExport: true,
      },
      // Pharmacies « Actif School » : admins seulement.
      ACTIVE_EXPORT: {
        method: 'POST',
        endpoint: 'school/active_pharmacies/export',
        schema: z.object({
          country: z.enum(['FR', 'BE']).optional(),
          period: z.enum(['all', '12m', '6m']).optional(),
        }),
        asyncExport: true,
      },
    },
    // Suivi des formations d'un groupement : la vue et les filtres de l'écran. Le serveur rejoue le périmètre.
    GROUPEMENT_TRACKING: {
      EXPORT: {
        method: 'POST',
        endpoint: 'school/groupement_tracking/export',
        schema: z.object({
          groupements: z.array(z.number().int().positive()).optional(),
          from: z.string().optional(),
          to: z.string().optional(),
          laboratory: z.number().int().positive().optional(),
          training: z.number().int().positive().optional(),
          state: z.enum(['online', 'ended', 'archived']).optional(),
          view: z.enum(['training', 'laboratory', 'pharmacy']),
        }),
        asyncExport: true,
      },
    },
    TRAINING: {
      CREATE: {
        method: 'POST',
        endpoint: 'trainings',
        schema: z
          .object({
            title: z.string().optional(),
            description: z.string().optional(),
            category: z.string().optional(),
            companyName: z.string().optional(),
            followUpTrainingId: z.number().int().nonnegative().nullable().optional(),
            followUpMessage: z.string().max(500).nullable().optional(),
          })
          .passthrough(),
      },
      UPDATE: {
        method: 'PATCH',
        // myTrainingProprietary=true : autorise le proprietaire (industry) a editer sa formation
        // (y compris brouillon) sans heriter du scope d'eligibilite des listes. Cf. resolver
        // GraphQL legacy updateTraining qui portait deja ce flag. Sans lui : 404 au read item.
        endpoint: 'trainings/:id?myTrainingProprietary=true',
        schema: z
          .object({
            title: z.string().optional(),
            description: z.string().optional(),
            status: z.string().optional(),
            endDate: z.union([z.string(), z.date()]).nullable().optional(),
            pointValue: z.union([z.string(), z.number()]).optional(),
            choiceSupport: z.string().optional(),
            followUpTrainingId: z.number().int().nonnegative().nullable().optional(),
            followUpMessage: z.string().max(500).nullable().optional(),
          })
          .passthrough(),
      },
      // myTrainingProprietary=true : meme raison que UPDATE (cf. resolver GraphQL deleteTraining).
      DELETE: { method: 'DELETE', endpoint: 'trainings/:id?myTrainingProprietary=true' },
      // Copie en brouillon : le droit (sa formation, ou un admin dans ses pays) est rejoué par l'API.
      DUPLICATE: { method: 'POST', endpoint: 'trainings/:id/duplicate' },
      ASSIGN: {
        method: 'PATCH',
        endpoint: 'trainings/assign/:id',
        schema: z
          .object({
            trainingsAssigned: z.array(z.string()),
            scheduledAt: z.union([z.string(), z.date()]).nullable().optional(),
            // Officine du réseau d'un manager de chaîne, bornée côté API.
            companyId: z.number().optional(),
          })
          .passthrough(),
      },
      ASSIGN_HIDDEN: {
        method: 'PATCH',
        endpoint: 'trainings/assign_hidden/:id',
        schema: z.object({}).passthrough(),
      },
      ADD_SUPPORT: {
        method: 'POST',
        // myTrainingProprietary=true : meme raison que UPDATE (cf. resolver GraphQL addTrainingSupport).
        endpoint: 'trainings/support/:id?myTrainingProprietary=true',
        schema: z.object({}).passthrough(),
      },
      DELETE_SUPPORT: { method: 'DELETE', endpoint: 'training_supports/:id' },
      // Operation Post : uriTemplate `/trainings/ask_create_video/{id}` (TrainingAskCreateVideoController).
      ASK_CREATE_VIDEO: { method: 'POST', endpoint: 'trainings/ask_create_video/:id' },
      // Operation Patch : uriTemplate `/trainings/follow/{id}` (TrainingFollowUsersController). Body { remove: boolean }.
      FOLLOW: {
        method: 'PATCH',
        endpoint: 'trainings/follow/:id',
        schema: z.object({ remove: z.boolean() }).passthrough(),
      },
    },
    TRAINING_SUPPORT: {
      UPDATE: { method: 'PATCH', endpoint: 'training_supports/:id' },
      // Operation Get cote API (new Get sur TrainingSupport, TrainingSupportCreateTaskController).
      // Action sans corps : la cle `id` resout :id. Reponse { taskId, trainingRecipient }.
      CREATE_TASK: { method: 'GET', endpoint: 'training_supports/:id/create_task' },
    },
    ASK_ACCESS_TRAINING: {
      EXPORT: { method: 'GET', endpoint: 'trainings/ask_access_export' },
      UPDATE: { method: 'PATCH', endpoint: 'school_ask_access_trainings/:id' },
      // Réponse automatique, réglée formation par formation. `id` résout :id.
      AUTO: {
        method: 'POST',
        endpoint: 'trainings/:id/ask_access_auto',
        schema: z.object({ id: z.number(), enabled: z.boolean() }),
      },
      // Accepte toutes les demandes en attente d'une formation. Réponse { accepted }.
      ACCEPT_ALL: {
        method: 'POST',
        endpoint: 'trainings/:id/ask_access_accept_all',
        schema: z.object({ id: z.number() }),
      },
      // Operation Post par defaut : uriTemplate `/school_ask_access_trainings` (SchoolAskAccessTrainingPostProcessor).
      // Body { trainingSupport: IRI, commentAskAccess?: string }.
      ADD: {
        method: 'POST',
        endpoint: 'school_ask_access_trainings',
        schema: z
          .object({
            trainingSupport: z.string(),
            commentAskAccess: z.string().nullable().optional(),
          })
          .passthrough(),
      },
    },
    TRAINING_SYNTHESIS: {
      EXPORT: { method: 'GET', endpoint: 'trainings/training_synthesis_export' },
    },
    TRAINING_RECIPIENT: {
      EXPORT: { method: 'GET', endpoint: 'trainings/trainings_recipient_export' },
      UPDATE: { method: 'PATCH', endpoint: 'trainings_recipients/:id' },
      // Correction par l'admin School depuis la modération : même opération que l'apprenant, mais
      // TrainingRecipientsItemProcessor pose l'admin en modérateur, donc la ligne est publiée.
      // Note en chaîne ('' = sans note), comme l'Assert\Choice de TrainingsRecipient::$rating.
      MODERATION_EDIT: {
        method: 'PATCH',
        endpoint: 'trainings_recipients/:id',
        schema: z.object({
          rating: z.enum(['', '1', '2', '3', '4', '5']),
          comment: z.string().trim().min(1).max(5000),
        }),
      },
      REMIND: { method: 'PATCH', endpoint: 'trainings_recipients/remind/:id' },
    },
    // Operation Patch sur User : uriTemplate `/users/{id}/follow_industry` (UserFollowIndustryPatchProcessor).
    // Body { company: { id }, followed: boolean } (followed=true => unfollow, voir processor).
    FOLLOW_INDUSTRY: {
      method: 'PATCH',
      endpoint: 'users/:id/follow_industry',
      schema: z
        .object({
          company: z.object({ id: z.union([z.string(), z.number()]) }),
          followed: z.boolean(),
        })
        .passthrough(),
    },
    TASK: {
      PARSE_ANSWERS: { method: 'POST', endpoint: 'taches/parse_answers' },
      DELETE: { method: 'DELETE', endpoint: 'taches/:id' },
    },
  },
  CARE: {
    // Partagé par l'activation du pharmacien et la fiche Care du back-office. L'API ignore
    // les champs autres que `activeCare` sans ROLE_ADMIN.
    ACTIVE_PHARMACY: {
      method: 'PUT',
      endpoint: 'company/:id/activeCare',
      schema: z.object({
        activeCare: z.boolean(),
        creditPlan: z.enum(['paid', 'free']).optional(),
        campaignCreditPlan: z.enum(['paid', 'free']).optional(),
        sevrageTabagiqueEnabled: z.boolean().optional(),
      }),
    },
    // Le schema du CREATE vit dans `components/Care/Campaign/careCampaign.schema.ts` et est
    // passe par le hook : il est partage avec la validation du formulaire de creation.
    CAMPAIGN: {
      CREATE: { method: 'POST', endpoint: 'care/campaigns' },
      UPDATE: {
        method: 'PATCH' as const,
        endpoint: 'care/campaigns/:campaignId',
        // Corps volontairement restreint aux trois champs editables. Zod retire les cles
        // inconnues : un `id` ou un `status` glisse par erreur dans le payload d'un
        // merge-patch serait interprete comme une modification de la ressource.
        schema: z.object({
          audienceRules: z.record(z.string(), z.unknown()),
          subject: z.string().max(200).nullable().optional(),
          body: z.string().nullable().optional(),
        }),
      },
      DELETE: {
        method: 'DELETE' as const,
        endpoint: 'care/campaigns/:campaignId',
        schema: z.object({ campaignId: z.number().int().positive() }),
      },
      // Actions sans payload : l'identifiant est dans l'URI, le corps est vide. Pas de schema
      // a poser, il n'y a rien a valider.
      RESOLVE_AUDIENCE: { method: 'POST', endpoint: 'care/campaigns/:campaignId/audience' },
      TEST_SEND: {
        method: 'POST' as const,
        endpoint: 'care/campaigns/:campaignId/test-send',
        // Chaque cible est un envoi reellement expedie et facture : l'API en accepte cinq au
        // maximum, autant refuser un payload aberrant avant de partir.
        schema: z.object({
          targets: z.array(z.string().trim().min(1)).min(1).max(5),
        }),
      },
      SEND: { method: 'POST', endpoint: 'care/campaigns/:campaignId/send' },
      CANCEL: { method: 'POST', endpoint: 'care/campaigns/:campaignId/cancel' },
      RETRY_FAILED: { method: 'POST', endpoint: 'care/campaigns/:campaignId/retry-failed' },
    },
    ACTOR: {
      AUTHENTICATE: { method: 'POST' as const, endpoint: 'care/actor/authenticate' },
      SET_PIN: { method: 'POST' as const, endpoint: 'care/actor/set-pin' },
      ADD_MEMBER: { method: 'POST' as const, endpoint: 'care/actor/add-member' },
    },
    RESOURCE: {
      CREATE: { method: 'POST' as const, endpoint: 'care/resource' },
      GET_UPLOAD_URL: { method: 'POST', endpoint: 'care/resource/upload-url' },
    },
    VITALE: {
      SCAN: { method: 'POST', endpoint: 'care/vitale-scan' },
      EVENT: { method: 'POST', endpoint: 'care/vitale-event' },
      READ_REQUEST: { method: 'POST', endpoint: 'care/vitale-read-request' },
    },
    // Version du parcours vaccination servie à l'officine (CompanyCare.vaccinationVersion).
    VACCINATION: {
      SAVE_SETTINGS: { method: 'PUT', endpoint: 'care/vaccination/settings' },
      // Relecture d'une règle du registre (super admin) : `fingerprint` = empreinte affichée,
      // l'API répond 409 si la règle a changé depuis. Commentaire obligatoire pour « à corriger ».
      RULE_REVIEW: {
        method: 'POST' as const,
        endpoint: 'care/vaccination/rules/validations',
        schema: z
          .object({
            ruleId: z.string().min(1),
            fingerprint: z.string().min(1),
            decision: z.enum(['validated', 'rejected']),
            comment: z.string().trim().max(2000).nullable(),
          })
          .refine(v => v.decision === 'validated' || !!v.comment, {
            message: 'comment required',
            path: ['comment'],
          }),
      },
      // Test manuel d'une règle (super admin) : « testé tel jour, à telle heure, ça
      // fonctionnait / ça ne fonctionnait pas ». N'entre pas dans le statut de la règle.
      // Commentaire obligatoire pour un échec, date du test jamais dans le futur.
      RULE_TEST: {
        method: 'POST' as const,
        endpoint: 'care/vaccination/rules/tests',
        schema: z
          .object({
            ruleId: z.string().min(1),
            fingerprint: z.string().min(1),
            result: z.enum(['passed', 'failed']),
            testedAt: z.string().datetime({ offset: true }),
            comment: z.string().trim().max(2000).nullable(),
          })
          .refine(v => v.result === 'passed' || !!v.comment, {
            message: 'comment required',
            path: ['comment'],
          }),
      },
      // Carnet de vaccination du dossier (saisie manuelle).
      DOSSIER_CREATE: { method: 'POST', endpoint: 'care/dossier/:dossierId/vaccination' },
      DOSSIER_UPDATE: { method: 'PATCH', endpoint: 'care/dossier/:dossierId/vaccination/:id' },
      DOSSIER_DELETE: { method: 'DELETE', endpoint: 'care/dossier/:dossierId/vaccination/:id' },
      // Signature enregistrée de l'acteur Care courant, pour les bons de prise en charge.
      SAVED_SIGNATURE_SAVE: { method: 'PUT', endpoint: 'care/me/signature' },
      SAVED_SIGNATURE_DELETE: { method: 'DELETE', endpoint: 'care/me/signature' },
    },
    TICKETS: {
      CREATE: { method: 'POST', endpoint: 'user/tickets' },
      UPDATE_STATUS: { method: 'PATCH', endpoint: 'user/tickets/:ticketId' },
      SEND_MESSAGE: { method: 'POST', endpoint: 'crm_task_messages' },
      ADD_FILE: { method: 'POST', endpoint: 'crm_task_message_files' },
      GET_UPLOAD_URL: { method: 'POST', endpoint: 'crm-task-message-files/upload-url' },
      MARK_READ: { method: 'POST', endpoint: 'crm_tasks/:ticketId/mark-read' },
      // Generation IA d'une reponse de support (cote admin, sans limite). Body: { mode, instruction?, language? }
      DRAFT_REPLY: { method: 'POST', endpoint: 'crm_tasks/:ticketId/draft-reply' },
    },
    FACTURATION: {
      UPDATE: { method: 'PATCH', endpoint: 'care/facturation/:id' },
      SEND_TO_LGO: { method: 'POST', endpoint: 'care/facturation/:id/send-to-lgo' },
      SEND_ALL_TO_LGO: { method: 'POST', endpoint: 'care/facturation/send-all-to-lgo' },
    },
    // Tunnel public /patient/bilan-de-prevention : aucune authentification, la session est
    // identifiee par son token de reprise place dans le chemin.
    PUBLIC_BP: {
      CREATE_SESSION: { method: 'POST' as const, endpoint: 'public/care/bilan-prevention/session' },
      SAVE_DRAFT: {
        method: 'PATCH' as const,
        endpoint: 'public/care/bilan-prevention/session/:token/draft',
      },
      SUBMIT: {
        method: 'POST' as const,
        endpoint: 'public/care/bilan-prevention/session/:token/submit',
      },
      VITALE_OCR: {
        method: 'POST' as const,
        endpoint: 'public/care/bilan-prevention/session/:token/vitale-ocr',
      },
      SET_IDENTITY: {
        method: 'PATCH' as const,
        endpoint: 'public/care/bilan-prevention/session/:token/identity',
      },
    },
    // Envoi d'ordonnance par QR code (/patient/ordonnance?cip=…). Multipart : `cip` et `file`,
    // plus nom, prénom et date de naissance sans compte. Le jeton patient, s'il y en a un, rattache
    // l'ordonnance au dossier. Envoyé par sendPublicOrdonnance (lib/patient/ordonnanceDeposit.ts) :
    // fetchRest ne sait sérialiser que du JSON. Le schéma valide les champs texte.
    PUBLIC_ORDONNANCE: {
      DEPOSIT: {
        method: 'POST' as const,
        endpoint: 'public/care/ordonnance',
        schema: publicOrdonnanceDepositSchema,
      },
    },
    // INSi phase 1 : persistance de l'INS acquis/verifie sur le dossier Care.
    // PATCH /care/dossier/{id}/ins (merge-patch). `:id` resolu via le champ `id`
    // passe au mutateAsync. Corps : { result, signature, channel } (l'API re-verifie
    // le HMAC `signature` sur `result`). Le flux d'acquisition (Vitale/traits/verification)
    // est orchestre par useVerifierIdentite (lib/Ameli/INSi) via le socle useAmeliOperation.
    INS: {
      UPDATE: {
        method: 'PATCH',
        endpoint: 'care/dossier/:id/ins',
        schema: careDossierInsPatchSchema,
      },
      // Deduplication forte avant creation par recherche INSi (F7).
      // POST /care/dossier/lookup-by-ins : 200 { dossier, eligibleMissionIds } ou 204.
      DOSSIER_LOOKUP_BY_INS: {
        method: 'POST',
        endpoint: 'care/dossier/lookup-by-ins',
        schema: lookupByInsBodySchema,
      },
      // POST /care/dossier/lookup-by-traits : 200 { dossiers: [...] } (0..N homonymes).
      DOSSIER_LOOKUP_BY_TRAITS: {
        method: 'POST',
        endpoint: 'care/dossier/lookup-by-traits',
        schema: lookupByTraitsBodySchema,
      },
      // Creation EXPLICITE d'un dossier a partir d'un resultat INSi par traits (F7/F8).
      // POST /care/dossier/from-ins : corps signe { result, signature, expiresAt }.
      FROM_INS: {
        method: 'POST',
        endpoint: 'care/dossier/from-ins',
        schema: fromInsBodySchema,
      },
    },
    // Switch « Envoi automatique au DMP » du dossier patient (LOT 2a, EX_GEN-1060). ACTIF par
    // defaut. Refuse (409) une ACTIVATION si le patient s'oppose a l'alimentation de son DMP.
    DMP_ALIMENTATION_AUTO: {
      UPDATE: {
        method: 'PATCH',
        endpoint: 'care/dossier/:id/dmp-alimentation-auto',
        schema: dmpEnvoiAutomatiquePatchSchema,
      },
    },
    // Recherche de dossiers par carte Vitale multi-NIR (F6).
    // POST /care/dossier/lookup-by-vitale : 200 { dossiers: [{ dossier, eligibleMissionIds }] }.
    VITALE_LOOKUP: {
      DOSSIER_LOOKUP_BY_VITALE: {
        method: 'POST',
        endpoint: 'care/dossier/lookup-by-vitale',
        schema: lookupByVitaleBodySchema,
      },
    },
    PATIENT: {
      CREATE_APPOINTMENT: { method: 'POST', endpoint: 'care-patient/appointment' },
      SUBMIT_SURVEY_INVITATION: {
        method: 'PATCH',
        endpoint: 'care-patient/survey-invitation/:invitationId',
      },
      SAVE_DRAFT_SURVEY_INVITATION: {
        method: 'PATCH',
        endpoint: 'care-patient/survey-invitation/:invitationId/draft',
      },
      // Aucun payload : le serveur dérive la tranche d'âge et la mission du dossier authentifié.
      START_BILAN_PREVENTION: {
        method: 'POST' as const,
        endpoint: 'care-patient/survey-invitation/bilan-prevention',
      },
      // Rattache au dossier tout neuf l'auto-questionnaire rempli avant l'inscription.
      CLAIM_BILAN_PREVENTION: {
        method: 'POST' as const,
        endpoint: 'care-patient/bilan-prevention/claim',
      },
      DELETE_ACCOUNT: { method: 'DELETE', endpoint: 'care-patient/dossier' },
      ACCEPT_DATA_CONSENT: { method: 'POST' as const, endpoint: 'care-patient/data-consent' },
      ORDONNANCE_UPLOAD: { method: 'POST' as const, endpoint: 'care-patient/ordonnance' },
      ORDONNANCE_DELETE: { method: 'DELETE' as const, endpoint: 'care-patient/ordonnance/:id' },
      ELEVENLABS_SIGNED_URL: {
        method: 'POST' as const,
        endpoint: 'care-patient/elevenlabs/signed-url',
      },
    },
    IMPORT: {
      SCAN: { method: 'POST', endpoint: 'care/import/scan' },
      DRY_RUN: { method: 'POST', endpoint: 'care/import/dry-run' },
      EXECUTE: { method: 'POST', endpoint: 'care/import/execute' },
    },
    PRODUCT_IMPORT: {
      SCAN: { method: 'POST', endpoint: 'care/product-import/scan' },
      DRY_RUN: { method: 'POST', endpoint: 'care/product-import/dry-run' },
      EXECUTE: { method: 'POST', endpoint: 'care/product-import/execute' },
    },
    EXPORT: {
      INIT: { method: 'POST', endpoint: 'care/export' },
    },
    BDPM: {
      MATCH: { method: 'POST', endpoint: 'care/bdpm/match' },
    },
    TRAITEMENT: {
      CONFIRM_IMPORT: {
        method: 'PATCH',
        endpoint: 'care/dossier/:dossierId/traitement/:id/confirm-import',
      },
      DECLINE_IMPORT: {
        method: 'PATCH',
        endpoint: 'care/dossier/:dossierId/traitement/:id/decline-import',
      },
    },
    CONSENT: {
      SIGN_TOUCH: { method: 'POST' as const, endpoint: 'care/dossier/consent/sign-touch' },
      SEND_SMS_LINK: { method: 'POST' as const, endpoint: 'care/dossier/consent/send-sms-link' },
      UPLOAD: { method: 'POST' as const, endpoint: 'care/dossier/consent/upload' },
    },
    PUBLIC_CONSENT: {
      SIGN_VIA_LINK: {
        method: 'POST' as const,
        endpoint: 'public/consent/sign-link/:token/sign',
      },
    },
    // Enregistre l'opposition du patient. Meme URI que le GET d'information
    // (ApiQueryEndpoint.PUBLIC_CARE_UNSUBSCRIBE.INFO) : seule la methode change.
    PUBLIC_CAMPAIGN: {
      UNSUBSCRIBE: {
        method: 'POST' as const,
        endpoint: 'public/care/unsubscribe/:token',
      },
    },
    // Notes libres du pharmacien sur un dossier patient (contenu chiffré côté API, jamais
    // visible du patient). Une note n'est modifiable et supprimable que par son auteur.
    DOSSIER_NOTE: {
      CREATE: {
        method: 'POST' as const,
        endpoint: 'care/dossier/:dossierId/note',
        schema: z.object({ content: z.string().trim().min(1).max(5000) }),
      },
      UPDATE: {
        method: 'PATCH' as const,
        endpoint: 'care/dossier/:dossierId/note/:id',
        schema: z.object({ content: z.string().trim().min(1).max(5000) }),
      },
      DELETE: { method: 'DELETE' as const, endpoint: 'care/dossier/:dossierId/note/:id' },
    },
    ADMIN: {
      ANONYMIZE_DOSSIER: { method: 'DELETE', endpoint: 'care/dossier/:id' },
      CREATE_SURVEY_INVITATION: { method: 'POST', endpoint: 'care/survey-invitation' },
      CANCEL_SURVEY_INVITATION: { method: 'PATCH', endpoint: 'care/survey-invitation/:id' },
      // Tâche « Rappel patient » de la file « À traiter » (fin de séance 3 du sevrage). Une
      // tâche encore ouverte pour le même dossier et la même étape est mise à jour, pas doublée.
      SCHEDULE_PATIENT_REMINDER: { method: 'POST' as const, endpoint: 'care/patient-reminder' },
      // Geste « Fait » de la file, sans corps.
      DONE_PATIENT_REMINDER: {
        method: 'POST' as const,
        endpoint: 'care/patient-reminder/:id/done',
      },
      // Annulation d'une tâche encore à faire (relance du sevrage après la séance 3), sans corps.
      CANCEL_PATIENT_REMINDER: {
        method: 'POST' as const,
        endpoint: 'care/patient-reminder/:id/cancel',
      },
      // Résultat de l'appel du sevrage (statut fumeur, cigarettes par jour) : clôt la tâche.
      RESULT_PATIENT_REMINDER: {
        method: 'POST' as const,
        endpoint: 'care/patient-reminder/:reminderId/result',
      },
      // Renvoie le lien d'une invitation deja envoyee, sans en creer une seconde : le
      // patient qui a commence doit retrouver son brouillon. Payload optionnel
      // { method: 'email' | 'sms' } pour forcer un canal, sinon celui de l'invitation.
      RESEND_SURVEY_INVITATION: {
        method: 'POST' as const,
        endpoint: 'care/survey-invitation/:id/resend',
      },
      // Convertit un auto-questionnaire rempli sans compte en invitation sur un dossier reel.
      ATTACH_BP_SELF_SURVEY: {
        method: 'POST' as const,
        endpoint: 'care/bilan-prevention/self-survey/:id/attach',
      },
      // Efface les reponses puis supprime la session : l'officine ecarte ce qu'elle ne
      // traitera pas (doublon, essai, patient qui se retracte).
      DELETE_BP_SELF_SURVEY: {
        method: 'DELETE' as const,
        endpoint: 'care/bilan-prevention/self-survey/:id',
      },
      // Rattache une ordonnance recue par QR code a un dossier de la pharmacie : l'analyse des
      // medicaments part a ce moment-la. `:id` vient du champ `id` passe au mutate.
      ATTACH_ORDONNANCE_INBOX: {
        method: 'POST' as const,
        endpoint: 'care/ordonnances/inbox/:id/attach',
        schema: z.object({ dossierId: z.number().int().positive() }),
      },
      // Sort l'ordonnance de la file et supprime son fichier. Le motif n'est pas encore lu
      // par l'API : il est envoye pour la trace a venir.
      REJECT_ORDONNANCE_INBOX: {
        method: 'POST' as const,
        endpoint: 'care/ordonnances/inbox/:id/reject',
        schema: z.object({ reason: z.enum(['unreadable', 'wrongPharmacy', 'other']) }),
      },
      CREATE_APPOINTMENT: { method: 'POST', endpoint: 'care/appointment' },
      CREATE_UNSCHEDULED_APPOINTMENT: {
        method: 'POST',
        endpoint: 'care/appointment/unscheduled',
      },
      SCHEDULE_APPOINTMENT: { method: 'PATCH', endpoint: 'care/appointment/:id/schedule' },
      UNSCHEDULE_APPOINTMENT: { method: 'PATCH', endpoint: 'care/appointment/:id/unschedule' },
      UPDATE_APPOINTMENT: { method: 'PATCH', endpoint: 'care/appointment/:id' },
      CREATE_APPOINTMENT_TYPE: { method: 'POST', endpoint: 'care/appointment/type' },
      UPDATE_APPOINTMENT_TYPE: { method: 'PATCH', endpoint: 'care/appointment/type/:id' },
      DELETE_APPOINTMENT_TYPE: { method: 'DELETE', endpoint: 'care/appointment/type/:id' },
      CREATE_LEAVE: { method: 'POST', endpoint: 'care/planning/leave' },
      UPDATE_LEAVE: { method: 'PATCH', endpoint: 'care/planning/leave/:id' },
      DELETE_LEAVE: { method: 'DELETE', endpoint: 'care/planning/leave/:id' },
      CREATE_PLANNING_EVENT: { method: 'POST', endpoint: 'care/planning/event' },
      UPDATE_PLANNING_EVENT: { method: 'PATCH', endpoint: 'care/planning/event/:id' },
      DELETE_PLANNING_EVENT: { method: 'DELETE', endpoint: 'care/planning/event/:id' },
      CREATE_STAFF_WEEK_TEMPLATE: { method: 'POST', endpoint: 'care/staff-week-template' },
      UPDATE_STAFF_WEEK_TEMPLATE: { method: 'PATCH', endpoint: 'care/staff-week-template/:id' },
      DELETE_STAFF_WEEK_TEMPLATE: { method: 'DELETE', endpoint: 'care/staff-week-template/:id' },
      DUPLICATE_STAFF_WEEK_TEMPLATE: {
        method: 'POST',
        endpoint: 'care/staff-week-template/:id/duplicate',
      },
      CREATE_STAFF_NEED: { method: 'POST', endpoint: 'care/staff-need' },
      UPDATE_STAFF_NEED: { method: 'PATCH', endpoint: 'care/staff-need/:id' },
      DELETE_STAFF_NEED: { method: 'DELETE', endpoint: 'care/staff-need/:id' },
      CREATE_STAFF_SHIFT: { method: 'POST', endpoint: 'care/staff-shift' },
      UPDATE_STAFF_SHIFT: { method: 'PATCH', endpoint: 'care/staff-shift/:id' },
      DELETE_STAFF_SHIFT: { method: 'DELETE', endpoint: 'care/staff-shift/:id' },
      SET_STAFF_WEEK_PLAN: { method: 'POST', endpoint: 'care/staff-week-plan' },
      COPY_STAFF_WEEK: { method: 'POST', endpoint: 'care/staff-shift/copy-week' },
      SAVE_MISSION_CONFIG: { method: 'PUT', endpoint: 'care/mission-config' },
      SAVE_APPOINTMENT_SHIFT_SETTING: {
        method: 'PUT',
        endpoint: 'care/appointment-shift-setting',
      },
      SAVE_EXPERT_MODE: { method: 'PUT', endpoint: 'care/expert-mode' },
      SAVE_LGO_CONFIG: { method: 'PUT', endpoint: 'care/lgo-config' },
      SAVE_GROUPEMENT_LGO_CONFIG: { method: 'PUT', endpoint: 'care/admin/lgo-config/:companyId' },
      UPDATE_NOTIFICATION_TEMPLATE: {
        method: 'PATCH',
        endpoint: 'care/notification_template/:id',
      },
      // Bibliothèque admin (super admin) — ressources communes Care.
      RESOURCE: {
        CREATE: { method: 'POST' as const, endpoint: 'care/admin/resource' },
        UPDATE: { method: 'PATCH' as const, endpoint: 'care/admin/resource/:id' },
        DELETE: { method: 'DELETE' as const, endpoint: 'care/admin/resource/:id' },
        GET_UPLOAD_URL: { method: 'POST' as const, endpoint: 'care/admin/resource/upload-url' },
      },
    },
    CREDIT: {
      SETUP_SEPA: { method: 'POST', endpoint: 'care/credit/stripe/setup-intent' },
      CHECK: { method: 'POST', endpoint: 'care/credit/check' },
      // Offre « 2 mois offerts sur Mes missions » : sans payload, l'officine est celle
      // selectionnee cote API. Reponse : le bloc `trial` de GET care/credit/status, a jour.
      TRIAL_START: {
        method: 'POST',
        endpoint: 'care/credit/trial/start',
        schema: z.object({}).strict(),
      },
      TRIAL_POSTPONE: {
        method: 'POST',
        endpoint: 'care/credit/trial/postpone',
        schema: z.object({}).strict(),
      },
    },
  },
  NOTIFICATION: {
    MARK_READ: { method: 'PATCH', endpoint: 'my-notifications/mark_read/:id' },
    MARK_READ_ALL: { method: 'PATCH', endpoint: 'my-notifications/mark_read_all' },
  },
  BILLING: {
    SUBSCRIPTION: {
      CREATE: { method: 'POST', endpoint: 'billing/subscription/subscribe' },
      CANCEL: { method: 'POST', endpoint: 'billing/subscription/cancel' },
      // Operation Post : uriTemplate `/billing/subscription/save-selection` (SaveSubscriptionSelectionController).
      // Body { subscriptionType, stripePriceId? | offlineId? }. Reponse { success, subscriptionId, message }.
      SAVE_SELECTION: {
        method: 'POST',
        endpoint: 'billing/subscription/save-selection',
        schema: z
          .object({
            subscriptionType: z.string(),
            stripePriceId: z.string().optional(),
            offlineId: z.string().optional(),
          })
          .passthrough(),
      },
    },
  },
  // Endpoints publics (non authentifies) : reinitialisation de mot de passe.
  AUTH: {
    // Operation Post : uriTemplate `/password_lost` (LostPasswordController).
    // Body { redirectUrl } + `email` OU `cip`. Le `cip` sert la page publique /register, ou
    // l'adresse du titulaire n'est pas exposee au navigateur : le serveur la resout.
    ASK_RESET_PASSWORD: {
      method: 'POST',
      endpoint: 'password_lost',
      schema: z
        .object({
          redirectUrl: z.string(),
          email: z.string().optional(),
          cip: z.string().optional(),
        })
        .passthrough()
        .refine(payload => !!payload.email || !!payload.cip, {
          message: 'email ou cip requis',
        }),
    },
    // Operation Post : uriTemplate `/password_new` (NewPasswordController). Body { token, password }.
    RESET_PASSWORD: {
      method: 'POST',
      endpoint: 'password_new',
      schema: z.object({ token: z.string(), password: z.string() }).passthrough(),
    },
  },
  CALL: {
    PROCEDURE: {
      CREATE: {
        method: 'POST',
        endpoint: 'call/procedures',
        schema: z
          .object({
            title: z.string().min(1).max(100),
            content: z.string().optional(),
            status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']).optional(),
            entity: z.string().min(1).nullable(),
            // IRI ou null (retrait). Les mots-clés sont des tags libres.
            category: z.string().nullable().optional(),
            keywords: z.array(z.string()).optional(),
            type: z.enum(['PROCEDURE', 'FORMATION', 'INFORMATION', 'QUIZ']).optional(),
            mandatoryForAll: z.boolean().optional(),
            requiresAttestation: z.boolean().optional(),
            organism: z.string().nullable().optional(),
            objective: z.string().nullable().optional(),
            stakes: z.string().nullable().optional(),
            externalUrl: z.string().nullable().optional(),
            validationQuiz: z
              .string()
              .regex(/^\/api\/v2\/call\/procedures\/\d+$/)
              .nullable()
              .optional(),
            quizKeyFile: z.string().nullable().optional(),
            quizPassPercent: z.number().int().min(1).max(100).optional(),
            renewalMonths: z.number().int().positive().nullable().optional(),
            targetRoles: z.array(z.string()).optional(),
            targetServices: z.array(z.number()).optional(),
            // Obligatoire : un ciblage sans pays partait aussi en Belgique (19/10/2026).
            targetCountries: z.array(z.enum(['FR', 'BE', 'LU', 'IT'])).min(1),
            excludeFromAi: z.boolean().optional(),
          })
          .passthrough(),
      },
      UPDATE: {
        // `procedureId` est résolu par la prop `variables` du hook, pas par le corps.
        method: 'PATCH',
        endpoint: 'call/procedures/:procedureId',
        schema: z
          .object({
            title: z.string().min(1).max(100).optional(),
            content: z.string().optional(),
            status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']).optional(),
            entity: z.string().nullable().optional(),
            category: z.string().nullable().optional(),
            keywords: z.array(z.string()).optional(),
            type: z.enum(['PROCEDURE', 'FORMATION', 'INFORMATION', 'QUIZ']).optional(),
            mandatoryForAll: z.boolean().optional(),
            requiresAttestation: z.boolean().optional(),
            organism: z.string().nullable().optional(),
            objective: z.string().nullable().optional(),
            stakes: z.string().nullable().optional(),
            externalUrl: z.string().nullable().optional(),
            validationQuiz: z
              .string()
              .regex(/^\/api\/v2\/call\/procedures\/\d+$/)
              .nullable()
              .optional(),
            quizKeyFile: z.string().nullable().optional(),
            quizPassPercent: z.number().int().min(1).max(100).optional(),
            renewalMonths: z.number().int().positive().nullable().optional(),
            targetRoles: z.array(z.string()).optional(),
            targetServices: z.array(z.number()).optional(),
            // Facultatif dans un PATCH partiel, mais jamais vide quand il est envoyé.
            targetCountries: z
              .array(z.enum(['FR', 'BE', 'LU', 'IT']))
              .min(1)
              .optional(),
            excludeFromAi: z.boolean().optional(),
          })
          .passthrough(),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'call/procedures/:procedureId',
        schema: z.object({ procedureId: z.union([z.number(), z.string()]) }),
      },
    },
    // Attribution des formations internes (CDC P-12) — ecriture PROCEDURE_ADMIN,
    // sauf COMPLETE (l'assigne lui-meme marque sa formation terminee).
    FORMATION_ASSIGNMENT: {
      CREATE: {
        method: 'POST',
        endpoint: 'call/formation_assignments',
        schema: z
          .object({
            formation: z.string().regex(/^\/api\/v2\/call\/procedures\/\d+$/),
            user: z.string().regex(/^\/api\/v2\/users\/\d+$/),
            mandatory: z.boolean().optional(),
            dueDate: z.string().nullable().optional(),
          })
          .passthrough(),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'call/formation_assignments/:assignmentId',
        schema: z
          .object({
            mandatory: z.boolean().optional(),
            dueDate: z.string().nullable().optional(),
          })
          .passthrough(),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'call/formation_assignments/:assignmentId',
        schema: z.object({ assignmentId: z.union([z.number(), z.string()]) }),
      },
      // Alignement SYNCHRONE des attributions d'une fiche sur son ciblage : rend un
      // compte immediat, la, ou la sauvegarde se contente de dispatcher un message que
      // rien ne consomme en dev. `removeOffTarget` est un choix explicite a chaque appel.
      SYNC: {
        method: 'POST',
        endpoint: 'call/procedures/:procedureId/sync_assignments',
        schema: z.object({ removeOffTarget: z.boolean().optional() }).passthrough(),
      },
      // Retrait EN MASSE : une fiche passee de « obligatoire pour toute l'equipe » a un
      // ciblage restreint garde ses attributions (l'attribution auto n'ajoute jamais que
      // ce qui manque). Les retirer une par une = un DELETE par admin du vivier.
      BULK_DELETE: {
        method: 'POST',
        endpoint: 'call/formation_assignments/bulk_delete',
        schema: z.object({ ids: z.array(z.number().int().positive()).min(1) }),
      },
    },
    // Attestations nominatives horodatees (CDC attestation FR-3) : l'acte se fait
    // toujours POUR SOI-MEME (pas de user dans le corps) ; invalidation admin (FR-8).
    ATTESTATION: {
      CREATE: {
        method: 'POST',
        endpoint: 'call/attestations',
        schema: z
          .object({
            procedure: z.string().regex(/^\/api\/v2\/call\/procedures\/\d+$/),
            realizationDate: z.string().nullable().optional(),
            justificationKeyFile: z.string().nullable().optional(),
            // Score d'un quiz passé dans la fiche (fiche Quiz) : le serveur compare au seuil.
            quizScore: z.number().int().min(0).optional(),
            quizTotal: z.number().int().positive().optional(),
          })
          .passthrough(),
      },
      // Dépôt par un admin (ROLE_PHARMEDICALL_PROCEDURE_ADMIN) au nom d'un collaborateur :
      // aucun e-mail n'est envoyé, l'attestation porte `attestedBy`.
      ON_BEHALF: {
        method: 'POST',
        endpoint: 'call/attestations/on_behalf',
        schema: z.object({
          procedure: z.string().regex(/^\/api\/v2\/call\/procedures\/\d+$/),
          userId: z.number().int().positive(),
          realizationDate: z.string().nullable(),
          justificationKeyFile: z.string().nullable(),
        }),
      },
      INVALIDATE: {
        method: 'POST',
        endpoint: 'call/attestations/:attestationId/invalidate',
        schema: z.object({ attestationId: z.union([z.number(), z.string()]) }),
      },
      // Justificatif de formation : ouvert à toute personne qui atteste, déposé dans son
      // dossier S3 (`call/procedure-files/upload-url` reste réservé au rôle procédures).
      JUSTIFICATION_UPLOAD_URL: {
        method: 'POST',
        endpoint: 'call/attestations/justification-upload-url',
        // `userId` : dépôt pour un collaborateur par l'admin (dossier S3 du collaborateur).
        schema: z
          .object({ extension: z.string().min(1), userId: z.number().int().positive().optional() })
          .passthrough(),
      },
    },
    // Pilotage RH (CDC P-13) : relance mail asynchrone (userIds vide = tous les
    // retardataires de la vue filtree) et export XLSX asynchrone par email au
    // demandeur. Filtres FR-7 : organisme strict, periode d'ATTRIBUTION (bornes
    // incluses, dates 'YYYY-MM-DD'), service.
    FORMATION_REPORT: {
      REMIND: {
        method: 'POST',
        endpoint: 'call/formation_report/remind',
        schema: z
          .object({
            userIds: z.array(z.number()).optional(),
            organism: z.string().optional(),
            from: z.string().optional(),
            to: z.string().optional(),
            serviceId: z.number().optional(),
          })
          .passthrough(),
      },
      EXPORT: {
        method: 'POST',
        endpoint: 'call/formation_report/export',
        asyncExport: true,
        schema: z
          .object({
            organism: z.string().optional(),
            from: z.string().optional(),
            to: z.string().optional(),
            serviceId: z.number().optional(),
          })
          .passthrough(),
      },
    },
    CATEGORY: {
      CREATE: {
        method: 'POST',
        endpoint: 'call/categories',
        schema: z
          .object({
            label: z.string().min(1).max(100),
            // IRI de la catégorie parente, ou null (racine).
            parent: z.string().nullable().optional(),
          })
          .passthrough(),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'call/categories/:categoryId',
        schema: z
          .object({
            categoryId: z.union([z.number(), z.string()]),
            label: z.string().min(1).max(100).optional(),
            parent: z.string().nullable().optional(),
          })
          .passthrough(),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'call/categories/:categoryId',
        schema: z.object({ categoryId: z.union([z.number(), z.string()]) }),
      },
    },
    FAVORITE: {
      ADD: {
        method: 'POST',
        endpoint: 'call/favorites',
        // IRI de la procédure mise en favori.
        schema: z.object({ procedure: z.string().min(1) }),
      },
      REMOVE: {
        method: 'DELETE',
        endpoint: 'call/favorites/:favoriteId',
        schema: z.object({ favoriteId: z.union([z.number(), z.string()]) }),
      },
    },
    FEEDBACK: {
      CREATE: {
        method: 'POST',
        endpoint: 'call/feedbacks',
        schema: z.object({
          procedure: z.string().min(1),
          type: z.enum(['ERROR', 'IMPROVEMENT']),
          message: z.string().min(1),
        }),
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'call/feedbacks/:feedbackId',
        schema: z
          .object({
            feedbackId: z.union([z.number(), z.string()]),
            status: z.enum(['OPEN', 'IN_REVIEW', 'RESOLVED']).optional(),
          })
          .passthrough(),
      },
    },
    VERSION: {
      RESTORE: {
        method: 'POST',
        endpoint: 'call/procedure-versions/:versionId/restore',
        schema: z.object({ versionId: z.union([z.number(), z.string()]) }),
      },
    },
    VIEW: {
      TRACK: {
        method: 'POST',
        endpoint: 'call/procedure-views',
        schema: z.object({ procedure: z.string().min(1) }),
      },
    },
    FILE: {
      CREATE: {
        method: 'POST',
        endpoint: 'call/procedure-files',
        schema: z
          .object({
            procedure: z.string().min(1),
            keyFile: z.string().min(1),
            originalName: z.string().nullable().optional(),
            mimeType: z.string().nullable().optional(),
            fileSize: z.number().nullable().optional(),
            displayOrder: z.number().optional(),
          })
          .passthrough(),
      },
      DELETE: {
        method: 'DELETE',
        endpoint: 'call/procedure-files/:fileId',
        schema: z.object({ fileId: z.union([z.number(), z.string()]) }),
      },
      GET_UPLOAD_URL: {
        method: 'POST',
        endpoint: 'call/procedure-files/upload-url',
        schema: z.object({ extension: z.string().min(1) }).passthrough(),
      },
      // Quiz HTML d'une fiche Quiz : rangé dans l'extranet, pas sur le bucket public.
      QUIZ_UPLOAD_URL: {
        method: 'POST',
        endpoint: 'call/procedure-files/upload-url',
        schema: z.object({ purpose: z.literal('quiz') }),
      },
    },
  },
  COMPANION: {
    INSTALLATION: {
      UPDATE_CONFIG: { method: 'PATCH', endpoint: 'companion-installations/:id' },
      FORCE_UPDATE: { method: 'POST', endpoint: 'companion-installations/:id/force-update' },
      // Le poste ne répond pas en HTTP : il pousse son instantané dans son flux de
      // logs, sur le canal `diagnostic`.
      REQUEST_DIAGNOSTIC: {
        method: 'POST',
        endpoint: 'companion-installations/:id/request-diagnostic',
      },
      // Reconnaissance Winpharma (lot 0 de la sauvegarde) : le poste répond plus tard, par un
      // rapport lisible dans WINPHARMA_PROBES.
      REQUEST_WINPHARMA_PROBE: {
        method: 'POST',
        endpoint: 'companion-installations/:id/request-winpharma-probe',
        schema: companionRequestWinpharmaProbeSchema,
      },
      // Sauvegarde Winpharma (lot 1) : le poste l'ouvre lui-même, elle se suit dans
      // WINPHARMA_BACKUPS.
      REQUEST_WINPHARMA_BACKUP: {
        method: 'POST',
        endpoint: 'companion-installations/:id/request-winpharma-backup',
        schema: companionRequestWinpharmaBackupSchema,
      },
      WINPHARMA_BACKUP_SCHEDULE: {
        method: 'POST',
        endpoint: 'companion-installations/winpharma-backup-schedule',
        schema: companionWinpharmaBackupScheduleSchema,
      },
      // Sauvegarde du SERVEUR Winpharma via le helper NetCall (déclencheur) : déclenche une copie
      // tournante côté serveur. Aucun corps ; le poste rend compte dans son flux de logs. Super
      // admin (vérifié côté API).
      REQUEST_WINPHARMA_SERVER_BACKUP: {
        method: 'POST',
        endpoint: 'companion-installations/:id/request-winpharma-server-backup',
      },
      // Variante douce : rapatrier la dernière copie serveur déjà produite (ne déclenche rien,
      // ne verrouille pas la base). Le poste liste COPIE_DB, prend la plus récente, la dépose sur S3.
      REQUEST_WINPHARMA_SERVER_BACKUP_FETCH: {
        method: 'POST',
        endpoint: 'companion-installations/:id/request-winpharma-server-backup-fetch',
      },
      // Dump logique de la base serveur Winpharma via le COM (lecture seule, sans verrou). Le poste
      // dépose un .sql.gz sur S3. `boot` = schéma + tables de config (démarrer un serveur de test),
      // `full` = schéma + toutes les tables. Super admin (vérifié côté API).
      REQUEST_WINPHARMA_DB_DUMP_BOOT: {
        method: 'POST',
        endpoint: 'companion-installations/:id/request-winpharma-db-dump-boot',
      },
      REQUEST_WINPHARMA_DB_DUMP_FULL: {
        method: 'POST',
        endpoint: 'companion-installations/:id/request-winpharma-db-dump-full',
      },
      // OUTIL DE VALIDATION TEMPORAIRE : exécuter une commande PowerShell sur le poste (super admin)
      // pour éprouver les commandes COM Winpharma sans redéployer. Le poste rend la sortie dans son
      // flux de logs (canal diagnostic). À RETIRER une fois la commande COM figée.
      PS_EXEC: {
        method: 'POST',
        endpoint: 'companion-installations/:id/ps-exec',
        schema: companionPsExecSchema,
      },
      // Retrait / retour dans la supervision. Sans effet sur le poste : il se desarchive seul
      // des qu'il se manifeste.
      ARCHIVE: { method: 'POST', endpoint: 'companion-installations/:id/archive' },
      UNARCHIVE: { method: 'POST', endpoint: 'companion-installations/:id/unarchive' },
      // Seules les erreurs de log reçues après ce geste restent à traiter.
      ACKNOWLEDGE_LOG_ERRORS: {
        method: 'POST',
        endpoint: 'companion-installations/:id/acknowledge-log-errors',
      },
      // Même geste sur plusieurs postes, depuis la sélection multiple de la liste.
      ACKNOWLEDGE_LOG_ERRORS_MANY: {
        method: 'POST',
        endpoint: 'companion/acknowledge-log-errors',
      },
    },
    VERSION_TARGET: {
      CREATE: {
        method: 'POST',
        endpoint: 'companion-version-targets',
        schema: companionVersionTargetSchema,
      },
      UPDATE: {
        method: 'PATCH',
        endpoint: 'companion-version-targets/:id',
        schema: companionVersionTargetSchema.extend({ id: z.number() }),
      },
      DELETE: { method: 'DELETE', endpoint: 'companion-version-targets/:id' },
    },
    FORCE_UPDATE_BROADCAST: {
      method: 'POST',
      endpoint: 'companion/force-update-broadcast',
    },
    // Connexion du companion par le navigateur (page /companion/authorize) : émet un code à
    // usage unique lié au code_challenge PKCE, que la page remet au companion local.
    // -> { code, expiresIn } ; 400 { error: 'invalid_request' }, 403 { error: 'impersonation_forbidden' }.
    BROWSER_AUTH_AUTHORIZE: {
      method: 'POST',
      endpoint: 'companion/auth/authorize',
      schema: z.object({
        codeChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
        codeChallengeMethod: z.literal('S256'),
      }),
    },
  },
  BROWSER_EXTENSION: {
    // Connexion de l'extension Chrome Pharmedigroup (page /extension/authorize,
    // pharmedigroup-api/docs/BROWSER_EXTENSION_AUTH.md) : émet un code lié au code_challenge
    // PKCE et rend l'adresse de retour chromiumapp.org qui le porte.
    // -> { redirectUrl, expiresIn } ; 400 { error: 'invalid_request' },
    // 403 { error: 'extension_access_required' | 'impersonation_forbidden' }.
    AUTHORIZE: {
      method: 'POST',
      endpoint: 'browser_extension/auth/authorize',
      schema: z.object({
        codeChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
        codeChallengeMethod: z.literal('S256'),
        redirectUri: z.string().regex(/^https:\/\/[a-p]{32}\.chromiumapp\.org\/$/),
        state: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
      }),
    },
  },
  AI: {
    // « Merlin au Alt+clic » (administrateurs) : analyse d'un extrait de l'écran. 400 entrée
    // invalide ou écran Care, 429 quota du jour atteint, 503 Merlin indisponible. Bornes et
    // actions identiques à MerlinAssistService (group-api) : les changer ensemble.
    MERLIN_ASSIST: {
      method: 'POST' as const,
      endpoint: 'merlin_assist',
      schema: z.object({
        action: z.enum(['analyze', 'explain', 'howto', 'ask']),
        selection: z.string().trim().min(1).max(4000),
        question: z.string().trim().max(500).optional(),
        page: z.object({ title: z.string().max(200), route: z.string().max(255) }),
        locale: z.string().max(5),
      }),
    },
    CREDITS: {
      ALLOCATE: { method: 'POST', endpoint: 'ai/credit-allocations' },
      DELETE_ALLOCATION: { method: 'DELETE', endpoint: 'ai/credit-allocations/:id' },
    },
    // Dictee vocale : POST multipart (`file`, `language`, `durationSeconds`) -> { text, durationSeconds, model }.
    // Consomme via postMultipartJson (FormData) : fetchRest serialise le corps en JSON, incompatible.
    // La reponse est validee par audioTranscriptionResponseSchema (components/Common/Dictation).
    TRANSCRIPTION: { method: 'POST', endpoint: 'ai/transcriptions' },
    // Dictee TEMPS REEL : ouvre une session OpenAI et rend la cle ephemere du WebSocket, que le
    // navigateur ouvre lui-meme (useRealtimeTranscription).
    TRANSCRIPTION_SESSION: {
      method: 'POST',
      endpoint: 'ai/transcriptions/sessions',
      schema: z.object({ language: z.string().min(2).max(5).optional() }),
    },
    // Fin d'une dictee temps reel : duree streamee et taille du texte, pour le journal de cout.
    TRANSCRIPTION_USAGE: {
      method: 'POST',
      endpoint: 'ai/transcriptions/usage',
      schema: z.object({
        durationSeconds: z.number().nonnegative(),
        characters: z.number().int().nonnegative(),
      }),
    },
    // Interrupteur des fournisseurs IA (super admin, carte « Fournisseur IA » de Coûts IA) : le
    // corps REMPLACE le réglage. En passant à bedrock, l'API sonde Bedrock d'abord et répond 422
    // avec le message d'AWS si la sonde échoue. Modèles : profils `eu.` seulement, vide = défaut.
    PROVIDER_CONFIG: {
      method: 'PUT',
      endpoint: 'ai/provider/config',
      schema: z.object({
        mode: z.enum(['legacy', 'bedrock']),
        excludedUsages: z.array(z.string().min(1)).max(50),
        modelFast: z
          .string()
          .trim()
          .regex(/^(eu\.[a-z0-9][a-z0-9.\-:]*)?$/)
          .nullable(),
        modelSmart: z
          .string()
          .trim()
          .regex(/^(eu\.[a-z0-9][a-z0-9.\-:]*)?$/)
          .nullable(),
      }),
    },
  },
  ONBOARDING: {
    PROGRESS: {
      CREATE: { method: 'POST' as const, endpoint: 'onboarding-progress' },
      UPDATE: { method: 'PATCH' as const, endpoint: 'onboarding-progress/:progressId' },
      RESET: { method: 'POST' as const, endpoint: 'onboarding-progress/reset' },
    },
  },
  ASSISTANT: {
    CHAT: { method: 'POST', endpoint: 'assistant/chat' },
    INITIAL_BUTTONS: { method: 'POST', endpoint: 'assistant/initial-buttons' },
  },
  ADMIN: {
    S3_EXPLORER: {
      // Upload multipart : consommé via fetch natif (FormData), pas useRestMutation.
      UPLOAD: { method: 'POST', endpoint: 'admin/s3_explorer/upload' },
      // Supprimer un fichier / créer un dossier : service IT, bucket public (garde côté API).
      DELETE: {
        method: 'POST',
        endpoint: 'admin/s3_explorer/delete',
        schema: z.object({
          bucket: z.string().min(1),
          key: z.string().min(1).max(1024),
        }),
      },
      CREATE_FOLDER: {
        method: 'POST',
        endpoint: 'admin/s3_explorer/folder',
        schema: z.object({
          bucket: z.string().min(1),
          prefix: z.string().max(1024),
          name: z
            .string()
            .trim()
            .min(1)
            .max(255)
            .refine(name => !/[/\\]/.test(name) && name !== '.' && name !== '..'),
        }),
      },
    },
  },
  // Socle teleservices Ameli (INSi phase 1, DMP phase 2). Endpoints generiques :
  // `:service` (ex: insi, dmp) et `:operation` (ex: recherche) sont des segments de
  // chemin resolus par resolvePathVariables via `variables: { service, operation }`.
  // Le flux prepare -> signature companion -> execute est orchestre par
  // useAmeliOperation (lib/Ameli/Core). Aucune specificite metier ici.
  AMELI: {
    // POST /ameli/{service}/{operation}/prepare
    // Body : { poste: { os, instanceId, lpsVersion }, ...params metier }.
    // Reponse : { requestId, toSign: { assertionXml, audience }, expiresAt }.
    PREPARE: { method: 'POST', endpoint: 'ameli/:service/:operation/prepare' },
    // POST /ameli/{service}/{operation}/execute
    // Body : { requestId, signature: { signatureXml, certificateDer } }.
    // Reponse : DTO metier de l'operation, ou SOAP Fault (selon contrat phase 1/2).
    EXECUTE: { method: 'POST', endpoint: 'ameli/:service/:operation/execute' },
    // Endpoints DMP (phase 2 alimentation + phase 3 consultation). Les URL divergent du
    // patron generique {service}/{operation} : l'orchestrateur dedie (lib/Ameli/DMP/useDmpOperation)
    // reutilise le meme socle companion (status/sign) + la meme taxonomie d'erreurs.
    // Contrat API : src/Model/Ameli/DMP/DmpOperationResource.php.
    DMP: {
      // TD0.2 / TD0.3 : acces (test d'existence, mise a jour d'autorisation).
      // {operation} = existence | autorisation. Body prepare : { ps, patient, action? }.
      ACCESS_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/access/:operation/prepare',
      },
      ACCESS_EXECUTE: {
        method: 'POST',
        endpoint: 'ameli/dmp/access/:operation/execute',
      },
      // Borne de la recherche automatique a l'ouverture du dossier (EX_3.1-2030). Donnee LOCALE :
      // aucune transaction DMP, ni carte ni signature. Sans `enregistrer`, la route lit la borne ;
      // avec `enregistrer: true`, elle la pose. A appeler AVANT le prepare de la recherche : un
      // document depose pendant l'aller-retour doit remonter a la passe suivante.
      RECHERCHE_AUTOMATIQUE: {
        method: 'POST',
        endpoint: 'ameli/dmp/recherche-automatique',
      },
      // Trace locale du choix du patient sur l'alimentation (EX_0.2-1100). Sans `opposee`, la route
      // rend l'etat courant.
      OPPOSITION_ALIMENTATION: {
        method: 'POST',
        endpoint: 'ameli/dmp/opposition-alimentation',
      },
      // TD3.1 : recherche de documents. {mode} = find-documents | resolve-entry-uuids |
      // find-submission-sets | get-associations | get-submission-set-and-contents.
      // Body prepare : { ps, patient, criteria?, automatique? } ; execute (sans mode) : { requestId, signature }.
      RECHERCHE_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/recherche/:mode/prepare',
      },
      RECHERCHE_EXECUTE: {
        method: 'POST',
        endpoint: 'ameli/dmp/recherche/execute',
      },
      // TD3.2 : consultation d'un/plusieurs documents. Body prepare : { ps, patient, documents[] }.
      CONSULTATION_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/consultation/prepare',
      },
      CONSULTATION_EXECUTE: {
        method: 'POST',
        endpoint: 'ameli/dmp/consultation/execute',
      },
      // TD3.3 : gestion des attributs d'un document. {action} = suppression (depublication, 3.3c) |
      // visibilite (rendre visible patient/RL, 3.3b) | archivage (3.3d).
      // Body prepare : { ps, patient, documentEntryUuid, ...selon action }.
      GESTION_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/gestion/:action/prepare',
      },
      GESTION_EXECUTE: {
        method: 'POST',
        endpoint: 'ameli/dmp/gestion/:action/execute',
      },
      // TD2.1 : depot d'un compte-rendu de mission Care (alimentation). DEUX signatures
      // (VIHF + XAdES). Body prepare : { compteRendu, ps, patient, careDocumentId? }.
      CARE_CR_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/care/compte-rendu/prepare',
      },
      // TD2.1 : depot d'un LOT de documents non structures televerses depuis la fiche patient
      // (CDA R2 niveau 1, 1 a 5 documents en un seul depot). Body prepare :
      // { documents: [{ contenuBase64, mediaType, titre, typeCode, dateActe?, pdfaConform? }],
      //   ps, patient, poste, xadesCertificateChain }.
      // L'assemble/execute reutilisent les endpoints compte-rendu (meme transaction TD2.1).
      CARE_DOCUMENTS_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/care/documents/prepare',
      },
      // TD2.1 : depot d'une note de vaccination (CDA R2 N3, volet VAC 2023.01). Body prepare :
      // { vaccination, ps, patient, replacedEntryUuid? }. Reponse : + vaccinationId (UUID a tracer).
      // L'assemble/execute reutilisent les endpoints compte-rendu (meme transaction TD2.1).
      CARE_VAC_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/care/vaccination/prepare',
      },
      // TD2.1 : depot d'un compte-rendu de TROD (CDA R2 N3, volet BIO-TROD 2024.01, typeCode
      // 96173-0, formatCode urn:ans:ci-sis:trod:2024). Body prepare :
      // { trod: { type, dateHeureTest, resultats[], dispositif, commentaire?, consentementPatient? },
      //   ps, patient, xadesCertificateChain }. Reponse : + setId (serie de versions, a tracer).
      // Endpoint DISTINCT de CARE_CR_PREPARE, qui depose un corps non structure.
      // L'assemble/execute reutilisent les endpoints compte-rendu (meme transaction TD2.1).
      CARE_TROD_PREPARE: {
        method: 'POST',
        endpoint: 'ameli/dmp/care/trod/prepare',
      },
      // Transport mTLS par carte CPS pour l'alimentation TD2.1 : assemble l'enveloppe MTOM finale
      // (2 signatures VIHF + XAdES + pieces jointes CDA/XAdES) a relayer au GIE via le companion
      // (phase aller). Body : { requestId, vihfSignature, xadesSignature: { signatureValue } }.
      // Contrat : src/Controller/Ameli/DMP/CareCompteRenduAssembleController.php.
      CARE_CR_ASSEMBLE: {
        method: 'POST',
        endpoint: 'ameli/dmp/care/compte-rendu/assemble',
      },
      CARE_CR_EXECUTE: {
        method: 'POST',
        endpoint: 'ameli/dmp/care/compte-rendu/execute',
      },
      // Transport mTLS par carte CPS : assemble l'enveloppe finale signee a relayer au GIE via le
      // companion (phase aller). Body : { requestId, signature }. Reponse : enveloppe a relayer.
      // Contrat : src/Controller/Ameli/DMP/DmpRelayController.php. Utilise par useDmpOperation quand
      // une CPS est presente (sinon l'API envoie elle-meme via le certificat .p12 organisationnel).
      RELAY_ASSEMBLE: {
        method: 'POST',
        endpoint: 'ameli/dmp/relay/assemble',
      },
      // Rattachement temporaire d'un PS sans structure carte (CPS remplacant, CPF en formation) a
      // l'officine courante (test CNDA 0.1-180). Donnee LOCALE : ni prepare ni execute.
      // Body : { psNameId } (lecture) | { psNameId, revoke: true } (revocation) |
      // { psNameId, identifiantStructure, dateFin } (creation/mise a jour, dateFin = AAAA-MM-JJ).
      // Contrat : src/Controller/Ameli/DMP/DmpStructureRattachementController.php.
      STRUCTURE_RATTACHEMENT: {
        method: 'POST',
        endpoint: 'ameli/dmp/structure-rattachement',
      },
      // Depots LOCAUX encore actifs d'un patient (test CNDA 3.3-540 : depublication sans
      // autorisation de consultation). Donnee locale, aucun appel au DMP.
      DEPOTS_LOCAUX: {
        method: 'POST',
        endpoint: 'ameli/dmp/depots-locaux',
      },
      // Regles de visibilite par defaut de la pharmacie pour l'alimentation AUTOMATIQUE, par type
      // de document (LOT 2a, EX_GEN-1060 / EX_2.1-1050). Upsert : cree ou remplace la regle de CE
      // groupement pour `careSource`. Donnee LOCALE : ni prepare ni execute.
      REGLAGE_VISIBILITE_WRITE: {
        method: 'POST',
        endpoint: 'ameli/dmp/reglage-visibilite',
        schema: dmpReglageVisibiliteWriteSchema,
      },
      // Affichage des traits d'identite retournes par TD0.2 dans le bloc patientPerson
      // (EX_0.2-1020), DESACTIVE par defaut. Meme endpoint que REGLAGE_VISIBILITE_WRITE ci-dessus
      // (cle `afficherTraitsIdentiteTd02` distincte de `careSource`, cf. DmpReglageVisibiliteController).
      REGLAGE_ACCES_WRITE: {
        method: 'POST',
        endpoint: 'ameli/dmp/reglage-visibilite',
        schema: dmpReglageAccesWriteSchema,
      },
      // Marque un document Care comme RETENU (case cochee dans l'encart d'envoi, LOT 2a,
      // EX_GEN-1060) : aucune transaction DMP. Body : { careDocumentId, careSource, patient }.
      ALIMENTATION_ETAT_RETENIR: {
        method: 'POST',
        endpoint: 'ameli/dmp/alimentation-etat',
        schema: dmpAlimentationEtatRetenirSchema,
      },
    },
  },
  // Connexion par QR code (pharmedigroup-api/docs/QR_LOGIN.md). Flux A (« pull », page de
  // login) : SESSION_*, publiques sauf SCAN/APPROVE/REJECT (JWT du téléphone qui valide).
  // Flux B (« push », icône du header) : TRANSFER_*, JWT du poste émetteur (CREATE/CONFIRM/
  // REJECT). TRANSFER_CLAIM est réclamé PUBLIQUEMENT par la page `/frontoffice/qr-login/app`
  // quand l'utilisateur choisit « Continuer dans ce navigateur » (platform:'web') ; sinon
  // c'est l'app mobile qui l'appelle. TRANSFER_EXCHANGE (public) suit ce claim.
  QR_LOGIN: {
    SESSION_CREATE: { method: 'POST', endpoint: 'qr-login/sessions' },
    SESSION_SCAN: {
      method: 'POST',
      endpoint: 'qr-login/sessions/:id/scan',
      schema: qrLoginSessionScanSchema,
    },
    SESSION_APPROVE: {
      method: 'POST',
      endpoint: 'qr-login/sessions/:id/approve',
      schema: qrLoginSessionApproveSchema,
    },
    SESSION_REJECT: {
      method: 'POST',
      endpoint: 'qr-login/sessions/:id/reject',
      schema: qrLoginSessionRejectSchema,
    },
    SESSION_EXCHANGE: {
      method: 'POST',
      endpoint: 'qr-login/sessions/:id/exchange',
      schema: qrLoginSessionExchangeSchema,
    },
    TRANSFER_CREATE: {
      method: 'POST',
      endpoint: 'qr-login/transfers',
      schema: qrLoginTransferCreateSchema,
    },
    TRANSFER_CONFIRM: {
      method: 'POST',
      endpoint: 'qr-login/transfers/:id/confirm',
      schema: qrLoginTransferActionSchema,
    },
    TRANSFER_REJECT: {
      method: 'POST',
      endpoint: 'qr-login/transfers/:id/reject',
      schema: qrLoginTransferActionSchema,
    },
    TRANSFER_CLAIM: {
      method: 'POST',
      endpoint: 'qr-login/transfers/:id/claim',
      schema: qrLoginTransferClaimSchema,
    },
    TRANSFER_EXCHANGE: {
      method: 'POST',
      endpoint: 'qr-login/transfers/:id/exchange',
      schema: qrLoginTransferExchangeSchema,
    },
  },
  // Double confirmation des factures par l'app mobile (opt-in). Contrat figé dans
  // pharmedigroup-api/docs/TRUSTED_DEVICE_2FA.md.
  USER_DEVICE: {
    // Révocation d'un appareil de confiance (§1) — confirmation avant appel côté écran.
    DELETE: { method: 'DELETE', endpoint: 'me/devices/:id' },
  },
  DEVICE_CONFIRMATION: {
    // Repli « je n'ai pas mon téléphone » : envoie un code à 6 chiffres par e-mail (§2).
    EMAIL_CODE: {
      method: 'POST',
      endpoint: 'device-confirmations/:id/email-code',
      schema: deviceConfirmationEmailCodeSchema,
    },
    VERIFY_EMAIL_CODE: {
      method: 'POST',
      endpoint: 'device-confirmations/:id/verify-email-code',
      schema: deviceConfirmationVerifyEmailCodeSchema,
    },
  },
  // Réglages de sécurité de l'utilisateur connecté (§3). Désactiver le réglage passe par
  // la garde 428 (action `disable_invoice_confirmation`) — géré par useDeviceConfirmation,
  // pas par un champ du schéma.
  SECURITY_SETTINGS: {
    UPDATE: {
      method: 'PATCH',
      endpoint: 'me/security-settings',
      schema: securitySettingsUpdateSchema,
    },
  },
} satisfies ApiMutationEndpointTree
